import { todayMessagesEn } from "../../features/clock/messages.today.en";
import { setupWizardMessagesEn } from "../../features/setup/messages.setup.en";
import type { MessageKey } from "./messages.de";
import { platformMessagesEn } from "./messages.platform.en";

/**
 * English messages (prepared, spec 11.2).
 */
export const messagesEn: Readonly<Record<MessageKey, string>> = {
  "app.title": "Zeitavo",
  "app.slogan": "Working time. Simply recorded.",
  "app.skipToContent": "Skip to content",
  "app.loading": "Loading …",
  "app.language": "Language",
  "app.theme": "Appearance",
  "app.theme.system": "System",
  "app.theme.light": "Light",
  "app.theme.dark": "Dark",
  "nav.main": "Main navigation",
  "nav.clock": "Today",
  ...todayMessagesEn,
  "nav.times": "My times",
  "nav.balances": "Balances",
  "nav.corrections": "Corrections",
  "nav.absences": "Absences",
  "nav.team": "Team",
  "nav.violations": "Violations",
  "nav.admin": "Administration",
  "nav.privacy": "Privacy",
  "nav.bar.absences": "Absences",
  "nav.more": "More",
  "more.areas": "More areas",
  "more.settings": "Settings",
  "more.account": "Account",
  "more.language.de": "Deutsch",
  "more.language.en": "English",
  "account.settings": "Settings",
  "account.preferences": "Language and appearance",
  "account.menu": "{name} – Account",
  "auth.redirecting": "Redirecting to sign-in …",
  "auth.emailFirst.title": "Sign in",
  "auth.emailFirst.label": "Work e-mail address",
  "auth.emailFirst.invalid":
    "Please enter your complete work e-mail address, for example name@company.com.",
  "auth.emailFirst.continue": "Continue",
  "auth.emailFirst.hint": "The address lets the sign-in find your employer. It is not stored.",
  "legalLinks.title": "Legal",
  "legalLinks.privacyPolicy": "Privacy policy",
  "legalLinks.imprint": "Legal notice",
  "legalLinks.accessibility": "Accessibility",
  "legalLinks.security": "Report a vulnerability",
  "start.notRegistered.title": "Account not linked yet",
  "start.notRegistered.text":
    "Your account is not linked to an employee yet, so you cannot clock in yet. Your HR department does this; afterwards the clock appears here.",
  "start.notRegistered.retry": "Check again",
  "start.notRegistered.account":
    "Settings and signing out are in the account menu at your name in the top right.",
  "start.noAppFunctions.title": "Welcome",
  "start.noAppFunctions.text":
    "This app has no functions for your role. Settings and signing out are in the account menu at your name in the top right.",
  "auth.signIn": "Sign in",
  "auth.completing": "Completing sign-in …",
  "auth.retry": "Sign in again",
  "auth.logout": "Sign out",
  "auth.signedInAs": "Signed in as {name}",
  "auth.logoutPendingWarning.one":
    "{count} entry has not been sent to the server yet. Signing out deletes it from this device and it is lost.",
  "auth.logoutPendingWarning.other":
    "{count} entries have not been sent to the server yet. Signing out deletes them from this device and they are lost.",
  "auth.logoutConfirm": "Sign out anyway",
  "auth.logoutFailed": "Signing out failed. Please try again.",
  "common.cancel": "Cancel",
  "error.title": "Something went wrong.",
  "error.reload": "Reload page",
  "error.notFound": "This page does not exist.",
  "error.backHome": "Go to start page",
  "error.network": "The server cannot be reached. Please check the connection.",
  "error.forbidden": "You are not allowed to do this.",
  "error.administrationReadOnly":
    "The administration is read-only because a payment is outstanding or the contract ended. Clocking, own times and exports keep working.",
  "error.entitlementMissing":
    "Your plan does not include this function. The tenant administration can change the plan.",
  "error.functionNotEnabled": "This function is not enabled for your organization.",
  "error.readOnly":
    "The administration is read-only. Clocking, own times and exports keep working.",
  "error.projectTimeTrackingDisabled":
    "Project time tracking is switched off. The tenant administration switches it on in the tenant settings.",
  "error.managersMayNotCreateProjects":
    'Managers may not create projects in this tenant. The tenant administration can allow it under Tenant → Settings ("Managers may create projects").',
  "error.notRegistered": "Your account is not linked to an employee. Please contact HR.",
  "error.generic": "The request failed.",
  "sync.online": "Online",
  "sync.offline": "Offline",
  "sync.syncing": "Synchronizing …",
  "sync.pending.one": "{count} entry not sent yet",
  "sync.pending.other": "{count} entries not sent yet",
  "sync.error": "Synchronization failed ({code}). It will be retried automatically.",
  "sync.refreshFailed":
    "All entries sent. The server state could not be loaded ({code}); it will be retried automatically.",
  "sync.notYet": "Not synchronized yet",
  "sync.upToDate": "Synchronized at {time}",
  "sync.upToDateOn": "Synchronized on {date} at {time}",
  "sync.conflicts.one": "{count} conflict to resolve",
  "sync.conflicts.other": "{count} conflicts to resolve",
  "sync.retry": "Send now",
  "update.available": "A new version is available.",
  "update.activate": "Update now",
  "update.waitingForSync": "The new version is activated after the running synchronization …",
  "update.activating": "Activating the new version …",
  "push.title": "Notifications",
  "push.hint":
    "Reminders about open working days (for example a forgotten clock-out) as notifications on this device. Notifications contain no names and no working times. Signing out removes the device.",
  "push.enable": "Turn on notifications",
  "push.disable": "Turn off notifications",
  "push.state.on": "This device receives reminders.",
  "push.state.off": "This device receives no reminders.",
  "push.state.denied":
    "Notifications are blocked in the browser settings. Allow them there to receive reminders.",
  "push.state.notConfigured": "Notifications are not set up on the server.",
  "push.state.unsupported": "This browser does not support notifications.",
  "push.state.installRequired":
    "On iPhone and iPad, notifications are available for the installed app only. Add Zeitavo to the home screen first.",
  "nav.install": "Install",
  "install.why.title": "Why install?",
  "install.why":
    "The installed app starts from the home screen, works without a connection and can remind you of a forgotten clock-out (on iPhone and iPad only as installed app).",
  "install.how.title": "How to install",
  "install.button": "Install app",
  "install.installed": "The app is installed on this device.",
  "install.dismissed":
    "The installation was cancelled. You can install the app later through the browser menu.",
  "install.ios.intro": "On iPhone and iPad in Safari:",
  "install.ios.step1":
    "Tap the share icon at the bottom (iPad: top), a square with an arrow pointing up.",
  "install.ios.step2": "Choose “Add to Home Screen”.",
  "install.ios.step3": "Tap “Add” and open Zeitavo from the new icon from now on.",
  "install.android.intro": "On Android in Chrome:",
  "install.android.step1": "Open the menu (three dots at the top right).",
  "install.android.step2": "Choose “Install app” or “Add to Home screen”.",
  "install.desktop.intro": "On a computer in Chrome or Edge:",
  "install.desktop.step1":
    "Click the install icon at the right of the address bar or open the browser menu.",
  "install.desktop.step2": "Choose “Install Zeitavo” or “Install app”.",
  "clock.action.ClockIn": "Clock in",
  "clock.action.BreakStart": "Break",
  "clock.action.BreakEnd": "Resume",
  "clock.action.ClockOut": "Clock out",
  "clock.breakAction.RestBreak": "Rest break",
  "clock.breakAction.Standby": "Standby/waiting",
  "clock.breakHint":
    "Rest break: the break is fixed when it begins and you are free from work and from standby. If you must stay available or wait, choose “Standby/waiting” – it does not count as a rest break.",
  "clock.state.OnStandby": "On standby/waiting since {time}",
  "breakQualification.Unclassified": "not declared",
  "breakQualification.RestBreak": "Rest break",
  "breakQualification.Standby": "Standby/waiting",
  "clock.state.Off": "Clocked out",
  "clock.state.Working": "Clocked in since {time}",
  "clock.state.OnBreak": "On break since {time}",
  "clock.workToday": "Working time",
  "clock.breakToday": "Break",
  "clock.dayOf": "Working day {date}",
  "clock.recorded": "{action} saved at {time}.",
  "clock.notStored":
    "The entry was not stored on this device and is not recorded. Please try again.",
  "clock.snapshotMissing":
    "No server state yet – the displayed state is based on this device only.",
  "clock.snapshotFrom": "Server state as of {time}",
  "clock.inconsistent": "Local and server data do not match. Please synchronize.",
  "clock.warning.breakDue":
    "Break due: in {minutes} min you will have worked more than {limit} h without a break.",
  "clock.warning.breakOverdue":
    "You have worked more than {limit} h without a break. Please take a break now.",
  "clock.warning.breakMissing":
    "At least {required} min of break are required today, so far {taken} min (segments of 15 min or more).",
  "clock.warning.dailyLimitSoon": "In {minutes} min you reach the statutory maximum of {limit} h.",
  "clock.warning.dailyLimitExceeded": "The statutory maximum of {limit} h has been exceeded.",
  "clock.warning.regularLimitExceeded": "More than {limit} h of work – compensation is required.",
  "clock.warning.restActive":
    "Rest period until {time}: starting earlier falls short of the statutory rest.",
  "clock.warning.openDays": "Working days not closed: {dates}. Please request a correction.",
  "clock.confirmEarlyStart":
    "The rest period lasts until {time}. Clock in now anyway? The entry is saved and reported as a rest period shortfall.",
  "clock.confirmEarlyStartAction": "Clock in anyway",
  "clock.conflicts": "Resolve conflicts",
  "clock.conflict": "{action} of {time} was rejected: {reason}",
  "clock.conflictCorrection": "Request correction",
  "clock.conflictDismiss": "Dismiss",
  "crew.title": "Clock for others",
  "crew.intro":
    "Choose one or more people you are responsible for and clock for all of them at once. Each person gets an event of their own with you as the recording person; they see it under “My times” and can request a correction.",
  "crew.empty": "Nobody you can clock for today.",
  "crew.search": "Search person",
  "crew.people": "People ({count} selected)",
  "crew.open": "Choose people and clock",
  "crew.selectShown": "Select all shown",
  "crew.selectNone": "Clear selection",
  "crew.action": "{action} for {count}",
  "crew.recorded": "{action} recorded for {count} people at {time}",
  "crew.skipped": "Not recorded because the step does not fit their state: {names}",
  "crew.pending": "{count} not sent",
  "crew.recordFailed":
    "The entries could not be stored on this device. Check the state of the people and clock missing steps again.",
  "crew.fromDevice": "No connection – people and state as of {time}",
  "crew.conflicts": "Rejected entries for others",
  "crew.conflictHint": "The person adds missing times through a correction.",
  "crew.conflict": "{action} for {name} at {time} was rejected: {reason}",
  "crew.unknownPerson": "a person no longer assigned",
  "project.title": "Project",
  "project.forClockIn": "Project when clocking in",
  "project.none": "No project",
  "project.budgetNearlyExhausted": "Budget nearly exhausted",
  "project.current": "Project: {project}",
  "project.currentNone": "No project attributed",
  "project.switch": "Switch project",
  "project.search": "Search project (code or name)",
  "project.list": "Projects",
  "project.favorite": "{project} as favorite",
  "project.noMatch": "No matching project.",
  "project.stop": "Continue without a project",
  "project.switched": "Project switched at {time}.",
  "dayOverview.title": "Day overview {date}",
  "dayOverview.summary": "Working time {work}, not attributed to a project: {open}",
  "dayOverview.segments": "Time parts",
  "dayOverview.range": "{from}–{to}",
  "dayOverview.unknownProject": "Project that can no longer be chosen",
  "dayOverview.split": "Split {range}",
  "dayOverview.splitShort": "Split",
  "dayOverview.splitAt": "Split at",
  "dayOverview.splitConfirm": "Split",
  "dayOverview.splitInvalid": "Please enter a time between {from} and {to}.",
  "dayOverview.later": "Attribute later",
  "dayOverview.done": "Done",
  "dayOverview.saved": "Attribution saved.",
  "openDays.title": "Open project attributions",
  "openDays.none": "No open project attributions.",
  "openDays.item": "{date}: {open} without a project",
  "openDays.assign": "Attribute",
  "nav.setup": "Setup",
  ...setupWizardMessagesEn,
  "setup.intro":
    "Set up Zeitavo in a few steps. You can change everything later in the administration.",
  "setup.checklist": "Setup checklist",
  "setup.progress": "Setup: {done} of {total} steps done",
  "setup.continue": "Continue setup",
  "setup.derived": "follows from the data",
  "setup.invitationsStepHint":
    "Also done for employees who already have an account to sign in, for example through your company's sign-in. They need no invitation.",
  "setup.open": "Open",
  "setup.confirm": "Done",
  "setup.withdraw": "Withdraw",
  "setup.confirmNamed": "Mark {step} as done",
  "setup.withdrawNamed": "{step}: withdraw confirmation",
  "setup.downloadTemplate": "Download template",
  "setup.step.Establishment":
    "Establishment reviewed (federal state, industries, Sunday exemption)",
  "setup.step.WorkTimeModels": "Work time models reviewed",
  "setup.step.Employees": "Employees added",
  "setup.step.Invitations": "All employees invited",
  "setup.step.WorksCouncil": "Works council informed (§ 87 (1) no. 6 BetrVG)",
  "setup.step.EmployeeInformation": "Employees informed under Art. 13 GDPR",
  "setup.step.FirstClockEvent": "First clock event recorded",
  "setup.models": "Work time models from templates",
  "setup.modelsHint": "Each template creates a model of your own that you can change afterwards.",
  "setup.templates": "Templates",
  "setup.templateItem": "{name} ({hours} h per week)",
  "setup.templateExists": "already created",
  "setup.createModels": "Create selected models",
  "setup.modelsCreated.one": "{count} work time model created.",
  "setup.modelsCreated.other": "{count} work time models created.",
  "setup.employees": "Employees",
  "setup.employeesHint":
    "Load a CSV file (semicolon or comma) or add employees one by one. Everyone with an e-mail address gets an invitation.",
  "setup.csvFile": "CSV file",
  "setup.csvText": "Content of the CSV file",
  "setup.csvHint":
    "Required columns: personnel number, first name, last name, entry date. Optional: e-mail, work time model, establishment, employment type, 18th birthday.",
  "setup.csvTooLarge": "The file is too large (at most 900 KB).",
  "setup.check": "Check",
  "setup.import": "Import",
  "setup.checkOk.one": "No errors: {count} employee can be imported.",
  "setup.checkOk.other": "No errors: {count} employees can be imported.",
  "setup.checkErrors.one": "{count} error found. Nothing is imported until it is fixed.",
  "setup.checkErrors.other": "{count} errors found. Nothing is imported until all are fixed.",
  "setup.errorTable": "Errors per line",
  "setup.errorLine": "Line",
  "setup.errorField": "Column",
  "import.field.csv": "File",
  "import.field.personnelNumber": "Personnel number",
  "import.field.firstName": "First name",
  "import.field.lastName": "Last name",
  "import.field.email": "E-mail",
  "import.field.entryDate": "Entry date",
  "import.field.workTimeModel": "Work time model",
  "import.field.establishment": "Establishment",
  "import.field.employmentType": "Employment type",
  "import.field.minorUntil": "18th birthday",
  "import.field.leaveYear": "Leave year",
  "import.field.days": "Remaining leave",
  "import.field.taken": "Taken",
  "import.field.part": "Part",
  "import.field.reason": "Reason",
  "import.field.number": "Number",
  "import.field.name": "Name",
  "import.field.externalReference": "Reference",
  "import.field.code": "Code",
  "import.field.billable": "Billable",
  "setup.errorMessage": "Error",
  "setup.imported.one": "{count} employee created; invitations prepared: {invitations}.",
  "setup.imported.other": "{count} employees created; invitations prepared: {invitations}.",
  "setup.single": "Add one by one",
  "setup.field.personnelNumber": "Personnel number",
  "setup.field.firstName": "First name",
  "setup.field.lastName": "Last name",
  "setup.field.email": "E-mail for the invitation",
  "setup.field.entryDate": "Entry date",
  "setup.add": "Add",
  "setup.singleAdded": "Employee added.",
  "setup.invitations": "Invitations",
  "setup.noInvitations":
    "No invitations yet. Employees with an e-mail address get one as soon as you import them above or add them one by one.",
  "setup.invitation.Pending": "waiting to be sent",
  "setup.invitation.Sent": "sent",
  "setup.invitation.Failed": "failed",
  "setup.send": "Send",
  "setup.sendNamed": "Send the invitation to {name}",
  "setup.resend": "Send again",
  "setup.resendNamed": "Send the invitation to {name} again",
  "setup.sendInvitations": "Send open invitations ({count})",
  "admin.teams": "Teams",
  "admin.team.hint":
    "Employees belong to a team through their employment. Projects and requirement policies can refer to teams.",
  "admin.team.new": "New team",
  "admin.team.name": "Team name",
  "admin.team.lead": "Team lead",
  "admin.team.establishment": "Establishment",
  "admin.team.noEstablishment": "No specific establishment",
  "admin.team.item": "{name} – lead: {lead}, from {from}",
  "admin.team.editNamed": "Edit team {name}",
  "admin.employment.team": "Team",
  "admin.employment.noTeam": "No team",
  "admin.employment.teamItem": "Team {team}",
  "projects.members.noTeams":
    "No teams yet. HR creates teams under Administration → Teams and assigns employees to a team through their employment.",
  "projects.members.noEmployees":
    "No people to choose from. Managers see the people of their team here.",
  "projects.members.unknown": "Not in your selection",
  "projects.policies.noEstablishments": "No establishments yet.",
  "projects.policies.noEmployments": "No employment is recorded for this person.",
  "projects.policies.employmentItem": "Employment {from} to {to}",
  "nav.projects": "Projects",
  "projects.list": "Project list",
  "projects.none": "No projects yet.",
  "projects.new": "New project",
  "projects.edit": "Edit",
  "projects.editNamed": "Edit {code}",
  "projects.archive": "Archive",
  "projects.archiveNamed": "Archive {code}",
  "projects.reactivate": "Reactivate",
  "projects.reactivateNamed": "Reactivate {code}",
  "projects.archived": "archived",
  "projects.participants": "Members",
  "projects.participantsNamed": "Members of {code}",
  "projects.field.code": "Project code",
  "projects.field.name": "Name",
  "projects.field.description": "Description",
  "projects.field.costCenter": "Cost center",
  "projects.field.customerReference": "Customer reference",
  "projects.field.validFrom": "Valid from",
  "projects.field.validTo": "Valid to",
  "projects.field.visibility": "Selectable by",
  "projects.field.billable": "Billable",
  "projects.visibility.AllEmployees": "All employees",
  "projects.visibility.Members": "Members only",
  "projects.members": "Members",
  "projects.members.none": "No members.",
  "projects.members.kind": "Kind",
  "projects.members.choose": "Please choose",
  "projects.members.from": "From",
  "projects.members.add": "Add",
  "projects.members.save": "Save members",
  "projects.memberKind.Team": "Team",
  "projects.memberKind.Employee": "Person",
  "projects.period": "{from} to {to}",
  "projects.remove": "Remove",
  "projects.responsibles": "Project responsibility",
  "projects.responsibles.none": "No project responsibles.",
  "projects.responsibles.employee": "Person",
  "projects.responsibles.add": "Add",
  "projects.responsibles.save": "Save project responsibility",
  "projects.saved": "Saved.",
  "projects.policies": "Project attribution requirement",
  "projects.policies.hint":
    "The most specific level applies: employment before team before establishment before tenant.",
  "projects.policies.none": "No policies; project attribution is optional.",
  "projects.policies.set": "Set policy",
  "projects.policies.scopeKind": "Level",
  "projects.policies.scope": "Scope",
  "projects.policies.mode": "Requirement",
  "projects.policies.deadlineDays": "Deadline (working days)",
  "projects.scope.Tenant": "Tenant",
  "projects.scope.Establishment": "Establishment",
  "projects.scope.Team": "Team",
  "projects.scope.Employment": "Employment",
  "projects.mode.Disabled": "Off",
  "projects.mode.Optional": "Optional",
  "projects.mode.RequiredByDayClose": "Required by the deadline",
  "projects.mode.PromptAtClockOut": "Prompt at clock-out",
  // F-27: project list as a table with a detail view.
  "projects.search": "Search projects (code or name)",
  "projects.statusFilter": "Status",
  "projects.filter.Active": "Active",
  "projects.filter.Archived": "Archived",
  "projects.filter.All": "All",
  "projects.count.one": "{count} project",
  "projects.count.other": "{count} projects",
  "projects.noneFound": "No projects match the search and status.",
  "projects.column.project": "Project",
  "projects.column.status": "Status",
  "projects.column.members": "Members",
  "projects.column.responsibles": "Responsible",
  "projects.column.period": "Period",
  "projects.status.Active": "Active",
  "projects.status.Archived": "Archived",
  "projects.members.everyone": "All employees",
  "projects.members.nobody": "Nobody yet",
  "projects.members.teams.one": "{count} team",
  "projects.members.teams.other": "{count} teams",
  "projects.people.one": "{count} person",
  "projects.people.other": "{count} people",
  "projects.responsibles.nobody": "Nobody",
  "projects.period.open": "open-ended",
  "projects.period.from": "from {from}",
  "projects.period.to": "until {to}",
  "projects.period.ended": "ended",
  "projects.period.upcoming": "planned",
  "projects.back": "All projects",
  "projects.facts": "Master data",
  "projects.yes": "Yes",
  "projects.no": "No",
  "projects.members.hintEveryone":
    "All employees can choose this project. Members restrict the choice only when “Selectable by” is set to “Members only”.",
  "projects.members.hintNobody":
    "Nobody can choose this project yet. Add teams or people and save.",
  "projects.members.name": "Name",
  "projects.members.addTitle": "Add member",
  "projects.responsibles.addTitle": "Add responsible person",
  "projects.responsiblesNamed": "Project responsibility of {code}",
  "projects.removeNamed": "Remove {name}",
  "projects.unsaved": "Unsaved changes",
  "projects.discard": "Discard changes",
  // T-169: project report.
  "projects.sections": "Project areas",
  "projects.section.list": "Project list",
  "projects.section.report": "Report",
  "projects.section.catalog": "Customers and activities",
  "projects.section.billing": "Billing",
  "projects.field.customer": "Customer",
  "projects.customerNone": "No customer",
  "projects.activities.title": "Allowed activities",
  "projects.activities.all": "No choice: every active activity is allowed.",
  "projects.activities.some":
    "Only the chosen activities are allowed. Other activities recorded offline are accepted and marked.",
  "projects.activities.none":
    "There are no activities yet. Create them under “Customers and activities”.",
  "projects.activities.saved": "Allowed activities saved.",
  "projects.policies.requireActivity": "Ask for an activity (hint only, never blocking)",
  "projects.policies.requireActivityShort": "activity asked for",
  "projects.report.column.customer": "Customer",
  "projects.report.column.activity": "Activity",
  "projects.report.customer": "Customer",
  "projects.report.allCustomers": "All customers",
  "projects.report.byActivity": "Split by activity",
  // T-185: project budgets.
  "budgets.title": "Budgets",
  "budgets.setting.limits":
    "Limits against fine-grained budgets (works agreement): budget at least {hours} hours, warning thresholds {min} to {max} %, at most {count} per budget, validity at least {days} days, at most {budgets} running budgets per project.",
  "budgets.name": "{scope}, {period}, {validity}",
  "budgets.problem.validity": "A budget with an end is valid for at least {days} days.",
  "budgets.hint":
    "Budgets in hours for the whole project or one activity, for the term or per month. They only warn: the persons responsible get at most one message per warning threshold, budget and period, recording goes on. The consumption is the project time of the report; HR sees it as well.",
  "budgets.none": "This project has no budget yet.",
  "budgets.add": "Add budget",
  "budgets.new": "New budget",
  "budgets.edit": "Change budget",
  "budgets.editShort": "Change",
  "budgets.editNamed": "Change budget {name}",
  "budgets.remove": "Remove",
  "budgets.removeNamed": "Remove budget {name}",
  "budgets.removeConsequence":
    "The budget and the marks of reached warning thresholds are removed. Recorded time stays unchanged.",
  "budgets.removeConfirm": "Remove budget",
  "budgets.removed": "Budget removed.",
  "budgets.saved": "Budget saved.",
  "budgets.cancel": "Cancel",
  "budgets.offline": "Budgets can only be changed with a connection.",
  "budgets.azvOmitted":
    "The consumption leaves out time on days of federal civil servants (section 7 (8) AZV); HR sees the full consumption.",
  "budgets.wholeProject": "Whole project",
  "budgets.unknownActivity": "Unknown activity",
  "budgets.period.Total": "Term",
  "budgets.period.Monthly": "Per month",
  "budgets.field.activity": "Applies to",
  "budgets.field.period": "Period",
  "budgets.field.hours": "Budget in hours",
  "budgets.field.thresholds": "Warning thresholds in percent",
  "budgets.field.validFrom": "Valid from",
  "budgets.field.validTo": "Valid until",
  "budgets.formHint":
    "Separate warning thresholds with commas, for example “80, 100”. Activity and period cannot be changed later.",
  "budgets.problem.hours": "Please enter a budget of at least {hours} hours (at most one million).",
  "budgets.problem.thresholds":
    "Please enter one to {count} warning thresholds between {min} and {max} percent.",
  "budgets.problem.range": "Start and end lie between 2000 and 2100, the end not before the start.",
  "budgets.column.scope": "Applies to",
  "budgets.column.period": "Period",
  "budgets.column.budget": "Budget",
  "budgets.column.validity": "Validity",
  "budgets.column.thresholds": "Warning thresholds",
  "budgets.column.usage": "Consumption",
  "budgets.column.actions": "Actions",
  "budgets.hours": "{hours} h",
  "budgets.usage": "{used} of {budget} h ({percent} %)",
  "budgets.level.warning": "Warning threshold reached",
  "budgets.level.exceeded": "Budget reached",
  "budgets.validSince": "from {from}",
  "budgets.validRange": "{from} to {to}",
  "budgets.notCurrent": "Not valid today",
  "budgets.report.title": "Plan and actual of the budgets",
  "budgets.report.hint":
    "For every budget period that touches the period of the report: the budget and the consumption of the whole budget period.",
  "budgets.setting.title": "Hint for employees",
  "budgets.setting.hint":
    "When on, employees see “Budget nearly exhausted” at the projects they can choose once a warning threshold of 80 % or more is reached in the current period – never hours or percentages. The works agreement decides whether the hint is shown.",
  "budgets.setting.employeeHint": "Employees see “Budget nearly exhausted”",
  "budgets.setting.saved": "Hint for employees saved.",
  "rates.projectTitle": "Hourly rates",
  "rates.projectHint":
    "Sales rates per hour (never wages). A working day uses the most specific valid rate: project with activity before project before activity before tenant.",
  "rates.fallbackTitle": "General rates if no project rate applies",
  "rates.generalTitle": "General hourly rates",
  "rates.generalHint":
    "Rates of the tenant and per activity for all projects. A rate of a project comes first.",
  "rates.table": "Table {title}",
  "rates.none": "No rates.",
  "rates.add": "Add rate",
  "rates.new": "New hourly rate",
  "rates.edit": "Change hourly rate",
  "rates.saved": "Hourly rate saved.",
  "rates.removed": "Hourly rate removed.",
  "rates.cancel": "Cancel",
  "rates.offline": "Rates can only be changed with a connection.",
  "rates.chooseActivity": "Choose an activity",
  "rates.unknownActivity": "unknown activity",
  "rates.field.scope": "Applies to",
  "rates.field.activity": "Activity",
  "rates.field.amount": "Rate per hour in euros (net)",
  "rates.field.validFrom": "Valid from",
  "rates.field.validTo": "Valid until (empty: open)",
  "rates.formHint":
    "Rates of the same scope must not overlap: end the previous rate on the day before the new one.",
  "rates.problem.amount": "Enter an amount between €0.01 and €100,000 with at most two decimals.",
  "rates.problem.activity": "Choose an activity.",
  "rates.problem.validity": "The end must not lie before the start.",
  "rates.scope.Tenant": "All projects",
  "rates.scope.Activity": "Activity",
  "rates.scope.Project": "Project",
  "rates.scope.ProjectActivity": "Project and activity",
  "rates.column.scope": "Applies to",
  "rates.column.amount": "Rate",
  "rates.column.validity": "Validity",
  "rates.column.actions": "Actions",
  "rates.perHour": "{amount} per hour",
  "rates.validSince": "from {from}",
  "rates.validRange": "{from} to {to}",
  "rates.editShort": "Change",
  "rates.editNamed": "Change rate: {name}",
  "rates.remove": "Remove",
  "rates.removeNamed": "Remove rate: {name}",
  "rates.removeConsequence":
    "The rate is removed. A new billing of the period then uses the next more general rate; downloaded files stay unchanged.",
  "rates.removeConfirm": "Remove rate",
  "locks.projectTitle": "Locked periods",
  "locks.allTitle": "Locked periods of all projects",
  "locks.hint":
    "After billing the customer, the period is locked. Attributions in a locked period are then only possible by a request the project responsibility approves. Only HR releases a lock.",
  "locks.none": "No locked period.",
  "locks.period": "{from} to {to}",
  "locks.everyProject": "all projects",
  "locks.new": "Lock period",
  "locks.lock": "Lock period",
  "locks.locked": "Period locked.",
  "locks.field.from": "From",
  "locks.field.to": "To",
  "locks.field.reason": "Reason, e.g. invoice number",
  "locks.problem.range": "Enter a period; the end must not lie before the start.",
  "locks.problem.reason": "Enter a reason of at most 500 characters.",
  "locks.offline": "Locks can only be changed with a connection.",
  "locks.release": "Release lock",
  "locks.releaseNamed": "Release lock: {period}",
  "locks.releaseConsequence":
    "Attributions in the period are possible again without a request; billing it again may give other amounts.",
  "locks.releaseConfirm": "Release lock",
  "locks.released": "Lock released.",
  "billing.title": "Billing",
  "billing.hint":
    "Billable project time of a customer with hourly rates. Zeitavo writes no invoice: download the CSV file for your invoicing program (e.g. Lexware Office, sevdesk) and the proof of service as PDF for the customer.",
  "billing.filter": "Choose billing",
  "billing.customer": "Customer",
  "billing.chooseCustomer": "Choose a customer",
  "billing.chooseCustomerHint": "Choose a customer to see their billing.",
  "billing.from": "From",
  "billing.to": "To",
  "billing.persons": "Persons in the proof of service",
  "billing.persons.none": "do not name",
  "billing.persons.initials": "with initials",
  "billing.personsHint":
    "The proof of service goes to the customer. Use initials only if evaluations per person are enabled and your employees are informed.",
  "billing.period": "{customer}: {from} to {to}",
  "billing.final": "Final: the period is locked for all billed projects.",
  "billing.preliminary":
    "Preliminary: the period is not locked, times may still change. Lock it after invoicing.",
  "billing.missingRate":
    "No hourly rate applies to some lines; their amount is missing from the total.",
  "billing.billableTime": "Billable time: {hours} h",
  "billing.azvOmitted":
    "Incomplete: time on days of federal civil servants (section 7 (8) AZV) is not included. HR creates the complete billing.",
  "billing.empty": "There is no billable time of this customer in the period that you may bill.",
  "billing.table": "Lines for {customer} from {from} to {to}",
  "billing.column.project": "Project",
  "billing.column.activity": "Activity",
  "billing.column.period": "Service period",
  "billing.column.quantity": "Quantity",
  "billing.column.unitPrice": "Rate",
  "billing.column.amount": "Amount (net)",
  "billing.linePeriod": "{from} to {to}",
  "billing.quantity": "{hours} h",
  "billing.noRate": "no rate",
  "billing.total": "Total",
  "billing.downloadCsv": "CSV for invoicing program",
  "billing.downloadPdf": "Proof of service (PDF)",
  "billing.downloaded": "Saved as {name}.",
  "billing.setting.title": "Amounts for project responsibles",
  "billing.setting.hint":
    "HR sees all rates and amounts, employees and the works council never. Project responsibles see the rates and amounts of their projects only if you switch it on here (part of the works agreement).",
  "billing.setting.responsiblesSeeAmounts":
    "Project responsibles see the rates and amounts of their projects",
  "billing.setting.saved": "Setting saved.",
  "projects.report.customers": "Project time per customer",
  "projects.report.noCustomer": "Projects without a customer",
  "activity.none": "Without activity",
  "activity.notChoosable": "Activity no longer available",
  "activity.requiredHint":
    "An activity is expected for this project time. You can also add it later.",
  "activity.forClockIn": "Activity when clocking in",
  "activity.current": "Activity",
  "activity.ofSegment": "Activity {range}",
  "catalog.customers.title": "Customers",
  "catalog.customers.intro":
    "Customers that project time is billed to. Customers are never deleted, only archived.",
  "catalog.customers.new": "New customer",
  "catalog.customers.edit": "Edit customer",
  "catalog.customers.empty": "No customers found.",
  "catalog.customers.import": "Import customers from a CSV file",
  "catalog.customers.importColumns":
    "Columns: number (or Kundennummer), name (or Kunde), optionally externalReference (or Referenz). Known numbers are changed, new ones created.",
  "catalog.activities.title": "Activities",
  "catalog.activities.intro":
    "Activities (kinds of service) of project time. They never change the working time and are never deleted, only archived.",
  "catalog.activities.new": "New activity",
  "catalog.activities.edit": "Edit activity",
  "catalog.activities.empty": "No activities found.",
  "catalog.activities.import": "Import activities from a CSV file",
  "catalog.activities.importColumns":
    "Columns: code (or Kürzel), name (or Bezeichnung), optionally billable (yes/no). Known codes are changed, new ones created.",
  "catalog.field.number": "Customer number",
  "catalog.field.code": "Code",
  "catalog.field.name": "Name",
  "catalog.field.reference": "Reference",
  "catalog.field.billable": "Usually billable",
  "catalog.field.status": "Status",
  "catalog.field.actions": "Actions",
  "catalog.yes": "Yes",
  "catalog.no": "No",
  "catalog.status.Active": "Active",
  "catalog.status.Archived": "Archived",
  "catalog.edit": "Edit",
  "catalog.editNamed": "Edit {key}",
  "catalog.archive": "Archive",
  "catalog.archiveNamed": "Archive {key}",
  "catalog.reactivate": "Reactivate",
  "catalog.reactivateNamed": "Reactivate {key}",
  "catalog.saved": "Saved.",
  "catalog.search": "Search",
  "catalog.showArchived": "Show archived",
  "catalog.import.file": "CSV file",
  "catalog.import.check": "Check",
  "catalog.import.run": "Import",
  "catalog.import.errors.one": "{count} error. Nothing was saved.",
  "catalog.import.errors.other": "{count} errors. Nothing was saved.",
  "catalog.import.error": "Line {line}, {field}: {message}",
  "catalog.import.checked": "File checked: {created} new, {updated} changed. Import now?",
  "catalog.import.done": "Imported: {created} new, {updated} changed.",
  "catalog.proposals.title": "Take over customers from customer references",
  "catalog.proposals.intro":
    "There is one proposal per distinct customer reference of the projects. Confirm an existing or a new customer. The customer reference stays at the projects.",
  "catalog.proposals.reference.one": "“{reference}” on {count} project",
  "catalog.proposals.reference.other": "“{reference}” on {count} projects",
  "catalog.proposals.customer": "Customer",
  "catalog.proposals.newCustomer": "Create a new customer",
  "catalog.proposals.confirm": "Confirm",
  "catalog.proposals.confirmFor": "Confirm for “{reference}”",
  "catalog.proposals.customerFor": "Customer for “{reference}”",
  "catalog.proposals.numberFor": "Customer number for “{reference}”",
  "catalog.proposals.nameFor": "Name for “{reference}”",
  "catalog.offline":
    "Without a connection, customers and activities cannot be changed. The lists show the last loaded state.",
  "catalog.proposals.confirmed.one": "Customer {customer} linked to {count} project.",
  "catalog.proposals.confirmed.other": "Customer {customer} linked to {count} projects.",
  "projects.report.title": "Project report",
  "projects.report.hint":
    "Sums of the project time you may see. The rows are ordered by project, day and personnel number and cannot be sorted by duration.",
  "projects.report.filter": "Period and project of the report",
  "projects.report.from": "From",
  "projects.report.to": "To",
  "projects.report.project": "Project",
  "projects.report.allProjects": "All projects",
  "projects.report.grouping": "Grouping",
  "projects.report.grouping.project": "By project",
  "projects.report.grouping.day": "By project and day",
  "projects.report.grouping.person": "By project and person",
  "projects.report.azvOmitted":
    "Without the days of federal civil servants (section 7 (8) AZV): the sums are not the whole project time.",
  "projects.report.perPersonUnavailable":
    "A report per person is not available to you; it needs the tenant setting “Evaluate project time per person”.",
  "projects.report.range.invalid": "Please enter two valid days.",
  "projects.report.range.reversed": "The last day lies before the first.",
  "projects.report.range.tooLong": "Choose at most {days} days.",
  "projects.report.period": "{from} to {to}",
  "projects.report.totals": "Totals",
  "projects.report.total.work": "Total working time",
  "projects.report.total.billable": "Billable",
  "projects.report.total.nonBillable": "Not billable",
  "projects.report.total.unattributed": "Without project",
  "projects.report.total.share": "Share without project",
  "projects.report.hours": "{hours} h",
  "projects.report.table": "Project time {from} to {to}",
  "projects.report.column.project": "Project",
  "projects.report.column.day": "Day",
  "projects.report.column.person": "Personnel number",
  "projects.report.column.billable": "Billing",
  "projects.report.column.hours": "Hours",
  "projects.report.billable": "billable",
  "projects.report.nonBillable": "not billable",
  "projects.report.notApplicable": "n/a",
  "projects.report.noProject": "Without project",
  "projects.report.empty": "There is no project time you may see in this period.",
  "projects.report.downloadPdf": "Download PDF",
  "projects.report.downloadCsv": "Download CSV",
  "projects.report.downloaded": "The file {name} was downloaded.",
  "rule.ARBZG-DAILY-MAX": "Daily maximum working time (§ 3 ArbZG)",
  "rule.ARBZG-AVERAGE": "Average working time (§ 3 ArbZG)",
  "rule.ARBZG-WEEKLY-MAX": "Weekly maximum working time",
  "rule.ARBZG-BREAK": "Rest breaks (§ 4 ArbZG)",
  "rule.ARBZG-REST": "Rest period (§ 5 ArbZG)",
  "rule.ARBZG-REST-REDUCED": "Reduced rest period (§ 5(2) ArbZG)",
  "rule.ARBZG-NIGHT": "Night work (§ 6 ArbZG)",
  "rule.ARBZG-SUNDAY": "Sunday and holiday rest (§§ 9–11 ArbZG)",
  "rule.ARBZG-ADJOINING-REST":
    "Rest together with Sunday, holiday and substitute rest (§ 11(4) ArbZG)",
  "rule.ARBZG-RECORD": "Recording of working time (former rule id)",
  "rule.ARBSCHG-RECORD": "Complete recording of working time (§ 3 ArbSchG)",
  "rule.ARBZG-OVERTIME-RECORD": "Recording of working time above 8 hours (§ 16(2) ArbZG)",
  "rule.ARBZG-DRAFT-RECORD":
    "Recording on the day of work (working draft as of June 2026, not officially published)",
  "check.IncompleteRecord": "End of work missing – record incomplete",
  "rule.JARBSCHG-DAILY": "Working time of minors (§ 8 JArbSchG)",
  "rule.JARBSCHG-BREAK": "Rest breaks of minors (§ 11 JArbSchG)",
  "rule.JARBSCHG-REST": "Free time of minors (§ 13 JArbSchG)",
  "rule.JARBSCHG-NIGHT": "Night rest of minors (§ 14 JArbSchG)",
  "rule.JARBSCHG-5DAY": "Five-day week of minors (§§ 15–17 JArbSchG)",
  "rule.JARBSCHG-SHIFT-TIME": "Shift time of minors (§ 12 JArbSchG)",
  "rule.ARBZG-MULTI-EMPLOYER": "Several employers (§ 2 (1) ArbZG)",
  "rule.JARBSCHG-MULTI-EMPLOYER": "Several employers of minors (§ 4 (5) JArbSchG)",
  "rule.MUSCHG-DAILY": "Working time under maternity protection (§ 4 MuSchG)",
  "rule.MUSCHG-NIGHT": "Night work under maternity protection (§ 5 MuSchG)",
  "rule.MUSCHG-SUNDAY": "Sunday work under maternity protection (§ 6 MuSchG)",
  "rule.MUSCHG-MULTI-EMPLOYER": "Several employers under maternity protection (§ 4 (1) MuSchG)",
  "rule.MILOG-RECORD": "Recording deadline (§ 17 MiLoG)",
  "rule.AENTG-RECORD": "Recording deadline (§ 19 AEntG)",
  "rule.AUEG-RECORD": "Recording deadline of the hirer (§ 17c AÜG)",
  "rule.ARBZG-ROAD-TRANSPORT": "Road transport (§ 21a ArbZG)",
  "rule.GSA-FLEISCH-RECORD": "Recording in the meat industry (§ 6 GSA Fleisch)",
  "rule.ARBZG-MINING-BREAKS": "Breaks as working time in underground mining (§ 2 (1) ArbZG)",
  "rule.JARBSCHG-UNDERGROUND": "Underground work of minors (§ 24 JArbSchG)",
  "rule.MUSCHG-UNDERGROUND": "Underground mining under maternity protection (§§ 11, 12 MuSchG)",
  "rule.OFFSHORE-PERIOD": "Period of offshore activities (§ 6 Offshore-ArbZV)",
  "rule.OFFSHORE-RECORD": "Daily recording of offshore activities (§ 8 Offshore-ArbZV)",
  "rule.BINSCH-DAILY-MAX": "Maximum working time in inland navigation (§ 4 BinSchArbZV)",
  "rule.BINSCH-BREAK": "Rest breaks in inland navigation (§ 5 BinSchArbZV)",
  "rule.BINSCH-REST": "Rest period in inland navigation (§ 6 BinSchArbZV)",
  "rule.BINSCH-SUNDAY": "Sundays and public holidays in inland navigation (§ 7 BinSchArbZV)",
  "rule.BINSCH-SEVEN-DAYS":
    "Seven-day limits and consecutive working days (§§ 4, 6, 7 BinSchArbZV)",
  "rule.PROTECTION-RULE": "Special protection rule",
  "check.Average": "Average exceeded",
  "check.ContinuousWork": "Too long without a break",
  "check.ContractualMonth": "Contractual monthly time exceeded",
  "check.DailyAbsolute": "Maximum exceeded",
  "check.DailyCompensation": "Extension without compensation",
  "check.DailyRegular": "More than the regular time – compensation needed",
  "check.DoubleWeek": "Double-week limit exceeded",
  "check.FreeSundays": "Too few free Sundays",
  "check.NightWork": "Night work",
  "check.NightWorker": "Night worker status",
  "check.NightWorkerAverage": "Night worker average exceeded",
  "check.RecordingDeadline": "Recording deadline",
  "check.LateStartRecording": "Start not recorded when work began",
  "check.LateEndRecording": "End or duration not recorded on the day of work",
  "check.Rest": "Rest period too short",
  "check.RestCompensation": "Compensation of reduced rest missing",
  "check.RestDayWork": "Work on a rest day",
  "check.SameDayRecording": "Not recorded on the same day (former product check)",
  "check.SubstituteRestDay": "Substitute rest day missing",
  "check.AdjoiningRest": "Rest day without the daily rest next to it",
  "check.SundayConsent": "Consent missing",
  "check.SundayWork": "Work on a Sunday or public holiday",
  "check.ShiftTime": "Shift time exceeded",
  "check.ExternalShiftTime": "Shift time at the other employer missing",
  "check.ExternalWorkingTime": "Working time at other employers missing",
  "check.ExternalWorkIntervals": "Times of day at other employers missing",
  "check.TotalBreak": "Break too short",
  "check.UnclassifiedBreak":
    "Interruption not declared as rest break – declare it with a correction",
  "check.Weekly": "Weekly limit exceeded",
  "check.WeeklyRegular": "More than the average weekly working time – compensation needed",
  "check.WeeklyAverage": "Weekly average exceeded",
  "check.NightWorkWithin24Hours": "Too much working time within 24 hours with night work",
  "check.WorkDaysPerWeek": "Too many working days per week",
  "check.WorkWindow": "Work outside the permitted hours",
  "check.UndergroundMinorBelowAge": "Underground work below 16 years",
  "check.UndergroundMinor": "Underground work without a confirmed exception",
  "check.UndergroundMaternity": "Underground work under maternity protection",
  "check.OffshorePeriodDays": "Offshore period longer than allowed",
  "check.OffshoreExtendedDays": "Too many days above 10 hours in the offshore period",
  "check.OffshoreConsecutiveExtendedDays": "Too many consecutive days above 10 hours",
  "check.OffshoreAverage": "Average of the offshore period above 10 hours",
  "check.SevenDays": "Working time in seven days exceeded",
  "check.SevenDaysNight": "Night work in seven days exceeded",
  "check.ConsecutiveWorkDays": "Too many consecutive working days",
  "finding.values": "Actual {actual} · limit {limit}",
  "finding.severity.Info": "Note",
  "finding.severity.Warning": "Warning",
  "finding.severity.Violation": "Violation",
  "finding.severity.IncompleteInput": "Incomplete input",
  "unit.days.one": "{count} day",
  "unit.days.other": "{count} days",
  "unit.count": "{value}",
  "unit.hours": "{value} h",
  "times.view": "View",
  "times.view.day": "Day",
  "times.view.week": "Week",
  "times.view.month": "Month",
  "times.today": "Today",
  "times.period": "Period {from} to {to}",
  "times.total": "Total: {work} h work, {break} h break",
  "dayBand.kind.Work": "Work",
  "dayBand.kind.Break": "Break",
  "dayBand.kind.Presence": "Presence",
  "dayBand.now": "now",
  "week.chart.title": "Working time per day",
  "team.kpi.working": "working now",
  "team.kpi.onBreak": "on a break",
  "team.kpi.decisions": "to decide",
  "team.kpi.worked": "worked",
  "team.kpi.withFindings": "with findings",
  "team.column.day": "Day",
  "times.empty": "No working time in this period.",
  "times.open": "open",
  "times.blocks": "Time blocks",
  "times.block.Work": "Work",
  "times.block.Break": "Break",
  "times.block.breakQualified": "Break ({qualification})",
  "times.correct": "Correct this day",
  "times.proxyEntries": "Recorded by others",
  "times.recordedBy": "recorded by {name}",
  "times.recordedByUnknown": "another person",
  "times.verifiedOfflineAtTerminal": "verified offline at a terminal",
  "times.proxyEntry": "{action} {time}",
  "times.proxyEntriesFailed":
    "Whether others recorded times for you could not be loaded right now.",
  "times.periodFindings": "Findings for the period",
  "times.pendingHint.one":
    "{count} entry of this device has not been sent yet and is missing here.",
  "times.pendingHint.other":
    "{count} entries of this device have not been sent yet and are missing here.",
  "times.column.date": "Date",
  "times.column.span": "Start – end",
  "times.column.work": "Work",
  "times.column.break": "Break",
  "balances.asOf": "Reference date",
  "balances.asOfEmpty":
    "Please choose a complete reference date. Until then you see the balances as of {date}.",
  "balances.month": "Month to date",
  "balances.year": "Year to date",
  "balances.carryOver": "Carry-over from previous years",
  "balances.target": "Target",
  "balances.actual": "Actual",
  "balances.absenceCredit": "Absence credit",
  "balances.overtime": "Overtime",
  "balances.flexitime": "Flexitime balance",
  "balances.vacationDays": "Vacation days taken",
  "balances.range": "{from} to {to}",
  "balances.noAccount":
    "Trust-based working time: no time account is kept. Working time is still fully recorded and checked.",
  "balances.cap.AbovePositiveCap": "The flexitime balance is above the permitted upper limit.",
  "balances.cap.BelowNegativeCap": "The flexitime balance is below the permitted lower limit.",
  "timeAccount.title": "Time account",
  "timeAccount.intro":
    "Payout and time off reduce the account from the booking month on. The recorded working time and the clock events stay unchanged; a booking is only reversed, never changed.",
  "timeAccount.noAccount":
    "The work time model keeps no time account on the reference date (trust-based time). Bookings are only possible in months with an account.",
  "timeAccount.balance.caption": "Balance per account",
  "timeAccount.balance.recorded": "Recorded",
  "timeAccount.balance.booked": "Booked",
  "timeAccount.balance.total": "Balance",
  "timeAccount.account.Overtime": "Overtime account",
  "timeAccount.account.Flexitime": "Flexitime account",
  "timeAccount.kind.Payout": "Paid out",
  "timeAccount.kind.TimeOff": "Time off in lieu",
  "timeAccount.kind.CarryOverCap": "Cap under § 7 (4) AZV",
  "timeAccount.state.Booked": "Booked",
  "timeAccount.state.Reversed": "Reversed",
  "timeAccount.state.Reversal": "Reversal",
  "timeAccount.column.month": "Month",
  "timeAccount.column.account": "Account",
  "timeAccount.column.kind": "Kind",
  "timeAccount.column.hours": "Hours",
  "timeAccount.column.reason": "Reason",
  "timeAccount.column.state": "Status",
  "timeAccount.column.actions": "Actions",
  "timeAccount.bookings": "Bookings",
  "timeAccount.bookings.none": "No bookings yet.",
  "timeAccount.form.title": "Book hours",
  "timeAccount.form.monthHint":
    "The booking reduces the account from this month on, at most the current month.",
  "timeAccount.form.monthFuture": "The month lies after the current month.",
  "timeAccount.form.time": "Hours",
  "timeAccount.form.timeHint": "For example 5:30 or 5.5 (whole minutes).",
  "timeAccount.form.timeInvalid": "Please enter hours in whole minutes, at most 2000 hours.",
  "timeAccount.form.capHint":
    "Only for federal civil servants in the first month of the settlement period: hours above the allowed carry-over that the service agreement forfeits. Nothing is capped automatically.",
  "timeAccount.form.timeOffHint":
    "Only book when the time off is not recorded already as a „Substitute rest day“ absence or by working less; otherwise it reduces the account twice.",
  "timeAccount.warning.CompensatoryRestInMonth":
    "Saved. A substitute rest day is approved in the month and already reduces the account. Check whether the time off counts twice and reverse it if so.",
  "timeAccount.warning.DaysWithoutWorkInMonth":
    "Saved. The month has working days without work or absence; they already reduce the account. Check whether the time off counts twice and reverse it if so.",
  "timeAccount.form.reason": "Reason",
  "timeAccount.form.reasonMissing": "Please give a reason.",
  "timeAccount.form.submit": "Save booking",
  "timeAccount.booked": "The booking is saved.",
  "timeAccount.reverse": "Reverse",
  "timeAccount.reverse.consequence":
    "The reversal gives the hours back in the same month. The original booking stays visible.",
  "timeAccount.reverse.hint":
    "The reason appears in the list of bookings and in the person's data export.",
  "timeAccount.reversed": "The booking is reversed.",
  "timeAccount.own": "Bookings on one's own time account are made by another HR administrator.",
  "timeAccount.milog.title": "Hints under section 2 (2) MiLoG",
  "timeAccount.milog.none": "No hints at the moment.",
  "timeAccount.milog.MonthlyCapExceeded":
    "{month}: {hours} placed on the account, more than half the target time ({limit}).",
  "timeAccount.milog.CompensationDue":
    "{hours} from {month} must be compensated by time off or payment by the end of {due}.",
  "timeAccount.milog.CompensationOverdue":
    "{hours} from {month} should have been compensated by the end of {due}.",
  "timeAccount.milog.ExitWithBalance":
    "The employment ends in {month} with a balance of {hours}; compensate by the end of {due}.",
  "timeAccount.milog.legal":
    "Hint for persons under the MiLoG recording duty; it applies only as far as the minimum wage is not already paid with the regular salary. It blocks nothing.",
  "timeAccount.own.title": "Time account",
  "timeAccount.own.intro":
    "Balance with the bookings of HR. Payout and time off reduce the account from their month on; the recorded working time above stays unchanged.",
  "timeAccount.own.paidOutYear": "Paid out this year",
  "timeAccount.own.timeOffYear": "Time off in lieu this year",
  "timeAccount.own.bookings": "Bookings on the time account this year",
  "timeAccount.azv.title": "Flexitime account under § 7 AZV",
  "timeAccount.azv.intro.own":
    "Your flexitime account as a federal civil servant. HR receives the hints as well; your superiors only learn whether the balance crosses +20 or −10 hours.",
  "timeAccount.azv.intro.hr":
    "Flexitime account under § 7 (4) and (5) AZV. Hints never block and never cut the balance; the service agreement governs the settlement, for example with a booking in the first month of the new settlement period.",
  "timeAccount.azv.period":
    "Settlement period {from} to {to}; flexi days count from {yearFrom} to {yearTo}.",
  "timeAccount.azv.opening": "Balance at the end of the previous period",
  "timeAccount.azv.carried": "Carried over (after a cap)",
  "timeAccount.azv.countsFrom":
    "Days before {date} are deleted under § 7 (7) AZV and no longer count in the balance.",
  "timeAccount.azv.balance": "Balance",
  "timeAccount.azv.excess": "Beyond the carry-over of {limit} (as of today)",
  "timeAccount.azv.flexiDays": "Flexi days this year",
  "timeAccount.azv.flexiDaysValue": "{days} of {max} ({left} left)",
  "timeAccount.azv.noHints": "No hints at the moment.",
  "timeAccount.azv.hint.NegativeLimitExceeded":
    "On {date} the balance first fell more than {limit} below zero (lowest {hours}, settlement period {from} to {to}).",
  "timeAccount.azv.hint.CarryOverExceeded":
    "At the end of the settlement period {from} to {to}, {hours} were carried over; at most {limit} are allowed. A cap in the first month of the new period settles it.",
  "timeAccount.azv.hint.FlexiDaysExceeded":
    "On {date} more than {max} flexi days were taken ({days} in {from} to {to}).",
  "timeAccount.azv.hint.AccountIncompleteAfterDeletion":
    "Days before {date} are deleted under § 7 (7) AZV; the balance lacks their time. Until a settlement period after that day is complete, Zeitavo checks neither carry-over nor shortfall and tells superiors no balance.",
  "timeAccount.azv.legal":
    "§ 7 (4) AZV: at most 40 hours below the regular working time and at most 40 hours carried over; § 7 (5) AZV: up to 12, if allowed up to 24 flexi days. Flexi days are approved time off in lieu on working days.",
  "error.TT-1240": "Please enter hours in whole minutes, at most 2000 hours per booking.",
  "error.TT-1241": "The month of the booking is invalid.",
  "error.TT-1242": "The booking is reversed already or a reversal itself.",
  "error.TT-1243": "A booking concerns the overtime or the flexitime account.",
  "error.TT-3300": "The month lies after the current month.",
  "error.TT-3301": "The month is closed. Reopen it with a reason to book.",
  "error.TT-3302": "The person kept no such time account in this month.",
  "error.TT-3303": "The booking exceeds the balance at the end of the month or of a later month.",
  "error.TT-3304": "Bookings on one's own time account are made by another HR administrator.",
  "surcharges.own.day": "Surcharge hours: {hours}",
  "surcharges.own.total": "Surcharge hours in the period: {hours}",
  "surcharges.own.failed": "The surcharge hours could not be loaded.",
  "surcharges.profile.title": "Surcharge profile",
  "surcharges.profile.hint":
    "Zeitavo counts surcharge hours per kind of surcharge in time windows, without percentages or amounts; payroll computes the surcharges. A new version applies to the working days from its first day; closed months never change.",
  "surcharges.profile.none": "No surcharge profile: no surcharge hours are counted.",
  "surcharges.profile.effective": "In force since {from}: {source}.",
  "surcharges.profile.source.template": "template {template}",
  "surcharges.profile.source.custom": "own time windows",
  "surcharges.profile.source.off": "surcharge hours switched off",
  "surcharges.profile.precedence":
    "Within a group an hour counts only for the first matching kind (precedence from top to bottom); the groups add up, for example night and Sunday work.",
  "surcharges.profile.limited":
    "The surcharge profile applies to all establishments; only HR of the whole company can change it.",
  "surcharges.column.kind": "Kind of surcharge",
  "surcharges.column.group": "Group",
  "surcharges.column.windows": "Time windows",
  "surcharges.column.validFrom": "Valid from",
  "surcharges.column.source": "Content",
  "surcharges.column.state": "Status",
  "surcharges.column.recordedAt": "Recorded",
  "surcharges.state.current": "In force",
  "surcharges.state.planned": "Planned",
  "surcharges.state.replaced": "Replaced",
  "surcharges.state.past": "Superseded",
  "surcharges.versions": "Versions of the surcharge profile",
  "surcharges.versions.none": "No version yet.",
  "surcharges.window.range": "{days} {from}–{to}",
  "surcharges.window.days.daily": "daily",
  "surcharges.window.days.weekday": "{weekday}",
  "surcharges.window.days.publicHoliday": "on public holidays at the establishment",
  "surcharges.window.days.publicHolidayWithEaster":
    "on public holidays at the establishment and on Easter and Pentecost Sunday",
  "surcharges.window.days.date": "on {date}",
  "surcharges.window.following": ", on the following day until {until} if begun before midnight",
  "surcharges.window.startedBefore": ", only if begun before midnight",
  "surcharges.weekday.1": "on Mondays",
  "surcharges.weekday.2": "on Tuesdays",
  "surcharges.weekday.3": "on Wednesdays",
  "surcharges.weekday.4": "on Thursdays",
  "surcharges.weekday.5": "on Fridays",
  "surcharges.weekday.6": "on Saturdays",
  "surcharges.weekday.7": "on Sundays",
  "surcharges.form.validFrom": "Valid from",
  "surcharges.form.validFromHint":
    "At the earliest {date}: the first day of the previous month, after the last closed month.",
  "surcharges.form.validFromInvalid": "Please enter a day on or after the earliest possible day.",
  "surcharges.form.source": "Time windows",
  "surcharges.form.source.template": "Take template {template}",
  "surcharges.form.source.custom": "Adjust time windows",
  "surcharges.form.source.off": "Switch surcharge hours off",
  "surcharges.form.customHint":
    "Times as HH:MM, the end also 24:00; an end before the start lies on the next day. Kinds that do not count are left out of the new version.",
  "surcharges.form.kindIncluded": "Count {kind}",
  "surcharges.form.noKind":
    "At least one kind of surcharge must count. To switch them off, choose „Switch surcharge hours off“.",
  "surcharges.form.previousMonth":
    "The version starts before the current month. If the previous month is paid already, payroll must correct it.",
  "surcharges.form.from": "Start ({window})",
  "surcharges.form.to": "End ({window})",
  "surcharges.form.timeInvalid": "Please enter a time HH:MM, the end also 24:00.",
  "surcharges.form.submit": "Save version",
  "surcharges.form.saved": "The version of the surcharge profile is saved.",
  "surcharges.form.titleOf": "New version: {profile}",
  "surcharges.profile.kindsOf": "Kinds of surcharge and time windows: {profile}",
  "surcharges.profile.purposeMismatch":
    "The server returns another surcharge profile for this section; it can be shown and changed only after the server is updated.",
  "surcharges.form.normReference": "Norm ({kind})",
  "surcharges.form.normVersion": "Version ({kind})",
  "surcharges.form.normInvalid": "Please enter the norm and its version.",
  "surcharges.column.norm": "Basis",
  "surcharges.norm.text": "{reference}, version {version}",
  "surcharges.conditions.title": "Surcharges of the applicable working conditions (§ 19 AEntG)",
  "surcharges.conditions.hint":
    "For establishments with the AEntG industry flag: time windows of the surcharges by time of day, weekday, date or public holiday of an ordinance or a collective agreement declared generally binding, each with its norm and version. The working time record shows start, end and duration of every interval that grants a surcharge, with the correction history. Surcharges from a threshold of hours or for an activity are not covered and must be recorded separately. The tax-free surcharge hours are separate and no record under § 19 AEntG.",
  "surcharges.conditions.none":
    "No surcharge basis recorded: the working time record shows no surcharge intervals for days under the AEntG duty.",
  "surcharges.conditions.versions": "Versions of the surcharge basis",
  "surcharges.conditions.limited":
    "The surcharge basis applies to all establishments; only the HR department of the whole company can change it.",
  "surcharges.conditions.source.off":
    "no surcharges by time of day, weekday, date or public holiday",
  "surcharges.conditions.form.off": "No surcharges by time of day, weekday, date or public holiday",
  "surcharges.conditions.form.noKind":
    "At least one surcharge must count. If no surcharge by time of day, weekday, date or public holiday applies, choose “No surcharges by time of day, weekday, date or public holiday”.",
  "surcharges.conditions.apiHint":
    "HR creates the first version with surcharges, time windows and norms through the API (PUT …/surcharge-profile?purpose=WorkingConditions); afterwards this form adjusts windows and versions.",
  "surcharges.month.title": "Surcharge hours",
  "surcharges.month.hint":
    "Hours per kind of surcharge of the working days of the month; a working day counts in the month it began. Payroll computes the amounts. These hours are no record of the surcharge intervals under § 19 AEntG; the working time record holds it for surcharges by time of day, weekday, date or public holiday, not for surcharges by thresholds of hours or activities.",
  "surcharges.month.caption": "Surcharge hours {month}",
  "surcharges.month.none": "No surcharge profile applies in this month.",
  "surcharges.month.noPersons": "Nobody is employed in this month.",
  "surcharges.month.person": "Person",
  "surcharges.month.failed": "Cannot be computed",
  "error.TT-1250": "The surcharge profile is incomplete or too large.",
  "error.TT-1251":
    "The code of a kind of surcharge starts with a capital letter and contains only capital letters, digits and hyphens.",
  "error.TT-1252": "A code names more than one kind of surcharge.",
  "error.TT-1253": "The name of a group or kind of surcharge is missing or too long.",
  "error.TT-1254": "A time window is invalid.",
  "error.TT-1255": "The surcharge profile starts on a day between 2000 and 2100.",
  "error.TT-3310":
    "A new version starts on the first day of the previous month at the earliest; earlier months may already be paid.",
  "error.TT-3311": "The month is closed. A new version starts after the last closed month.",
  "error.TT-3312": "This template does not exist.",
  "error.TT-3313": "Please name a template or the time windows.",
  "error.TT-1310":
    "Please enter the norm (up to 200 characters) and its version (up to 40 characters) for every kind of surcharge.",
  "error.TT-1311":
    "Every surcharge of the working conditions needs its norm and version; there are no templates for them.",
  "error.TT-3380":
    "There is no template for the surcharges of the working conditions. Please enter the surcharges with norm and version.",
  "error.TT-3381": "This purpose of a surcharge profile does not exist.",
  "corrections.new": "New correction request",
  "corrections.date": "Working day",
  "corrections.recorded": "Recorded entries",
  "corrections.noEvents": "There are no entries on this day.",
  "corrections.change": "Change time",
  "corrections.void": "Cancel entry",
  "corrections.add": "Add a missing entry",
  "corrections.kind": "Kind",
  "corrections.time": "Time",
  "corrections.newTime": "New time for {action} ({time})",
  "corrections.addChange": "Add",
  "corrections.applyTime": "Apply time",
  "corrections.changes": "Planned changes",
  "corrections.remove": "Remove",
  "corrections.reason": "Reason",
  "corrections.reasonHint": "Describe why the record is wrong or incomplete.",
  "corrections.preview": "Check effect",
  "corrections.submit": "Submit request",
  "corrections.previewTitle": "Effect",
  "corrections.previewDay":
    "{date}: working time {before} h → {after} h, findings {findingsBefore} → {findingsAfter}",
  "corrections.previewFindings": "Findings after the correction",
  "corrections.submitted.AutoApproved":
    "The correction was applied immediately (additions within the last 7 days only).",
  "corrections.submitted.Pending":
    "The request is waiting for approval by the responsible manager.",
  "corrections.change.AddEvent": "Add {action} at {time}",
  "corrections.change.ReplaceEvent": "Change {action} to {time}",
  "corrections.change.VoidEvent": "Cancel {action} at {time}",
  "corrections.change.unknownEvent": "entry",
  "corrections.mine": "My requests",
  "corrections.none": "You have not requested a correction yet.",
  "corrections.approvals": "To approve",
  "corrections.noApprovals": "No open requests in the team.",
  "corrections.requestedAt": "Requested on {time}",
  "corrections.forEmployee": "For {employee}",
  "corrections.decisionNote": "Decision note: {note}",
  "corrections.withdraw": "Withdraw request",
  "corrections.withdrawNamed": "Withdraw request of {time}",
  "corrections.withdrawConsequence":
    "The request of {time} will no longer be decided; your times stay as recorded.",
  "corrections.withdrawConfirm": "Withdraw",
  "corrections.keep": "Keep",
  "corrections.withdrawn": "Request withdrawn.",
  "corrections.withdrawnAt": "Withdrawn on {time}.",
  "corrections.note": "Note",
  "corrections.approve": "Approve",
  "corrections.reject": "Reject",
  "corrections.rejectNoteRequired": "A rejection requires a note.",
  "corrections.status.Pending": "Open",
  "corrections.status.Approved": "Approved",
  "corrections.status.Rejected": "Rejected",
  "corrections.status.AutoApproved": "Applied automatically",
  "corrections.status.Withdrawn": "Withdrawn",
  "corrections.error.noChanges": "Please change, cancel or add at least one clock entry.",
  "corrections.error.tooManyChanges": "At most 20 changes per request.",
  "corrections.error.reasonRequired": "A reason is required.",
  "corrections.error.reasonTooLong": "The reason is too long (at most 2000 characters).",
  "corrections.error.time": "Please enter a valid time.",
  "corrections.error.future": "This time lies in the future. Only past entries can be corrected.",
  "corrections.breakQualification": "Kind of break",
  "corrections.breakQualificationHint":
    "Rest break only if the break was fixed when it began and you were free from work and from standby.",
  "corrections.change.withQualification": "{change} as {qualification}",
  "absences.new": "Request absence",
  "absences.kind": "Kind",
  "absences.kind.Vacation": "Vacation",
  "absences.kind.Sick": "Sickness",
  "absences.kind.Holiday": "Public holiday",
  "absences.kind.Special": "Special leave",
  "absences.kind.Training": "Training",
  "absences.kind.CompensatoryRest": "Substitute rest day",
  "absences.from": "From",
  "absences.to": "To",
  "absences.halfDay": "Half day",
  "absences.submit": "Request",
  "absences.submitForOther": "Record",
  "absences.submitted": "The absence was requested and is waiting for approval.",
  "absences.recorded": "The absence was recorded.",
  "absences.error.range": "The end date must not be before the start date.",
  "absences.error.halfDay": "A half day is only possible for a single day.",
  "absences.error.past":
    "Vacation can be requested from today ({date}). Vacation for past days is recorded by HR.",
  "absences.error.startMissing": "Please enter the start date.",
  "absences.error.endMissing": "Please enter the end date.",
  "absences.error.pastOfHr":
    "Vacation can be requested from today ({date}). Another HR administrator records your own vacation for past days.",
  "absences.sickHint":
    "Reporting sick does not replace the medical certificate of incapacity for work.",
  "absences.calendar": "Calendar",
  "absences.weekday.1": "Mon",
  "absences.weekday.2": "Tue",
  "absences.weekday.3": "Wed",
  "absences.weekday.4": "Thu",
  "absences.weekday.5": "Fri",
  "absences.weekday.6": "Sat",
  "absences.weekday.7": "Sun",
  "absences.list": "Absences in the month",
  "absences.none": "No absences in this month.",
  "absences.status.Requested": "Requested",
  "absences.status.Approved": "Approved",
  "absences.status.Rejected": "Rejected",
  "absences.status.Cancelled": "Cancelled",
  "absences.cancel": "Cancel",
  "absences.approve": "Approve",
  "absences.reject": "Reject",
  "team.date": "Date",
  "team.members": "Team members",
  "team.empty": "No employees report to this account.",
  "team.column.name": "Name",
  "team.column.state": "State",
  "team.column.work": "Working time",
  "team.column.findings": "Findings",
  "team.state.Off": "Clocked out",
  "team.state.Working": "Working since {time}",
  "team.state.OnBreak": "On break since {time}",
  "team.openDays": "Open days: {count}",
  "team.offlineTerminal": "Verified offline at a terminal: {count}",
  "team.runningShiftDay": "running shift of {date}",
  "team.workUnavailable": "not available",
  "team.stateNoTime.Working": "Working",
  "team.stateNoTime.OnBreak": "On a break",
  "team.azvWithheld": "not shown (§ 7 (8) AZV)",
  "team.azvWithheldCount":
    "Without times and findings under § 7 (8) AZV (federal civil servants): {count}",
  "team.azvNotices.title": "Flexitime balances (§ 7 (8) AZV)",
  "team.azvNotices.hint":
    "Only for the targeted deployment of staff, never to control or assess performance or behaviour. Only whether the flexitime balance crosses a threshold is shown, never the balance itself.",
  "team.azvNotices.above": "Balance above +{hours} hours",
  "team.azvNotices.below": "Balance below −{hours} hours",
  "team.findings": "Violations: {violations}, warnings: {warnings}",
  "team.noFindings": "none",
  "team.clockedIn": "{count} of {total} clocked in",
  "team.workedOn.one": "{count} of {total} worked on {date}",
  "team.workedOn.other": "{count} of {total} worked on {date}",
  "team.approvals": "Open correction requests: {count}",
  "team.toApprovals": "Go to approvals",
  "team.toViolations": "Show violations",
  "violations.filters": "Filters",
  "violations.from": "From",
  "violations.to": "To",
  "violations.status": "Status",
  "violations.status.all": "All",
  "violations.status.Open": "Open",
  "violations.status.Acknowledged": "Acknowledged",
  "violations.status.Resolved": "Resolved",
  "violations.employee": "Employee",
  "violations.employee.all": "All",
  "violations.none": "No violations for this selection.",
  "violations.period": "Period: {from} – {to}",
  "violations.detected": "Detected on {time}",
  "violations.acknowledged": "Acknowledged by {name} on {time}: {comment}",
  "violations.acknowledgedEarlier":
    "Acknowledged by {name} on {time}, before the finding became a violation: {comment}",
  "violations.escalated":
    "The finding became a violation after it was acknowledged and needs to be acknowledged again.",
  "violations.comment": "Comment for the acknowledgement",
  "violations.acknowledge": "Acknowledge",
  "violations.commentRequired": "Please enter a comment.",
  "violations.masked": "Special protection rules apply. Only HR can see the details.",
  "admin.nav": "Administration areas",
  "admin.employees": "Employees",
  "admin.establishments": "Establishments",
  "admin.models": "Work time models",
  "admin.models.leaveHint":
    "The working days of a model decide how many leave days a vacation counts. Change the working days of a person from a given day with a new employment and another model, not by editing a model in use: leave accounts of open years are always computed with the current models.",
  "admin.exports": "Exports",
  "admin.profiles": "Rule profiles",
  "admin.absenceRequests": "Absence requests",
  "admin.absenceRequests.hint":
    "Open requests of all employees and who is absent in the next four weeks. Managers decide the requests of their team on the page “Team”; HR decides sick notes.",
  "admin.save": "Save",
  "admin.saved": "Saved.",
  "admin.new": "Create",
  "admin.edit": "Edit",
  "admin.cancel": "Cancel",
  "admin.search": "Search",
  "admin.employee.personnelNumber": "Personnel number",
  "admin.employee.displayName": "Name",
  "admin.employee.subject": "Identity provider subject (sub)",
  "admin.employee.minorUntil": "Minor until (18th birthday)",
  "admin.employee.open": "Open",
  "admin.employee.back": "Back to the list",
  "admin.employee.notFound": "This employee does not exist.",
  "admin.employments": "Employments",
  "admin.employment.item": "{from} – {to}: {type}, {establishment}, {model}",
  "admin.employment.open": "open",
  "admin.employment.new": "New employment",
  "admin.employment.validFrom": "Valid from",
  "admin.employment.validTo": "Valid to",
  "admin.employment.type": "Type",
  "admin.employment.type.Regular": "Regular",
  "admin.employment.type.MarginalMiniJob": "Marginal employment",
  "admin.employment.type.Trainee": "Trainee",
  "admin.employment.type.Temporary": "Temporary/agency",
  "admin.employment.establishment": "Establishment",
  "admin.employment.model": "Work time model",
  "admin.employment.manager": "Manager",
  "admin.employment.noManager": "None",
  "admin.employment.exempt": "Executive (ArbZG does not apply, § 18) – recording stays active",
  "admin.employment.rotatingShifts": "Regular rotating shifts",
  "admin.employment.roadTransport":
    "Driver or co-driver in road transport (§ 21a ArbZG, not with § 18)",
  "admin.employment.roadTransportHint":
    "Applies to road transport activities within the meaning of Regulation (EC) No 561/2006 or the AETR. Zeitavo then checks at most 60 hours a week, 48 hours on average, 10 hours within 24 hours with night work, working time at other employers and the records under § 21a (7) ArbZG. Zeitavo does not check driving and rest times under the drivers' regulations.",
  "admin.employment.undergroundYouthException":
    "Exception for minors from 16 years underground confirmed (§ 24 (2) JArbSchG)",
  "admin.employment.undergroundYouthExceptionHint":
    "Only tick this if the underground work is necessary to reach the training goal, the person has completed vocational training for underground work or takes or took part in a training programme for young miners approved by the mining authority, and a competent person supervises the work. Without the confirmation every working day of a minor underground is a violation; below 16 years no exception applies.",
  "admin.employment.recording": "Special statutory recording duties",
  "admin.employment.recordingHint":
    "Start and end are always recorded (§ 3 (2) no. 1 ArbSchG). These facts only decide which special norms with their own deadlines apply.",
  "admin.employment.hiredIn": "Agency worker, the tenant is the hirer (temporary/agency work only)",
  "admin.employment.wageOrdinance": "A minimum wage ordinance under § 3a AÜG applies (§ 17c AÜG)",
  "admin.employment.confirm": "Prerequisites confirmed: {reference} ({id}, version {version})",
  "admin.employment.durationOnly":
    "The duration suffices for this duty; start and end are still recorded.",
  "admin.employment.hiredInItem": "Hirer",
  "admin.employment.roadTransportItem": "Road transport (§ 21a ArbZG)",
  "admin.employment.undergroundYouthExceptionItem": "Underground exception (§ 24 (2) JArbSchG)",
  "admin.employment.workingTimeLaw": "Working time law",
  "admin.employment.workingTimeLaw.ArbZG": "Employee (ArbZG)",
  "admin.employment.workingTimeLaw.FederalCivilServantAzv": "Federal civil servant (AZV)",
  "admin.employment.workingTimeLawHint":
    "For federal civil servants Zeitavo deletes the time records under § 7 (7) AZV (period under Tenant → Settings). Zeitavo checks the rules of the AZV for them (rule profile DE-AZV), not those of the ArbZG, and managers see only what § 7 (8) AZV allows. Not for civil servants of the federal states and not for employees to whom the law of civil servants was transferred under § 19 ArbZG. Only for regular employments and trainees.",
  "admin.employment.civilServantItem": "Federal civil servant (AZV)",
  "admin.employment.civilServantConfirm":
    "I understand: the time records of this employment from its first day, at the earliest from today, are deleted under § 7 (7) AZV, at the earliest 3 months after the end of the month of collection and irrevocably.",
  "admin.employment.civilServantPast":
    "The first day lies in the past: the days before today keep the periods of the ArbZG.",
  "admin.employment.confirmedItem": "confirmed: {rules}",
  "admin.employment.profile": "Additional rule profile",
  "admin.employment.noProfile": "None (statutory default profiles)",
  "admin.employment.end": "End on",
  "admin.employment.endAction": "End",
  "admin.employment.missingData": "Please create an establishment and a work time model first.",
  "admin.absences": "Absences",
  "admin.absences.record": "Record absence",
  "admin.consents": "Opt-out consents (§ 7(7) ArbZG)",
  "admin.consents.hint":
    "A deviation under § 7(2a) ArbZG applies only on whole days after the consent was recorded. From the day a revocation is recorded, the statutory limits apply again; Zeitavo does not use the notice period of six months.",
  "admin.consent.item": "Granted on {granted}, document {document}",
  "admin.consent.revoked": "revoked on {revoked}",
  "admin.consent.document": "Document reference",
  "admin.consent.add": "Record consent",
  "admin.consent.revoke": "Record revocation",
  "admin.protection": "Maternity protection (health data, HR only)",
  "admin.protection.item": "{from} – {to}",
  "admin.protection.add": "Record period",
  "admin.protection.sundayConsent": "Consent to Sunday/holiday work",
  "admin.protection.eveningPermit": "Authority permit for work until 22:00",
  "admin.externalWork":
    "Multiple employment (§ 2 (1) ArbZG, § 4 (5) JArbSchG, § 4 (1) MuSchG, § 21a (8) ArbZG)",
  "admin.externalWork.hint":
    "Only periods, durations and – where known – times of day are recorded, never the name of the other employer or the kind of work. The working times at all employers are added up; breaks and rest periods are checked with the times of day. Missing input is reported as incomplete. In road transport, request the statement in writing (§ 21a (8) ArbZG).",
  "admin.externalWork.periods": "Periods of another employment",
  "admin.externalWork.periods.none": "No other employment recorded.",
  "admin.externalWork.period.item": "{from} – {to}",
  "admin.externalWork.period.add": "Record period",
  "admin.externalWork.period.lastDay": "Last day",
  "admin.externalWork.period.end": "End period",
  "admin.externalWork.declarations": "Working time at other employers",
  "admin.externalWork.declarations.none": "No declarations in the last six months.",
  "admin.externalWork.declaration.withdrawn": "withdrawn on {withdrawn}",
  "admin.externalWork.declaration.withdraw": "Withdraw declaration",
  "admin.externalWork.declaration.new": "New declaration",
  "admin.externalWork.declaration.from": "From",
  "admin.externalWork.declaration.to": "To",
  "admin.externalWork.declaration.hint":
    "Empty days mean: no work at other employers. Declarations are needed for every day of a week with own work. Times of day in the time zone of the establishment (for example 17:00-19:00, 19:30-22:00; an end before its start lies on the next day) are needed only for own working days and the days before and after them, to check breaks and rest periods; with them the working time may stay empty. The shift time (working time including rest breaks) is needed for minors; without it the span of the times of day counts.",
  "admin.externalWork.declaration.source": "Source of the declaration",
  "admin.externalWork.declaration.work": "Working time on {date} (h:mm)",
  "admin.externalWork.declaration.shift": "Shift time on {date} (h:mm)",
  "admin.externalWork.declaration.times": "Times of day on {date} (from-to, {zone})",
  "admin.externalWork.declaration.timesItem": "{date}: {times} ({zone})",
  "admin.externalWork.declaration.timesHidden": "{date}: times of day recorded ({zone})",
  "admin.externalWork.declaration.zoneUnknown": "time zone of the establishment unknown",
  "admin.externalWork.declaration.tooLong": "A declaration covers one to at most 31 days.",
  "admin.externalWork.declaration.invalid":
    "Enter durations as h:mm up to 24:00; a shift time needs a working time and is not shorter than it. Times of day start on their day, do not overlap and add up to the working time.",
  "admin.externalWork.source.SelfDeclaration": "Self-declaration of the employee",
  "admin.externalWork.source.OtherEmployerRecord": "Record or certificate of the other employer",
  "admin.externalWork.declaration.add": "Record declaration",
  "admin.preparation": "Preparation and follow-up times (§ 6 GSA Fleisch)",
  "admin.preparation.hint":
    "Preparation and follow-up serving the employer, such as changing clothes, washing or setting up, is working time. Categorize parts of the clocked working time here; if the time lies outside, correct the clock times first. The entries appear in the record for the authority.",
  "admin.preparation.none": "No preparation or follow-up times in the last 31 days.",
  "admin.preparation.item": "{date}, {start}–{end}: {phase}, {activity} ({duration} h)",
  "admin.preparation.withdrawn": "withdrawn on {withdrawn}",
  "admin.preparation.withdraw": "Withdraw entry",
  "admin.preparation.totals": "Total of the last 31 days: {totals}",
  "admin.preparation.new": "New preparation or follow-up time",
  "admin.preparation.date": "Working day",
  "admin.preparation.start": "Start",
  "admin.preparation.end": "End",
  "admin.preparation.phase": "Kind",
  "admin.preparation.activity": "Activity",
  "admin.preparation.phase.Preparation": "Preparation",
  "admin.preparation.phase.FollowUp": "Follow-up",
  "admin.preparation.activity.ChangingClothes": "Changing clothes",
  "admin.preparation.activity.Washing": "Washing, hygiene",
  "admin.preparation.activity.SetUp": "Setting up",
  "admin.preparation.activity.InternalWalking": "Walking inside the establishment",
  "admin.preparation.activity.Other": "Other",
  "admin.preparation.invalid": "Enter start and end; an entry covers at most 4 hours.",
  "admin.preparation.add": "Categorize time",
  "admin.establishment.name": "Name",
  "admin.establishment.timeZone": "Time zone (IANA)",
  "admin.establishment.state": "Federal state",
  "admin.establishment.industries": "Industry characteristics",
  "admin.establishment.exemption": "Exemption for Sunday and holiday work (§ 10 ArbZG)",
  "admin.establishment.regionalHolidays": "Regional holidays",
  "admin.industry.Bakery": "Bakery/confectionery",
  "admin.industry.Hospital": "Hospital/care",
  "admin.industry.Transport": "Transport",
  "admin.industry.Broadcasting": "Broadcasting (recordings and broadcasts)",
  "admin.industry.Agriculture": "Agriculture",
  "admin.industry.AnimalHusbandry": "Animal husbandry",
  "admin.industry.SchwarzArbGSector": "Sector under § 2a SchwarzArbG",
  "admin.industry.PostedWorkersActSector":
    "Sector with working conditions under the AEntG (§ 19 AEntG)",
  "admin.industry.UndergroundMining": "Underground mining",
  "admin.industry.ConstructionSite": "Construction and assembly sites (minors work there)",
  "admin.industry.MeatIndustry":
    "Meat industry: slaughtering, cutting, meat processing (§ 6 GSA Fleisch)",
  "admin.industry.Offshore":
    "Offshore activities in the territorial sea or the EEZ (Offshore-ArbZV, not ship crews)",
  "admin.industry.InlandNavigation": "Inland navigation: crew and on-board staff (BinSchArbZV)",
  // F-59: restaurant and accommodation split, open shops, effects of the industry flags.
  "admin.industry.Restaurant": "Restaurant or bar (food or drinks served on the premises)",
  "admin.industry.Accommodation": "Accommodation (hotel, guest house, holiday lets)",
  "admin.industry.Retail": "Retail with an open shop",
  "admin.establishment.assignIndustry.title": "Assign the industry",
  "admin.establishment.assignIndustry.text.one":
    "The flag “Hospitality” is now split into “Restaurant or bar” and “Accommodation”, because the Youth Employment Protection Act grants its exceptions to restaurants only. Until you assign them, these exceptions do not apply to minors; the reduced rest remains. Please choose “Restaurant or bar”, “Accommodation”, both or neither for this establishment: {names}",
  "admin.establishment.assignIndustry.text.other":
    "The flag “Hospitality” is now split into “Restaurant or bar” and “Accommodation”, because the Youth Employment Protection Act grants its exceptions to restaurants only. Until you assign them, these exceptions do not apply to minors; the reduced rest remains. Please choose “Restaurant or bar”, “Accommodation”, both or neither for these {count} establishments: {names}",
  "admin.establishment.assignIndustry.form":
    "This establishment still carries the split flag “Hospitality”. Until it is assigned, the reduced rest remains and the exceptions for minors do not apply. Choose restaurant or bar, accommodation, both or neither under “Assignment to restaurant or accommodation”; the establishment cannot be saved until then.",
  "admin.establishment.effects": "What the industry flags change",
  "admin.establishment.effects.reducedRest":
    "Rest: may be reduced to {hours} hours with compensation (§ 5 (2) ArbZG)",
  "admin.establishment.effects.noReducedRest": "Rest: 11 hours without reduction (§ 5 (1) ArbZG)",
  "admin.establishment.effects.minorShift":
    "Minors: shift time up to {hours} hours (§ 12 JArbSchG)",
  "admin.establishment.effects.minorEvening":
    "Minors aged 16 or 17: work until {time} (§ 14 JArbSchG)",
  "admin.establishment.effects.minorEveningRestaurant":
    "Minors aged 16 or 17: work until {time}, until 20:00 before a school day with lessons before 9:00 (§ 14 (2) and (4) JArbSchG)",
  "admin.establishment.effects.minorSaturday.Allowed":
    "Minors on Saturdays: allowed; at least two Saturdays a month should stay free (§ 16 JArbSchG)",
  "admin.establishment.effects.minorSaturday.Forbidden":
    "Minors on Saturdays: not allowed (§ 16 JArbSchG)",
  "admin.establishment.effects.minorSunday.Allowed":
    "Minors on Sundays: allowed; at least two Sundays a month free, every second one should stay free (§ 17 JArbSchG)",
  "admin.establishment.effects.minorSunday.Forbidden":
    "Minors on Sundays: not allowed (§ 17 JArbSchG)",
  "admin.establishment.effects.minorHoliday.Allowed":
    "Minors on public holidays: allowed except on 25 December, 1 January, Easter Sunday and 1 May, with a free substitute day in the same or the following week (§ 18 JArbSchG)",
  "admin.establishment.effects.minorHoliday.Forbidden":
    "Minors on public holidays: not allowed (§ 18 JArbSchG)",
  "admin.establishment.effects.minorDecemberEves":
    "Minors on 24 and 31 December: no work after 14:00, also not in restaurants (§ 18 JArbSchG)",
  "admin.establishment.assignIndustry.label": "Assignment to restaurant or accommodation",
  "admin.establishment.assignIndustry.choose": "Please choose",
  "admin.establishment.assignIndustry.Restaurant": "Restaurant or bar",
  "admin.establishment.assignIndustry.Accommodation": "Accommodation",
  "admin.establishment.assignIndustry.Both": "Restaurant or bar and accommodation",
  "admin.establishment.assignIndustry.Neither": "Neither (no restaurant or bar, no accommodation)",
  "admin.establishment.effects.schwarzArbG":
    "Recording under § 17 MiLoG: sector under § 2a SchwarzArbG",
  "check.FreeSaturdays": "Too few free Saturdays",
  "check.AlternateSundays": "Two Sundays in a row worked – every second Sunday should stay free",
  "check.HolidaySubstituteDay":
    "No free substitute day for the work on the public holiday in the same or the following week",
  "check.EveAfternoonWork": "Work after 14:00 on 24 or 31 December",
  "error.TT-1350":
    "The flag “Hospitality” is split. Please choose “Restaurant or bar”, “Accommodation”, both or neither.",
  "admin.holiday.AugsburgerFriedensfest": "Augsburg Peace Festival",
  "admin.holiday.MariaeHimmelfahrt": "Assumption Day",
  "admin.holiday.Fronleichnam": "Corpus Christi",
  "admin.model.name": "Name",
  "admin.model.kind": "Kind",
  "admin.model.kind.Fixed": "Fixed hours",
  "admin.model.kind.Flexitime": "Flexitime",
  "admin.model.kind.TrustBased": "Trust-based working time",
  "admin.model.kind.Shift": "Shift work",
  "admin.model.weekly": "Weekly hours",
  "admin.model.days": "Working days",
  "admin.model.coreStart": "Core time from",
  "admin.model.coreEnd": "Core time to",
  "admin.model.maxPlus": "Flexitime upper limit in hours",
  "admin.model.maxMinus": "Flexitime lower limit in hours",
  "admin.model.item": "{name}: {kind}, {hours} h/week",
  "admin.model.rosterTarget": "Target from the roster",
  "admin.model.rosterTargetHint":
    "Daily target from the published shifts instead of an even distribution: days with a shift count the planned working time, days without a shift within a published roster are days off (target 0); only a credited absence (for example vacation or sickness) on a planned day off takes its share of the model's target and credits it. Days without a published roster keep the target of the model. Takes effect from the first day of a month that has not begun; past and closed months stay unchanged.",
  "admin.model.rosterTargetEnabled": "Take the target from the published roster",
  "admin.model.rosterTargetFrom": "From month",
  "admin.model.rosterTargetMonthInvalid": "Please enter the month as YYYY-MM, for example 2026-12.",
  "admin.model.rosterTargetHistory": "So far: {changes}",
  "admin.model.rosterTargetOn": "on from {date}",
  "admin.model.rosterTargetOff": "off from {date}",
  "admin.model.invalidHours": "Please enter the hours as a number.",
  "admin.day.Monday": "Mon",
  "admin.day.Tuesday": "Tue",
  "admin.day.Wednesday": "Wed",
  "admin.day.Thursday": "Thu",
  "admin.day.Friday": "Fri",
  "admin.day.Saturday": "Sat",
  "admin.day.Sunday": "Sun",
  "admin.export.authority": "Authority export (§ 17(4) ArbZG)",
  "admin.export.authorityHint": "Complete records of one person including the correction history.",
  "admin.export.employee": "Employee",
  "admin.export.from": "From",
  "admin.export.to": "To",
  "admin.export.format": "Format",
  "admin.export.format.surchargeCsv": "CSV: surcharges by time windows (§ 19 AEntG)",
  "export.surchargeCsv.hint":
    "The file holds surcharge intervals only if § 19 AEntG requires surcharges under applicable working conditions to be recorded in the period; otherwise it holds the header only.",
  "export.surchargeCsv.headerOnly":
    "The file holds the header only: in the period § 19 AEntG requires no surcharge intervals to be recorded, or no surcharge by time of day, weekday, date or public holiday applied.",
  "admin.export.download": "Download",
  "admin.export.payroll": "Payroll export",
  "admin.export.month": "Month",
  "admin.export.done": "The file {name} was downloaded.",
  "datev.title": "DATEV payroll export",
  "datev.hint":
    "Transfers hours and days per personnel number and wage type to DATEV LODAS or Lohn und Gehalt. The file contains no names. Agree on wage types and processing keys with your payroll office.",
  "datev.settings": "DATEV settings",
  "datev.program": "Payroll program",
  "datev.program.Lodas": "DATEV LODAS",
  "datev.program.LohnUndGehalt": "DATEV Lohn und Gehalt",
  "datev.consultantNumber": "Consultant number",
  "datev.clientNumber": "Client number",
  "datev.lodasHint":
    "LODAS reads the file through “Mandant → Daten übernehmen → ASCII-Import”. The processing key tells LODAS how to process the value (1 = hours).",
  "datev.lohnUndGehaltHint":
    "For Lohn und Gehalt the payroll office creates an import description once in the ASCII import assistant: separator semicolon, columns personnel number, wage type, hours, days. It chooses the month when importing.",
  "datev.wageTypes": "Wage types",
  "datev.column.transfer": "Transfer",
  "datev.column.wageType": "Wage type",
  "datev.column.processingKey": "Processing key",
  "datev.category.WorkedHours": "Hours worked",
  "datev.category.OvertimeHours": "Overtime (increase of the overtime account)",
  "datev.category.AbsenceHours": "Credited absence hours",
  "datev.category.VacationDays": "Vacation days (days with a target time)",
  "datev.category.SickDays": "Sick days (calendar days)",
  "datev.category.LeavePayoutDays": "Leave days paid in lieu (in the month of the exit)",
  "datev.transferFor": "Transfer {category}",
  "datev.wageTypeFor": "Wage type for {category}",
  "datev.processingKeyFor": "Processing key for {category}",
  "datev.save": "Save DATEV settings",
  "datev.saved": "The DATEV settings were saved.",
  "datev.download": "Download DATEV file",
  "datev.unusableNumbers":
    "DATEV cannot take these personnel numbers (digits only, 1 to 99999, each once): {employees}. Change them in the master data, otherwise the export refuses them.",
  "admin.profile.item": "{id} (version {version}, valid from {from})",
  "admin.profile.hint":
    "The rule profiles implement the statutory rules; Zeitavo ships them in fixed versions, and they cannot be changed here. Stricter values from a collective or works agreement and legal deviations are maintained by the tenant administration under “Tenant → Deviations”.",
  "admin.recordingObligations": "Recording obligations",
  "admin.recordingObligation.item":
    "{ruleId}: {reference} (profile {id}, version {version}, valid from {from})",
  "admin.recordingObligation.retention": "Retention: at least {years} years",
  "admin.recordingObligation.timeliness":
    "Start immediately when work begins (tolerance {minutes} minutes), end and duration on the day of work",
  "admin.recordingObligation.tamperProof":
    "Electronic and tamper-proof; the server timestamp counts, later records and corrections are reported as findings.",
  "admin.recordingObligation.rule":
    "{kind}: {reference} ({id}, version {version}, valid from {from})",
  "admin.recordingObligation.kind.Exemption": "Exemption",
  "admin.recordingObligation.kind.Simplification": "Simplification",
  "admin.recordingObligation.notConfigured":
    "Not applicable yet: thresholds are missing (legal review).",
  "admin.establishment.invalidTimeZone":
    "The time zone is not a valid IANA time zone (for example Europe/Berlin).",
  "privacy.export": "Export my data",
  "privacy.exportHint":
    "Access under Art. 15 GDPR: a copy of your clock entries, corrections, absences, findings, project times, monthly totals, push registrations, master and account data – as PDF to read and as JSON in a structured, machine-readable format. The right to data portability (Art. 20 GDPR) applies only to the categories marked below, not to the working time records required by law.",
  "privacy.exportJson": "Download as JSON",
  "privacy.exportPdf": "Download as PDF",
  "privacy.exportDone": "The file {name} was downloaded.",
  "privacy.records": "Copy of my working time records",
  "privacy.recordsHint":
    "Working time record of a period with all clock entries, corrections and findings, for example as the copy of the records under § 21a (7) ArbZG. The retrieval is recorded in the audit log.",
  "privacy.records.from": "From",
  "privacy.records.to": "To",
  "privacy.records.rangeHint": "On or after „From“, at most {days} days (about three years).",
  "privacy.records.format": "Format",
  "privacy.records.format.surchargeCsv": "CSV: surcharges by time windows (§ 19 AEntG)",
  "privacy.records.download": "Download copy",
  "privacy.configuration": "Configuration for the works agreement",
  "privacy.configurationHint":
    "All settings subject to co-determination (§ 87 (1) no. 2 and 6 BetrVG) as an annex to the works agreement, without personal data.",
  "privacy.auditChain": "Verify audit chain",
  "privacy.auditChain.hint":
    "Every change to working times, master data and settings is recorded in a tamper-evident log (hash chain). The check recomputes the complete chain of your company and compares it with the daily anchors. The result contains no personal data; the check itself is logged.",
  "privacy.auditChain.verify": "Verify now",
  "privacy.auditChain.verifying": "Verifying …",
  "privacy.auditChain.intact":
    "The audit chain is unchanged. Entries checked: {count} (no. 1 to {last}).",
  "privacy.auditChain.empty": "The audit chain has no entries yet.",
  "privacy.auditChain.broken":
    "The audit chain is not intact. Defects: {count}, the first at entry no. {first}. Please report the finding to the operator immediately.",
  "privacy.auditChain.breaks": "Defects found",
  "privacy.auditChain.break": "Entry no. {sequence}: {kind}",
  "privacy.auditChain.moreBreaks": "Further defects not listed here: {count}.",
  "privacy.auditChain.checkedAt": "Checked on {at}. Daily anchors compared: {anchors}.",
  "privacy.auditChain.anchor": "Latest daily anchor: {day}, entry no. {sequence}, checksum:",
  "privacy.auditChain.anchorHint":
    "Keep the checksum outside Zeitavo. If it matches in a later check, the chain is unchanged up to this entry.",
  "privacy.auditChain.noAnchor":
    "There is no daily anchor yet; the first one is created in the night after the first entry.",
  "privacy.auditChain.kind.SequenceGap": "An entry is missing or was inserted (gap in numbering).",
  "privacy.auditChain.kind.PreviousHashMismatch": "The link to the previous entry does not match.",
  "privacy.auditChain.kind.EntryHashMismatch": "The entry was changed afterwards.",
  "privacy.auditChain.kind.TenantMismatch": "The entry does not belong to your company.",
  "privacy.auditChain.kind.HeadMismatch": "Entries are missing at the end of the chain.",
  "privacy.auditChain.kind.AnchorMismatch": "A daily anchor does not match the chain.",
  "privacy.auditChain.kind.unknown": "Defect of another kind.",
  "privacy.device": "Data on this device",
  "privacy.deviceHint":
    "To record without a connection, the app stores on this device only entries not yet sent, the most recently sent entries and the last server state, as well as the last fetched state of your leave account per leave year: totals and items with period, kind of booking and rule, items related to health (leave restored for sickness during leave) only summed up without kind and period. Signing out deletes this data; when someone else signs in, the leave accounts of other people are deleted. Server responses with personal data are not kept in the browser cache.",
  "privacy.deviceCount": "Clock entries stored locally: {count}",
  "privacy.deviceHintWithoutRecords":
    "Your role records no working time of your own. The app therefore keeps no clock entries and no leave account of yours on this device, only your roles for the navigation and technical settings, for example for notifications. Signing out deletes this data. Server responses with personal data are not kept in the browser cache.",
  "privacy.info": "Information under Art. 13 GDPR",
  "privacy.info.purpose.title": "Purpose and legal basis",
  "privacy.info.purpose":
    "Your employer is legally obliged to record the start, end and duration of your daily working time (§ 3(2) no. 1 ArbSchG, BAG 1 ABR 22/21; § 16(2) ArbZG; where applicable § 17 MiLoG). The legal basis is Art. 6(1)(c) GDPR. The data is also used to check maximum working time, breaks and rest periods for your protection.",
  "privacy.info.data.title": "Which data",
  "privacy.info.data":
    "Clock times with time zone and source (web, app), the clock deviation of your device, corrections with reasons, absences, work time model and employment data. No location is recorded; no device fingerprint is created. Error reports of the app to the operator contain only the kind and technical type of an error, the area of the app without identifiers, version and platform; they are deleted after 7 days.",
  "privacy.info.recipients.title": "Who sees the data",
  "privacy.info.recipients":
    "You, your responsible manager (times and findings of the team, without health data and absences), HR, your employer's hosting provider as processor and, in an inspection, the supervisory authority. If you turn on notifications, the push service of your browser or operating system (Google, Apple, Mozilla or Microsoft, possibly processing in the USA) receives a device identifier and encrypted notices without names or working times.",
  "privacy.info.retention.title": "Retention",
  "privacy.info.retention":
    "Periods count from the end of the calendar year; afterwards the data is deleted automatically. The period and its basis per data category are listed below. After you leave and all periods have expired, your name, personnel number and sign-in identifier are anonymized. Your employer may set longer periods.",
  "privacy.info.rights.title": "Your rights",
  "privacy.info.rights":
    "Access (export above), rectification through a correction request, erasure unless a retention duty applies, restriction and objection under Art. 17, 18 and 21 GDPR, data portability under Art. 20 GDPR for the categories marked below, and complaint to a data protection supervisory authority. For all requests contact HR or your employer's data protection officer.",
  "privacy.info.integrity.title": "Traceability",
  "privacy.info.integrity":
    "Entries are never overwritten. Every change creates a new entry with reason, person and time and is recorded in a tamper-evident log.",
  "privacy.info.controller.title": "Controller",
  "privacy.info.controller": "The controller is your employer: {employer}.",
  "privacy.info.contact": "Contact of the data protection officer: {contact}",
  "privacy.info.contactMissing":
    "The contact of the data protection officer has not been provided yet; please contact HR.",
  "privacy.categories.title": "Data categories, legal bases and retention",
  "privacy.categories.hint":
    "Defaults of the software (as of {version}). Your employer may lengthen periods, up to 10 years; statutory minimum periods are never shortened. You can obtain access under Art. 15 GDPR for all categories.",
  "privacy.categories.label.basis": "Legal basis",
  "privacy.categories.label.retention": "Retention",
  "privacy.categories.label.portability": "Data portability (Art. 20 GDPR)",
  "privacy.categories.portability.applies": "Yes",
  "privacy.categories.portability.partly": "Partly",
  "privacy.categories.portability.notApplicable": "No",
  "privacy.categories.portabilityValue": "{value}. {reason}",
  "privacy.categories.workingTime": "Clock entries, break declarations, corrections and findings",
  "privacy.categories.workingTime.basis":
    "Legal obligation (Art. 6(1)(c) GDPR) under § 3(2) no. 1 ArbSchG and § 16(2) ArbZG, depending on the work also § 17 MiLoG, § 19 AEntG, § 17c AÜG, § 21a(7) ArbZG (road transport) or § 6 GSA Fleisch; checks under ArbZG, JArbSchG and MuSchG. For federal civil servants Art. 6(1)(c) and (e) GDPR with § 87(3) BBG and § 7 AZV.",
  "privacy.categories.workingTime.retention":
    "Deleted {years} years after the end of the calendar year. For records under § 16(2) ArbZG, § 21a(7) ArbZG, § 17 MiLoG, § 19 AEntG and § 17c AÜG this is the statutory minimum; for the other recording under the ArbSchG there is no statutory period, the same default applies. For federal civil servants § 7(7) AZV applies from the day their employment was marked: kept at least 3 months after the end of the month of collection, deleted at the latest by the limit of the supreme service authority (6 months after the end of the settlement period or 13 months after the end of the month of collection).",
  "privacy.categories.workingTime.portability":
    "The processing is based on a legal obligation (Art. 6(1)(c) GDPR), not on consent or contract.",
  "privacy.categories.clockLocations":
    "Location when clocking (only if your employer turned it on)",
  "privacy.categories.clockLocations.basis":
    "Set by your employer when switching it on: legitimate interest (Art. 6 (1) (f) GDPR) or a works agreement (Art. 88 GDPR); access to the device location under § 25 TDDDG only after your permission in the system dialog.",
  "privacy.categories.clockLocations.retention":
    "Stored apart from the clock entry and deleted at the latest 60 days after clocking (your employer can set a shorter period); the clock entry stays.",
  "privacy.categories.clockLocations.portability":
    "The processing rests on legitimate interest or a works agreement, not on consent or contract; the locations are part of the access export.",
  "privacy.categories.nfcTags": "Clock entries at NFC tags (only if your employer registered tags)",
  "privacy.categories.nfcTags.basis":
    "Legitimate interest in proving where you clocked (Art. 6 (1) (f) GDPR) or a works agreement (Art. 88 GDPR, § 87 (1) no. 6 BetrVG); no location, a tag is read only after you chose a step.",
  "privacy.categories.nfcTags.retention":
    "The tag and establishment of a clock entry are deleted with the entry, {years} years after the end of the calendar year.",
  "privacy.categories.nfcTags.portability":
    "The processing rests on legitimate interest or a works agreement, not on consent or contract; the access export contains the tag and establishment of your clock entries.",
  "privacy.categories.terminalAccess":
    "Sign-in at a terminal and personal NFC chips (only if your employer set up terminals or chips)",
  "privacy.categories.terminalAccess.basis":
    "PIN and QR badge like the clock entries (Art. 6(1)(c) GDPR), stored only as a hash, with the day they were issued and failed sign-ins. A personal NFC chip like the clock entries at NFC tags, based on a legitimate interest (Art. 6 (1) (f) GDPR) or a works agreement (Art. 88 GDPR, § 87 (1) no. 6 BetrVG), with its name, the day it was issued and its last use, its identifier only as a hash. No photos, no biometrics, no location.",
  "privacy.categories.terminalAccess.retention":
    "PIN and QR badge are deleted as soon as HR removes them, at the latest with the end of your last employment. Personal NFC chips are kept until the end of your last employment; a chip HR removes before is blocked instead of deleted.",
  "privacy.categories.terminalAccess.portability":
    "Your employer issues the sign-in and the chips; the access export contains the days of issue, failed sign-ins and your chips, never the PIN, the badge or the identifier of a chip.",
  "privacy.categories.rosters":
    "Rosters and the right „create rosters“ (only if your employer turned rostering on)",
  "privacy.categories.rosters.basis":
    "Performance of the employment (Art. 6 (1) (b) GDPR, § 106 GewO); a works agreement sets the planning principles (§ 87 (1) no. 2 and 3 BetrVG). A roster is a plan, not a record of your working time.",
  "privacy.categories.rosters.retention":
    "Planned shifts are deleted {years} years after the end of the calendar year in which the roster ends; the right „create rosters“ likewise after it ends.",
  "privacy.categories.rosters.portability":
    "Your employer creates the rosters; the access export contains your shifts and your right „create rosters“.",
  "privacy.categories.onCallPeriods": "On-call duty and on-site standby",
  "privacy.categories.onCallPeriods.basis":
    "Performance of the employment relationship and checking the rest period (Art. 6 (1) (b) and (c) GDPR, §§ 5, 7 ArbZG); only start, end and kind are stored, no reason.",
  "privacy.categories.onCallPeriods.retention":
    "Periods are deleted {years} years after the end of the calendar year in which they end, like the clock events.",
  "privacy.categories.onCallPeriods.portability":
    "Your employer enters the periods; the self-service export contains your periods.",
  "privacy.categories.rosterRequests":
    "Availability and requests about shifts (only if your employer switched rostering on)",
  "privacy.categories.rosterRequests.basis":
    "Performance of the employment relationship (Art. 6 (1) (b) GDPR); only you and the planners of your team or establishment see your availability, never colleagues.",
  "privacy.categories.rosterRequests.retention":
    "Availability is deleted {years} years after the end of the calendar year of its last day, requests with their roster; availability also with the anonymization after you leave.",
  "privacy.categories.rosterRequests.portability":
    "You provide this data yourself; the self-service export contains your availability with notes and your requests.",
  "privacy.categories.projectTimes": "Project times (only if your employer turned them on)",
  "privacy.categories.projectTimes.basis":
    "Set by your employer, for example the employment contract (Art. 6(1)(b) GDPR) or legitimate interest (point (f)); never part of the statutory working time records.",
  "privacy.categories.projectTimes.retention":
    "Like clock entries: deleted {years} years after the end of the calendar year unless your employer sets a longer period (for example as basis of customer invoices).",
  "privacy.categories.projectTimes.portability":
    "Only if your employer bases project time tracking on the employment contract.",
  "privacy.categories.budgetWarnings":
    "Budget warnings (only if you are responsible for a project with a budget)",
  "privacy.categories.budgetWarnings.basis":
    "Set by your employer as for project times, for example the employment contract (Art. 6(1)(b) GDPR) or legitimate interest (point (f)). Stored are the budget, the period and the threshold reached with you as recipient; push message and e-mail name no project, no hours and no names.",
  "privacy.categories.budgetWarnings.retention":
    "Deleted 30 days after sending, warnings never sent 30 days after storing, at the latest with the budget.",
  "privacy.categories.budgetWarnings.portability":
    "The app creates the warnings from the project time of everybody involved, they do not come from you; the access export contains them.",
  "privacy.categories.monthlyTotals":
    "Monthly totals for payroll and the values of downloaded payroll files (check log)",
  "privacy.categories.monthlyTotals.basis":
    "Employment contract and tax duties (Art. 6(1)(b) and (c) GDPR; § 41 EStG, § 147 AO).",
  "privacy.categories.monthlyTotals.retention":
    "Deleted {years} years after the end of the calendar year (payroll account, § 41 EStG). Whether longer tax periods apply depends on the kind of document and is decided by your employer. Months that lie completely in your time as a federal civil servant follow § 7 (7) AZV like the time records; months with days as an employee keep this period.",
  "privacy.categories.monthlyTotals.portability":
    "Your employer calculates the totals; Art. 20 GDPR covers only data you provided yourself.",
  "privacy.categories.timeAccountBookings":
    "Bookings on the time account (payout and time off in lieu by HR)",
  "privacy.categories.timeAccountBookings.basis":
    "Employment contract and payroll (Art. 6(1)(b) and (c) GDPR; § 2 (2) MiLoG, § 41 EStG). A booking never changes your recorded working time.",
  "privacy.categories.timeAccountBookings.retention":
    "Deleted {years} years after the end of the calendar year of the booking month, like the monthly totals (payroll account, § 41 EStG).",
  "privacy.categories.timeAccountBookings.portability":
    "Your employer makes the bookings; the access export contains them with their reason.",
  "privacy.categories.monthConfirmations":
    "Confirmations and approvals of your months (month closing)",
  "privacy.categories.monthConfirmations.basis":
    "Performance of the employment and a legitimate interest in proving which balance was approved with which reservation, also to defend against claims (Art. 6(1)(b) and (f) GDPR); not a payroll record. Your confirmation is voluntary and waives nothing.",
  "privacy.categories.monthConfirmations.retention":
    "Deleted {years} years after the end of the calendar year of the month, like the monthly totals (no statutory period); while a confirmation is kept, your name is not anonymized after you leave. Months that lie completely in your time as a federal civil servant follow § 7 (7) AZV like the time records.",
  "privacy.categories.monthConfirmations.portability":
    "You give your confirmation and comment yourself, your employer gives the approval; the access export contains both.",
  "privacy.categories.absences": "Absences",
  "privacy.categories.absences.basis":
    "Employment contract and legal obligation (Art. 6(1)(b) and (c) GDPR; BUrlG, EFZG, education leave laws of the federal states; § 15 BBiG and §§ 9, 10 JArbSchG for times of vocational school and examinations with times of day and travel); sickness and rehabilitation as health data under Art. 9(2)(b) GDPR.",
  "privacy.categories.absences.retention":
    "Deleted {years} years after the end of the calendar year in which the absence ended. There is no statutory retention period; the period follows the regular limitation period (§§ 195, 199 BGB).",
  "privacy.categories.absences.portability":
    "For your vacation requests (employment contract), not for sickness or rehabilitation.",
  "privacy.categories.maternityProtection": "Maternity protection periods",
  "privacy.categories.maternityProtection.basis":
    "Duties under the Maternity Protection Act for health data (Art. 9(2)(b) GDPR).",
  "privacy.categories.maternityProtection.retention":
    "Deleted {years} years after the end of the calendar year in which the period ended (§ 27(5) MuSchG).",
  "privacy.categories.maternityProtection.portability":
    "The processing is based on Art. 9(2)(b) GDPR, not on consent or contract.",
  "privacy.categories.optOutConsents": "Consents under § 7(7) ArbZG (opt-out)",
  "privacy.categories.optOutConsents.basis":
    "Legal obligation to keep the register (Art. 6(1)(c) GDPR with § 16(2) ArbZG). This consent under labor law is not a consent under the GDPR.",
  "privacy.categories.optOutConsents.retention":
    "Deleted {years} years after the end of the calendar year of the revocation or of your leaving (§ 16(2) ArbZG).",
  "privacy.categories.optOutConsents.portability":
    "The processing is based on a legal obligation (Art. 6(1)(c) GDPR).",
  "privacy.categories.externalWorkingTime":
    "Working time at other employers (only with multiple employment)",
  "privacy.categories.externalWorkingTime.basis":
    "Legal obligation to add up the working time (Art. 6(1)(c) GDPR with § 2(1) ArbZG, § 4(5) JArbSchG, § 4(1) MuSchG or § 21a(8) ArbZG). Durations, the times of day where declared (for breaks and rest periods) and the source of the declaration are stored; the name of the other employer and the kind of work are not.",
  "privacy.categories.externalWorkingTime.retention":
    "Deleted {years} years after the end of the calendar year, like your clock events.",
  "privacy.categories.externalWorkingTime.portability":
    "The processing is based on a legal obligation (Art. 6(1)(c) GDPR).",
  "privacy.categories.preparationTimes":
    "Preparation and follow-up times (for example changing clothes), categorized by HR",
  "privacy.categories.preparationTimes.basis":
    "In the meat industry a legal obligation (Art. 6(1)(c) GDPR with § 6(1) GSA Fleisch), otherwise your employer sets the basis (for example the employment contract). Only kind and time are stored, no free text.",
  "privacy.categories.preparationTimes.retention":
    "Deleted {years} years after the end of the calendar year, like your clock events.",
  "privacy.categories.preparationTimes.portability":
    "Your employer records the entries, not you; you receive them through the export (Art. 15 GDPR).",
  "privacy.categories.masterData": "Master and employment data",
  "privacy.categories.masterData.basis":
    "Employment contract and legal obligation (Art. 6(1)(b) and (c) GDPR).",
  "privacy.categories.masterData.retention":
    "Anonymized after you leave, once no record refers to you any more (usually after the monthly totals expire).",
  "privacy.categories.masterData.portability":
    "For contract data you provided yourself (for example your name), not for data your employer assigns, such as the personnel number.",
  "privacy.categories.pushSubscriptions": "Notifications (push registration of your device)",
  "privacy.categories.pushSubscriptions.basis":
    "Your consent by turning them on (Art. 6(1)(a) GDPR, § 25(1) TDDDG), revocable at any time on this page.",
  "privacy.categories.pushSubscriptions.retention":
    "Deleted when you turn notifications off, sign out or the push service reports the device as invalid.",
  "privacy.categories.pushSubscriptions.portability":
    "The processing is based on your consent. The export lists your registrations with channel, push service, checksum and date; it leaves out the device address, token and keys because they only work with this service.",
  "nav.tenant": "Tenant",
  "nav.billing": "Billing",
  "roles.title": "Roles",
  "roles.intro":
    "Roles belong to the application, not to the sign-in service. A change applies from the chosen day; earlier roles stay traceable.",
  "roles.roles": "Roles",
  "roles.validFrom": "Valid from",
  "roles.save": "Save roles",
  "roles.cancel": "Cancel",
  "roles.saved": "Roles saved.",
  "roles.none": "none",
  "roles.noneHint": "Without a role the person has no access from this day on.",
  "roles.edit": "Change",
  "roles.editFor": "Change roles of {name}",
  "roles.editTitle": "Roles of {name}",
  "roles.column.name": "Name",
  "roles.column.roles": "Roles",
  "roles.column.since": "Since",
  "roles.column.actions": "Actions",
  "roles.name.Employee": "Employee",
  "roles.name.Manager": "Manager",
  "roles.name.HrAdmin": "HR",
  "roles.name.TenantAdmin": "Tenant administration",
  "roles.name.Auditor": "Auditor",
  "roles.name.WorksCouncil": "Works council",
  "roles.name.BillingAdmin": "Billing",
  "roles.establishments": "HR only for these establishments",
  "roles.establishments.hint":
    "Without a choice the HR role applies to every establishment. Tenant administration and audit always apply to every establishment.",
  "roles.establishments.all": "all establishments",
  "roles.establishments.count": "{count} establishments",
  "roles.establishments.hintLimited":
    "Without a choice the current limit stays. It is lifted only with „Lift limit“.",
  "roles.establishments.unreadable": "Limit unreadable – no establishment visible",
  "roles.scope.unreadableHint":
    "The stored limit to establishments cannot be read: the HR role sees no establishment. Choose the establishments again or lift the limit explicitly; without a choice it stays.",
  "roles.scope.notApplicable":
    "The HR role is limited to establishments. Without HR or together with tenant administration or audit the limit cannot stay. Keep HR without these roles or lift the limit explicitly.",
  "roles.scope.lift": "Lift limit",
  "roles.scope.liftConsequence":
    "The HR role applies to every establishment from the chosen day on. A later grant of HR, for example through SCIM, is then no longer limited either.",
  "roles.scope.liftConfirm": "Lift limit",
  "roles.scope.liftPending":
    "The limit to establishments is lifted when you save. „Cancel“ discards the change.",
  "roles.column.establishments": "Establishments",
  "establishmentFilter.label": "Establishment",
  "establishmentFilter.all": "All establishments",
  "establishmentScope.limited": "Your HR rights apply only to: {names}.",
  "establishmentOverview.title": "Overview per establishment",
  "establishmentOverview.intro":
    "Numbers only, no persons. Open days are working days not closed yet; open violations count with a period that ends within the last 31 days.",
  "establishmentOverview.column.name": "Establishment",
  "establishmentOverview.column.active": "Active employees",
  "establishmentOverview.column.openDays": "Open days",
  "establishmentOverview.column.violations": "Open violations",
  "establishmentOverview.empty": "No establishments.",
  "legal.title": "Contracts and legal documents",
  "legal.intro":
    "These are the versions that apply to your company. As an administrator you accept new versions.",
  "legal.promptTitle": "New contract version",
  "legal.prompt":
    "Please review the following documents. Clocking and exports keep working regardless.",
  "legal.kind.Terms": "General terms and conditions",
  "legal.kind.Dpa": "Data processing agreement (DPA) with TOMs",
  "legal.kind.Sla": "Service level agreement",
  "legal.kind.PrivacyNotice": "Privacy notice",
  "legal.version": "Version {version}, valid from {day}",
  "legal.open": "Open {document}",
  "legal.inForce": "Version {version} applies to you.",
  "legal.none": "No version accepted yet.",
  "legal.accepted": "Accepted.",
  "legal.declined": "Declined; version {version} keeps applying until the end of the contract.",
  "legal.informationOnly": "For information, no acceptance needed.",
  "legal.accept": "Accept {document}",
  "legal.decline": "Decline {document}",
  "legal.declineHint":
    "If you decline, the previous version keeps applying until the end of your contract.",
  "legal.later": "Later",
  "legal.empty": "The operator has not published any legal documents.",
  "subscription.title": "Plan and usage",
  "subscription.plan": "Plan: {plan}",
  "subscription.status.Active": "active",
  "subscription.status.PastDue": "payment due",
  "subscription.status.Suspended": "administration restricted",
  "subscription.status.ExportWindow": "ended, export only",
  "subscription.trial": "The trial ends on {day}.",
  "subscription.readOnly":
    "Administration is read-only. Clocking, own times and exports keep working.",
  "serviceStatus.exportWindow":
    "The contract has ended. Until the deletion the data can only be read and exported.",
  "serviceStatus.exportWindowUntil":
    "The contract has ended. Until the data is deleted on {day} it can only be read and exported.",
  "serviceStatus.readOnly":
    "The administration is read-only. Clocking, own times and exports keep working.",
  "serviceStatus.toExport": "To the data export",
  "serviceStatus.toBilling": "Go to billing",
  "subscription.limitExceeded":
    "More active employees than the plan includes ({limit}). Nothing is blocked; the invoice adjusts.",
  "subscription.usage": "Active employees per month",
  "subscription.month": "Month",
  "subscription.active": "Active",
  "billing.profile": "Billing details",
  "billing.profileMissing":
    "No billing details yet. Invoices need the company name, the address and an invoice address.",
  "billing.companyName": "Company name (without legal form)",
  "billing.legalForm": "Legal form",
  "billing.street": "Street and number",
  "billing.postalCode": "Postal code",
  "billing.city": "City",
  "billing.vatId": "VAT ID",
  "billing.vatIdHint":
    "In Germany the VAT ID is optional. Companies in other EU countries must provide it, because the invoice is then issued without VAT (reverse charge).",
  "billing.invoiceEmail": "E-mail address for invoices",
  "billing.save": "Save billing details",
  "billing.paymentMethod": "Payment method: {method}.",
  "billing.payment.SepaDebit": "SEPA direct debit",
  "billing.payment.Card": "Card",
  "billing.payment.BankTransfer": "Bank transfer",
  "billing.notAvailable":
    "This installation runs without an operator; there are no billing details.",
  "payment.title": "Subscription and payment",
  "payment.notAvailable": "A subscription cannot be taken out online in this installation.",
  "payment.none": "No subscription has been taken out yet.",
  "payment.state": "Subscription {plan}:",
  "payment.unknownPlan": "unknown plan",
  "payment.status.Incomplete": "payment being confirmed",
  "payment.status.Trialing": "trial period",
  "payment.status.Active": "active",
  "payment.status.PastDue": "payment failed, being retried",
  "payment.status.Unpaid": "unpaid",
  "payment.status.Paused": "paused",
  "payment.status.Canceled": "ended",
  "payment.periodEnd": "Current billing period until {day}.",
  "payment.cancelAt": "The subscription ends on {day}.",
  "payment.plan": "Plan",
  "payment.checkout": "Take out subscription",
  "payment.checkoutHint":
    "You will be forwarded to the payment provider and choose SEPA direct debit or card there. Charged are the monthly base fee, from the second month the active employees of the previous month and, with the first subscription, the one-time setup fee if the plan has one.",
  "payment.portal": "Manage payment method or cancel",
  "payment.changePlan.title": "Change plan",
  "payment.changePlan.plan": "New plan",
  "payment.changePlan.hint":
    "A higher plan applies at once and is charged pro rata for the current period; a lower plan applies from the end of the paid period.",
  "payment.changePlan.submit": "Change plan",
  "payment.changePlan.confirm":
    "Change to the plan {plan}? If it is higher, the difference is charged pro rata at once.",
  "payment.changePlan.confirmButton": "Confirm change",
  "payment.changePlan.cancel": "Cancel",
  "payment.changePlan.now":
    "The plan {plan} applies from now. It appears here once the payment provider confirms the change.",
  "payment.changePlan.atPeriodEnd": "The plan {plan} applies from {day}.",
  "payment.returned.success":
    "Thank you! The subscription appears here once the payment provider confirms it.",
  "payment.returned.cancelled": "Checkout was cancelled; nothing was charged.",
  "payment.onRequest":
    "Your plan {plan} is billed as agreed. A subscription for it cannot be taken out or changed online.",
  "payment.onRequest.contact": "Agree changes to your plan with the provider:",
  "payment.onRequest.noContact": "Agree changes to your plan with your contact at the provider.",
  "invoices.title": "Invoices",
  "invoices.hint":
    "Invoices and credit notes of the provider as PDF with an embedded e-invoice (ZUGFeRD). Keep them for eight years.",
  "invoices.none": "There are no invoices yet.",
  "invoices.notAvailable": "This installation runs without an operator; there are no invoices.",
  "invoices.number": "Number",
  "invoices.issueDate": "Date",
  "invoices.period": "Period",
  "invoices.gross": "Amount (gross)",
  "invoices.periodRange": "{from} – {to}",
  "invoices.kind.Invoice": "Invoice",
  "invoices.kind.CreditNote": "Credit note for {number}",
  "invoices.reverseCharge": "Reverse charge",
  "invoices.download": "Download PDF {number}",
  "invoices.downloaded": "{name} was saved.",
  "contacts.title": "Contacts for the provider",
  "contacts.hint":
    "The provider writes to these addresses about the contract, invoices, data breaches and changes of subprocessors. Contract, billing and security are mandatory.",
  "contacts.kind.Contract": "Contract",
  "contacts.kind.Billing": "Billing",
  "contacts.kind.Security": "Security (data breaches, subprocessors)",
  "contacts.kind.DataProtection": "Data protection officer",
  "contacts.name": "Name or function",
  "contacts.email": "E-mail address",
  "contacts.save": "Save contacts",
  "contacts.missing": "Still missing: {list}.",
  "contacts.notAvailable": "This installation runs without an operator; there are no contacts.",
  "tenant.export": "Complete data export",
  "tenant.exportHint":
    "All data of your company as ZIP archive: JSON with JSON schema, CSV per table and PDF reports per person and year (switching providers under the Data Act, free of charge). You must keep working time records for at least two years, also after the contract ends.",
  "tenant.exportRequest": "Request export",
  "tenant.exportState.pending": "Building",
  "tenant.exportState.failed": "Failed",
  "tenant.exportState.expired": "Expired",
  "tenant.exportState.ready": "Ready",
  "tenant.exportPending": "Export of {time} is being built …",
  "tenant.exportFailed": "Export of {time} failed. Please request a new one.",
  "tenant.exportExpired": "Export of {time} has expired.",
  "tenant.exportReady": "Export of {time}, {size}, downloadable until {until}.",
  "tenant.exportDownload": "Download export of {time}",
  "tenant.exportChecksum": "SHA-256: {hash}",
  "tenant.exportNone": "No export has been requested yet.",
  "emergency.title": "Emergency access of support",
  "emergency.intro":
    "The operator's support sees no data of your company. Only if you approve a request may it read for a limited time; every access is recorded in the audit log.",
  "emergency.none": "No requests.",
  "emergency.requestBy": "Request from {name}",
  "emergency.details": "Requested {requested} for {hours} h.",
  "emergency.until": "Valid until {until}.",
  "emergency.hours": "Approval in hours",
  "emergency.hoursInvalid": "Please enter a whole number from 1 to {max}.",
  "emergency.approve": "Approve",
  "emergency.reject": "Reject",
  "emergency.revoke": "End access",
  "emergency.state.requested": "awaiting decision",
  "emergency.state.active": "active",
  "emergency.state.ended": "expired",
  "emergency.state.rejected": "rejected",
  "emergency.state.revoked": "ended early",
  "scim.title": "SCIM access",
  "scim.intro":
    "With SCIM access the identity system of the employer (for example Microsoft Entra ID) creates employees, changes roles and blocks people who left at once. Several credentials can be valid at the same time: create a new one, enter it in the identity system, revoke the old one.",
  "scim.name": "Name",
  "scim.create": "Create credential",
  "scim.secretOnce": "The secret is shown only now. Enter it in the identity system.",
  "scim.baseUrl": "SCIM address (tenant URL)",
  "scim.tokenUrl": "Token address (OAuth 2.0)",
  "scim.clientId": "Client ID",
  "scim.secret": "Secret (client secret or secret token)",
  "scim.column.name": "Name",
  "scim.column.created": "Created",
  "scim.column.expires": "Valid until",
  "scim.column.state": "State",
  "scim.column.actions": "Actions",
  "scim.state.active": "valid",
  "scim.state.expired": "expired",
  "scim.state.revoked": "revoked",
  "scim.revoke": "Revoke",
  "scim.revokeFor": "Revoke credential {name}",
  "publicApi.title": "Public API",
  "publicApi.intro":
    "Your own systems (for example payroll, ERP or reporting) read working days, findings, employees, absences, payroll months and project time, request absences and maintain projects, customers and activities – with OAuth 2.0 client credentials and signed webhooks.",
  "publicApi.planHint": "Only in plans with the additional function “Public API and webhooks”.",
  "publicApi.rotation":
    "To change a secret: create a new client, enter it in the system, revoke the old one.",
  "publicApi.clients": "API clients",
  "publicApi.name": "Name of the system",
  "publicApi.scopes": "Permissions (scopes)",
  "publicApi.healthScopeHint":
    "“absence-kinds:read” shows the kind of absences and the sick days of the payroll month (health data, Art. 9 GDPR); without it every absence appears as “absent”. Absences requested through “absences:write” are still decided in Zeitavo.",
  "publicApi.validityDays": "Validity in days (1 to 365)",
  "publicApi.createClient": "Create API client",
  "publicApi.secretOnce": "The secret is shown only now. Enter it in the system.",
  "publicApi.tokenPath": "Token path (append to the address of the API)",
  "publicApi.clientId": "Client ID",
  "publicApi.secret": "Client secret",
  "publicApi.column.name": "System",
  "publicApi.column.scopes": "Permissions",
  "publicApi.column.expires": "Valid until",
  "publicApi.column.state": "State",
  "publicApi.column.actions": "Actions",
  "publicApi.column.url": "Receiver",
  "publicApi.column.events": "Events",
  "publicApi.state.active": "valid",
  "publicApi.state.expired": "expired",
  "publicApi.state.revoked": "revoked",
  "publicApi.state.enabled": "active",
  "publicApi.state.disabled": "disabled",
  "publicApi.revoke": "Revoke",
  "publicApi.revokeFor": "Revoke API client {name}",
  "publicApi.webhooks": "Webhooks",
  "publicApi.webhooksIntro":
    "Notifications carry identifiers only; your system reads the details through the API. Every delivery is signed with HMAC-SHA256 and repeated up to eight times.",
  "publicApi.url": "Receiver address (HTTPS, port 443)",
  "publicApi.events": "Events",
  "publicApi.eventsHint":
    "“violation.escalated” reports a stored finding that became a violation, such as a compensation not granted. Existing webhooks do not receive it; create an additional webhook with this event only and leave the existing ones unchanged.",
  "publicApi.createWebhook": "Create webhook",
  "publicApi.signingKeyOnce": "The signing key is shown only now. Enter it in the receiver.",
  "publicApi.signingKey": "Signing key",
  "publicApi.disable": "Disable",
  "publicApi.disableFor": "Disable webhook {url}",
  "settings.title": "Settings",
  "settings.features": "Monitoring-related features",
  "settings.featuresHint":
    "Off by default. Switch on only with a works agreement (§ 87 (1) no. 6 BetrVG).",
  "settings.feature.locationCaptureEnabled": "Capture the location when clocking",
  "settings.feature.projectTimeReportsPerPersonEnabled": "Evaluate project time per person",
  "settings.feature.teamComparisonsEnabled": "Comparisons within a team",
  "settings.feature.projectTimeTrackingEnabled": "Project time tracking",
  "settings.feature.managersMayCreateProjects": "Managers may create projects",
  "settings.feature.managersMayConfigureProjectRequirement":
    "Managers may change the project requirement of their team",
  "settings.retention": "Retention (years after the end of the calendar year)",
  "settings.retentionHint":
    "Leave empty for the platform default. Periods can only be lengthened, to at most 10 years.",
  "settings.retention.rawDataYears": "Clock events and findings",
  "settings.retention.absenceYears": "Absences",
  "settings.retention.aggregateYears": "Monthly sums (payroll)",
  "settings.platformDefault": "Default: {years}",
  "settings.azv": "Federal civil servants (§ 7 (7) AZV)",
  "settings.azv.hint":
    "Applies only to employments with the working time law „Federal civil servant (AZV)“. Their time records are kept at least 3 months after the end of the month of collection and deleted at the latest by the limit the supreme service authority sets; the years above do not apply to them. Without a choice the earlier of both latest dates applies.",
  "settings.azv.limitation":
    "For federal civil servants Zeitavo checks the rules of the AZV (rule profile DE-AZV), not those of the ArbZG. Absences keep their own period.",
  "settings.azv.mode": "Choice of the supreme service authority",
  "settings.azv.mode.none": "No choice (earlier latest date)",
  "settings.azv.mode.AfterSettlementPeriod":
    "Deletion after the end of the settlement period (at most 6 months)",
  "settings.azv.mode.AfterCollectionMonth":
    "Deletion after the end of the month of collection (at most 13 months)",
  "settings.azv.deletionMonths": "Deletion after months (3 to the maximum)",
  "settings.azv.latest": "At most: {months}",
  "settings.azv.settlementStart": "Start of the settlement period",
  "settings.azv.settlementStartDefault": "Default: {month}",
  "settings.azv.settlementLength": "Length of the settlement period",
  "settings.azv.settlementLengthDefault": "Default: 12 months (calendar year)",
  "settings.azv.months": "{months} months",
  "settings.azvFlexitime": "Flexitime of federal civil servants (§ 7 (4) and (5) AZV)",
  "settings.azvFlexitime.hint":
    "The flexitime account settles in the settlement period above. A carry-over above 40 hours, a balance below −40 hours and too many flexi days appear as hints to HR; nothing is forfeited automatically.",
  "settings.azvFlexitime.maxFlexiDays": "Flexi days per year",
  "settings.azvFlexitime.maxFlexiDaysHint":
    "More than 12 flexi days only when it helps the service or suits its circumstances (§ 7 (5) sentence 2 AZV). Half flexi days count half only with a core time.",
  "settings.azvFlexitime.default": "Default: {days} (§ 7 (5) sentence 1 AZV)",
  "settings.azvFlexitime.days": "{days} flexi days",
  "settings.azv.confirm":
    "I understand: after {days} days the next nightly run irrevocably deletes older time records of federal civil servants by the new period. Until then, what the previous period keeps stays.",
  "settings.branding": "Appearance",
  "settings.defaultLanguage": "Default language",
  "settings.languageUnset": "App default",
  "settings.accentColor": "Accent color (#rrggbb)",
  "settings.accentColorHint":
    "Applies to the light theme; the color needs enough contrast (WCAG 2.2 AA).",
  "settings.save": "Save settings",
  "settings.saved": "Settings saved.",
  "settings.logo": "Logo (PNG or JPEG)",
  "settings.logoHint": "At most 64 KB; shown in the header.",
  "settings.logoTooLarge": "The logo is larger than 64 KB.",
  "settings.logoCurrent": "Current logo",
  "settings.logoRemove": "Remove logo",
  "overrides.title": "Stricter rule profiles",
  "overrides.intro":
    "Stricter values than in the rule profiles based on a collective or works agreement, for example a longer rest. Statutory limits cannot be relaxed here; use the legal deviations for that. Only released parameters can be changed; earlier versions are kept.",
  "overrides.none": "No overrides – the rule profiles apply.",
  "overrides.row": "Override {number}",
  "overrides.profile": "Rule profile",
  "overrides.validFrom": "Valid from",
  "overrides.validTo": "Valid until",
  "overrides.allowed": "Changeable: {parameters}",
  "overrides.legalBasis": "Legal basis",
  "overrides.legalBasisKind": "Kind of legal basis",
  "overrides.kind.CollectiveAgreement": "Collective agreement",
  "overrides.kind.WorksAgreement": "Works agreement",
  "overrides.remove": "Remove override {number}",
  "overrides.add": "Add override",
  "overrides.save": "Save overrides",
  "overrides.saved": "Overrides saved. They apply to all following evaluations.",
  "overrides.ineffective":
    "No effect because relaxed: {parameters}. Relaxations need a legal deviation with a fitting norm.",
  "legalDeviations.title": "Legal deviations (§ 7, §§ 14, 15 ArbZG)",
  "legalDeviations.intro":
    "Statutory limits can only be relaxed with a fitting legal basis: a collective agreement or a works agreement based on one (§ 7 ArbZG), a permit of the authority (§ 7 (5), § 15 ArbZG) or an emergency (§ 14 ArbZG). Each norm allows only certain parameters within fixed limits. Permits and emergencies end automatically with their last day. Entries are never changed, only revoked; every step is logged. Expired and revoked entries are deleted together with the time records of their period. The configuration export for the works agreement lists the deviations in force and scheduled.",
  "legalDeviations.list": "Recorded deviations",
  "legalDeviations.none": "No deviations – the statutory limits apply.",
  "legalDeviations.column.norm": "Norm",
  "legalDeviations.column.basis": "Legal basis",
  "legalDeviations.column.scope": "Scope",
  "legalDeviations.column.period": "Period",
  "legalDeviations.column.parameters": "Parameters",
  "legalDeviations.column.status": "State",
  "legalDeviations.column.actions": "Actions",
  "legalDeviations.type.CollectiveAgreement": "Collective agreement",
  "legalDeviations.type.WorksAgreement": "Works agreement based on a collective agreement",
  "legalDeviations.type.AuthorityPermit": "Permit of the authority",
  "legalDeviations.type.EmergencyCase": "Emergency or exceptional case",
  "legalDeviations.type.OperationalReasons": "Technical or organisational reasons",
  "legalDeviations.scope.Tenant": "Whole employer",
  "legalDeviations.scope.Establishment": "Establishment",
  "legalDeviations.scope.Employee": "Employee",
  "legalDeviations.status.Scheduled": "Scheduled",
  "legalDeviations.status.Active": "In force",
  "legalDeviations.status.Expired": "Expired",
  "legalDeviations.status.Revoked": "Revoked",
  "legalDeviations.status.Ineffective": "No effect (norm changed)",
  "legalDeviations.period": "{from} to {to}",
  "legalDeviations.openEnd": "open",
  "legalDeviations.revoke": "Revoke deviation {reference}",
  "legalDeviations.revokeHint":
    "After the revocation the deviation applies to no day, not even in the past. To end a deviation early, revoke it and record it again with a shorter period.",
  "legalDeviations.revokeReason": "Reason of the revocation",
  "legalDeviations.revokeConfirm": "Revoke",
  "legalDeviations.cancel": "Cancel",
  "legalDeviations.revoked": "Deviation revoked.",
  "legalDeviations.new": "New deviation",
  "legalDeviations.type": "Kind of legal basis",
  "legalDeviations.norm": "Norm",
  "legalDeviations.noNorm": "No norm allows this legal basis.",
  "legalDeviations.conditions": "Preconditions: {conditions}",
  "legalDeviations.allowed": "Allowed parameters (* required, seconds): {parameters}",
  "legalDeviations.scopeKind": "Scope",
  "legalDeviations.choose": "Please choose",
  "legalDeviations.validFrom": "Valid from",
  "legalDeviations.validTo": "Valid until",
  "legalDeviations.reference": "Reference or file number",
  "legalDeviations.authority": "Issuing authority",
  "legalDeviations.enablingAgreement": "Collective agreement with opening clause",
  "legalDeviations.justification": "Justification of the emergency",
  "legalDeviations.reasons": "The technical or organisational reasons",
  "legalDeviations.confirm": "I confirm that the preconditions of the norm are met.",
  "legalDeviations.save": "Record deviation",
  "legalDeviations.saved": "Deviation recorded. It applies to all following evaluations.",
  "tenantSelection.title": "Choose employer",
  "tenantSelection.intro":
    "You are registered with several employers. Choose whom you work for now; the choice applies until you sign out.",
  "tenantSelection.switch": "Switch employer",
  "tenant.contact": "Contact of the data protection officer",
  "tenant.contactHint":
    "Shown to all employees in the information under Art. 13 GDPR, for example name, e-mail address and postal address. Leave empty to remove it.",
  "tenant.employer": "Employer: {employer}",
  "tenant.save": "Save",
  "tenant.saved": "Saved.",
  "signup.title": "Register your company",
  "signup.intro":
    "Try Zeitavo free of charge for 30 days, without a payment method. The offer is for businesses only.",
  "signup.companyName": "Company",
  "signup.legalForm": "Legal form",
  "signup.street": "Street and number",
  "signup.postalCode": "Postal code",
  "signup.city": "City",
  "signup.federalState": "Federal state of the first establishment",
  "signup.vatId": "VAT ID",
  "signup.contactFirstName": "First name of the contact person",
  "signup.contactLastName": "Last name of the contact person",
  "signup.contactEmail": "E-mail address of the contact person",
  "signup.confirmations": "Confirmations",
  "signup.businessUse": "I register the company as a business (§ 14 BGB), not as a consumer.",
  "signup.acceptTerms": "I accept the terms and conditions (version {version}).",
  "signup.acceptDpa": "I conclude the data processing agreement (version {version}).",
  "signup.noLegalAdvice":
    "The hints from Zeitavo help to comply with working time law but are no legal advice.",
  "signup.submit": "Register",
  "signup.checking": "Checking …",
  "signup.unavailable": "Registration is not possible at the moment. Please try again later.",
  "signup.checkEmail":
    "Almost done: we sent an e-mail to {email}. Confirm the registration with the link in it.",
  "signup.verifyTitle": "Confirm registration",
  "signup.tokenMissing": "The link is incomplete. Please open it directly from the e-mail.",
  "signup.verified":
    "Your registration is confirmed. You will receive a second e-mail to set your password; then you can sign in.",
  "specialRegimes.title": "Special industries and regimes",
  "specialRegimes.intro":
    "Zeitavo checks the rules of general working time law. The industries and groups below have rules of their own that Zeitavo checks partially or not at all. Select what concerns your business. Complying with working time law remains the employer's duty.",
  "specialRegimes.registerVersion": "Overview: version {version}, last reviewed on {date}.",
  "specialRegimes.pending": "{title}: not law in force; legislative status last checked on {date}.",
  "specialRegimes.status.Supported": "Fully checked",
  "specialRegimes.status.PartiallySupported": "Partially checked",
  "specialRegimes.status.OutOfScope": "Not checked",
  "specialRegimes.scope": "Applies to: {scope}",
  "specialRegimes.checked": "Checked: {rules}",
  "specialRegimes.unchecked": "Not checked:",
  "specialRegimes.sources": "Legal sources",
  "specialRegimes.sourceCheckedOn": "checked on {date}",
  "specialRegimes.sourceUnverified": "citation not yet checked against the official text",
  "specialRegimes.select": "Concerns our business",
  "specialRegimes.acknowledgePartially":
    "Acknowledged: Zeitavo does not check the rules listed above as not checked.",
  "specialRegimes.acknowledgeOutOfScope":
    "Acknowledged: Zeitavo does not check the rules of this special regime; hints of the general checks may not fit this group.",
  "specialRegimes.reconfirm":
    "The scope of the checks changed since your confirmation (confirmed: {status}). Please confirm the selection again.",
  "specialRegimes.missingAcknowledgment": "Please confirm the scope of the checks for: {names}.",
  "specialRegimes.save": "Save selection",
  "specialRegimes.saved": "The selection is saved.",
  "specialRegimes.setupHint":
    "Zeitavo checks the rules of general working time law. Some industries and groups have rules of their own that Zeitavo checks partially or not at all:",
  "specialRegimes.setupAction":
    "The tenant administration records under “Tenant” which of them concern your business.",
  "specialRegimes.openTenant": "Select special regimes",
  "times.projects": "Projects: {list}",
  "times.projectShare": "{project} {duration} h",
  "times.unattributed": "without project",
  "times.projectsLocked": "Project period locked",
  "times.attribute": "Assign projects",
  "times.attributeNamed": "Assign projects for {date}",
  "times.requests": "Requests for project attribution",
  "dayOverview.lockedSegment": "Locked",
  "dayOverview.request": "Request a change",
  "dayOverview.requestNamed": "Request a change for {range}",
  "pastDay.sending": "Sending the attribution …",
  "pastDay.rejected": "The attribution {from}–{to} to {project} was not accepted.",
  "pastDay.savedOffline":
    "Attribution saved on this device. It is sent as soon as a connection is available.",
  "pastDay.locked":
    "This day lies in a locked project period. You can change the time of locked projects only by a request to the project responsibility:",
  "pastDay.lock": "{project}: {from} to {to} ({reason})",
  "pastDay.lockAll": "All projects",
  "pastDay.noProjects":
    "No projects are available to you on this day. The working time stays without a project; projects are assigned by the project management.",
  "pastDay.requestSubject": "{date}, {from}–{to}",
  "attributionRequest.title": "Request a change",
  "attributionRequest.explanation":
    "The project period is locked, for example because it has already been invoiced. The project responsibility decides on your request; the attribution changes only after approval. Your working time stays unchanged in any case.",
  "attributionRequest.project": "Requested project",
  "attributionRequest.fixedProject": "Requested project: {project}",
  "attributionRequest.reason": "Reason",
  "attributionRequest.reasonHint":
    "Required, at most {max} characters, for example: time attributed to the wrong project by mistake.",
  "attributionRequest.reason.required": "Please enter a reason.",
  "attributionRequest.reason.tooLong": "The reason is too long.",
  "attributionRequest.offline":
    "Requests need an internet connection. Please submit the request once you are back online.",
  "attributionRequest.submit": "Submit request",
  "attributionRequest.sent":
    "Request submitted. The project responsibility decides on it; you can follow its status in “My times” on the day concerned.",
  "attributionRequests.title": "Change requests",
  "attributionRequests.none":
    "No open requests. Requests arise when employees want to change time in a locked project period; you then decide on them here.",
  "attributionRequests.offline":
    "Decisions need an internet connection. They are possible again as soon as you are online.",
  "attributionRequests.status.Pending": "Open",
  "attributionRequests.status.Approved": "Approved",
  "attributionRequests.status.Rejected": "Rejected",
  "attributionRequests.subject": "{date}, {time} → {project}",
  "attributionRequests.from": "from {from}",
  "attributionRequests.removal": "No project (remove attribution)",
  "attributionRequests.employee": "Person: {employee}",
  "attributionRequests.unknownEmployee": "Unknown person",
  "attributionRequests.reason": "Reason: {reason}",
  "attributionRequests.requestedAt": "Requested on {time}",
  "attributionRequests.decisionNote": "Decision note: {note}",
  "attributionRequests.note": "Note",
  "attributionRequests.noteHint": "Required for a rejection; the person sees the note.",
  "attributionRequests.noteRequired": "Please explain the rejection in the note.",
  "attributionRequests.approve": "Approve",
  "attributionRequests.reject": "Reject",
  "attributionRequests.confirm.approve":
    "Approve the request? The attribution is stored despite the lock and changes the project times of the locked period.",
  "attributionRequests.confirm.reject": "Reject the request? The attribution stays unchanged.",
  "attributionRequests.confirmAction.approve": "Yes, approve",
  "attributionRequests.confirmAction.reject": "Yes, reject",
  "attributionRequests.approved": "Request approved.",
  "attributionRequests.rejected": "Request rejected.",
  "rejectedAttributions.title": "Rejected project attributions",
  "rejectedAttributions.switch": "Project switch on {time} to {project}",
  "rejectedAttributions.range": "{date}, {from}–{to}: {project}",
  "rejectedAttributions.locked":
    "The project period is locked. You can request the change from the project responsibility.",
  "rejectedAttributions.reason":
    "The attribution was not accepted ({code}). Your working time is not affected.",
  "rejectedAttributions.dismiss": "Dismiss",
  // UX WP-2: shared feedback components, error codes and names (ux-review.md).
  "error.code": "Error code {code}",
  "error.withCode": "{message} (error code {code})",
  "error.retry": "Try again",
  "error.retrying": "Trying again …",
  "error.hint.offline":
    "Clocking also works without a connection; the entries are sent later. This data loads once the connection is back.",
  "error.hint.permission":
    "If you need this access, please contact HR or the tenant administration.",
  "error.hint.server":
    "This is not caused by your input. Please try again in a few minutes; if the error persists, give the error code to your administration.",
  "error.TT-1001":
    "This clock event does not fit the current state, for example clocking out without clocking in. Please reload the page and check your state.",
  "error.TT-1002": "You are already clocked in. A new working day starts only after clocking out.",
  "error.TT-1003": "The time lies in the future. Please check the date and time of your device.",
  "error.TT-1004": "Later changes to clock events are only possible with a correction request.",
  "error.TT-1005":
    "The time zone is unknown. Please enter an IANA time zone, for example Europe/Berlin.",
  "error.TT-1006": "A required value is missing. Please fill in all required fields.",
  "error.TT-1007": "The period is invalid: the end must not lie before the start.",
  "error.TT-1008": "The period overlaps with a period that is already recorded.",
  "error.TT-1009":
    "This change is not possible in the current status, for example because a decision was already made. Please reload the page.",
  "error.TT-1010":
    "The month is already closed. Changes are only possible with a correction request.",
  "error.TT-1012": "This entry already exists.",
  "error.TT-1013": "A value is invalid. Please check your entries.",
  "error.TT-1014": "The clock event is older than 7 days. Please add it with a correction request.",
  "error.TT-1015":
    "No decision is possible on this version, for example because it does not apply yet or was already accepted. Please reload the page.",
  "error.TT-1019": "The kind of break can only be given when a break starts.",
  "error.TT-1030": "This special regime is unknown. Please reload the page.",
  "error.TT-1031":
    "The scope of checks of a selected special regime has changed. Please confirm it again.",
  "error.TT-1040":
    "The time does not lie completely within the clocked working time. Correct the clock events first.",
  "error.TT-1045":
    "An employment cannot be in road transport (§ 21a ArbZG) and exempt from the ArbZG (§ 18 ArbZG) at the same time.",
  "error.TT-1050":
    "The leave entitlement lies below the statutory minimum leave (§ 3 BUrlG, for minors § 19 JArbSchG).",
  "error.TT-1051": "Leave days are given in whole or half days.",
  "error.TT-1052": "This booking is already cancelled or is a reversal itself.",
  "error.TT-1053":
    "There is already an opening balance or days taken in the previous system for this leave year and part. Cancel that booking with a reversal first.",
  "error.TT-1054": "There are no statutory leave rules in the system for this period.",
  "error.TT-1060":
    "There is no leave account for this year: the leave rules are not stored yet, the person's leave account starts later, or the year lies outside the leave years 2000 to 2100.",
  "error.TT-1100":
    "Payment in lieu and credits of a previous employer name the first day of their employment relationship, other bookings none.",
  "error.TT-1101": "Leave of a previous employer is credited only for the leave year of the entry.",
  "error.TT-1102": "No employment relationship of the person starts on this day.",
  "error.TT-1103": "The employment relationship has no end yet. End the employment first.",
  "error.TT-1104":
    "The booked days of previous employers would exceed a full year's leave here. Book at most a full year's leave, even if the certificate names more; more does not reduce the entitlement further (§ 6 Abs. 1 BUrlG).",
  "error.TT-1105": "There is no leave left to pay in lieu for this employment relationship.",
  "error.TT-1107": "The chosen employment relationship is not in the leave year of the booking.",
  "error.TT-1106":
    "Leave requests within the employment relationship are still open. Decide them before the payment in lieu.",
  "error.TT-1410":
    "An employment of a federal civil servant (AZV) cannot be marginal, temporary agency work or road transport (§ 21a ArbZG) and has no simplification of the recording under the MiLoG or the AEntG.",
  "error.TT-1411":
    "The deletion period for federal civil servants lies outside the limits of § 7 (7) AZV: at least 3 months, at most 6 months after the settlement period or 13 months after the month of collection; a settlement period has 1, 2, 3, 4, 6 or 12 months.",
  "error.TT-1460":
    "The flexi days for federal civil servants lie outside § 7 (5) AZV: 0 to 24 per year.",
  "error.TT-1461": "A cap under § 7 (4) AZV is booked only on the flexitime account.",
  "error.TT-3680":
    "The reference date lies outside the computed time: from 1 January 2000 up to one year after today.",
  "error.TT-3681":
    "A cap under § 7 (4) AZV needs an employment as a federal civil servant in the booking month.",
  "error.TT-3682": "A cap under § 7 (4) AZV is booked in the first month of the settlement period.",
  "error.TT-3683":
    "The records of this month are deleted under § 7 (7) AZV; a booking in this month would not count.",
  "error.TT-2001": "The values are invalid. Please check the parameters.",
  "error.TT-2002":
    "This parameter may not be changed; only the released parameters can be changed.",
  "error.TT-2003": "No rule profile is valid on this date.",
  "error.TT-2011":
    "This recording rule cannot be confirmed yet: no version applies to the period or its thresholds are missing.",
  "error.TT-2013":
    "The norm does not permit this deviation. Please check the legal basis, scope, period and parameters.",
  "error.TT-2014": "Overrides may only tighten values. To relax a value, record a legal deviation.",
  "error.TT-2015": "This norm does not exist or does not apply to the whole period.",
  "error.TT-2025": "Protection and special profiles apply automatically and cannot be assigned.",
  "error.TT-3003": "You are not responsible for this person.",
  "error.TT-3005": "Your sign-in has expired. Please sign in again.",
  "error.TT-3006": "This employee does not exist.",
  "error.TT-3007": "Your own requests are decided by someone else.",
  "error.TT-3008": "The correction request does not exist or is not visible to you.",
  "error.TT-3009": "This value is already in use, for example a personnel number or a name.",
  "error.TT-3010": "A referenced entry does not exist. Please reload the page.",
  "error.TT-3011": "The violation does not exist or is not visible to you.",
  "error.TT-3012": "Notifications are not set up on the server.",
  "error.TT-3013": "Too many requests. Please wait a moment and then try again.",
  "error.TT-3014": "The data sent is too large.",
  "error.TT-3016": "The e-mail address already belongs to an account. Please use another address.",
  "error.TT-3017":
    "The tenant needs at least one person with the tenant administration role on that day. Give the role to someone else first.",
  "error.TT-3018":
    "This employer is not available for your account. Please choose the employer again.",
  "error.TT-3021": "Your account was deactivated by your employer. Please contact HR.",
  "error.TT-3023":
    "The emergency access cannot be changed in its current state. Please reload the page.",
  "error.TT-3024": "An emergency access can only read data.",
  "error.TT-3030":
    "Your company's data is being moved. Please send the change again in a few minutes.",
  "error.TT-3035": "The project period is locked. Please contact the project responsibles.",
  "error.TT-3037": "This project cannot be chosen on that day.",
  "error.TT-1260": "This number or code already occurs in an earlier line of the file.",
  "error.TT-1261": "The file contains errors. Nothing was saved.",
  "error.TT-3320": "This activity is archived or not allowed for the project.",
  "error.TT-3321": "This customer is archived. Reactivate it first.",
  "error.TT-3322": "This activity is archived. Reactivate it first.",
  "error.TT-1280":
    "The project has as many running budgets as the tenant allows. End or remove a budget first.",
  "error.TT-1290": "An hourly rate is valid between 2000 and 2100 only.",
  "error.TT-1291":
    "The period overlaps another rate for the same scope. End the previous rate first.",
  "error.TT-1292":
    "The rate does not fit its scope: choose project and activity as the scope requires.",
  "error.TT-3350": "Hourly rates and amounts are not released to project responsibles. Ask HR.",
  "error.TT-3360":
    "Sickness and rehabilitation cannot be requested through the public API; the person reports them or HR records them in Zeitavo.",
  "error.TT-1281": "A budget is valid between 2000 and 2100 only.",
  "error.TT-1282": "The warning thresholds lie outside the limits of the tenant.",
  "error.TT-1283": "The budget is smaller than the limit of the tenant.",
  "error.TT-1284": "The validity is shorter than the limit of the tenant.",
  "error.TT-3340":
    "This activity is archived or not allowed for the project; a budget needs a choosable activity.",
  "error.TT-3323":
    "No project without a customer carries this customer reference any more. Please reload the proposals.",
  "error.TT-3038": "Project time per person is switched off or not available to you.",
  "error.TT-3040": "The data processing agreement that applies today has not been accepted yet.",
  "error.TT-3041": "The security check failed. Please reload the page and send the form again.",
  "error.TT-3042":
    "New versions of the contract documents apply meanwhile. Please reload the page and confirm them again.",
  "error.TT-3043": "The confirmation link is unknown or has expired. Please register again.",
  "error.TT-3044":
    "This company is already registered. Please contact your company's administration.",
  "error.TT-3046": "This step follows from the data and cannot be confirmed by hand.",
  "error.TT-3047": "This template does not exist. Please reload the page.",
  "error.TT-3048": "The e-mail address already belongs to the account of another employee.",
  "error.TT-3180":
    "This invitation cannot be connected to an account automatically. Please contact the support.",
  "error.TT-3049": "Some entries have errors; nothing was saved. Please check the errors per line.",
  "error.TT-3050": "Please store the billing details first.",
  "error.TT-3051": "This plan cannot be bought online.",
  "error.TT-3052":
    "There already is a subscription. Change the plan under “Change plan” and the payment method with “Manage payment method or cancel”.",
  "error.TT-3471": "There is no running subscription whose plan could be changed.",
  "error.TT-3472": "The subscription already has this plan.",
  "error.TT-3053": "No subscription has been taken out yet.",
  "error.TT-3470":
    "Your plan is billed as agreed; a subscription cannot be taken out online. Agree changes with the provider.",
  "error.TT-3055": "Invoices cannot be issued in this installation.",
  "error.TT-3058":
    "The export is still being built or failed. Please wait until it is ready, or request a new one.",
  "error.TT-3059": "The export has expired and was erased. Please request a new one.",
  "error.TT-3060":
    "The contract has ended. Only tenant administration, HR and billing can still sign in, to read and export data.",
  "error.TT-3064":
    "The DATEV export is not set up yet: consultant number, client number and at least one wage type are missing.",
  "error.TT-3065":
    "DATEV cannot take some personnel numbers (digits only, 1 to 99999, each only once). Change them in the master data; nothing was exported.",
  "error.TT-3066": "There is nothing to transfer to DATEV in the chosen month.",
  "error.TT-3070": "Leave rules can apply from 1 January of the current year at the earliest.",
  "error.TT-3071": "The person has no employment in the validity. Record the employment first.",
  "error.TT-3072": "This kind of booking comes from its own process and cannot be recorded here.",
  "error.TT-3073": "The file contains errors; nothing was booked.",
  "error.TT-3074":
    "This change worsens leave rules or leave accounts for past periods. Let changed rules apply from today at the earliest; the start of the leave accounts and takeovers cannot be placed past years with leave data.",
  "error.TT-3075":
    "The leave accounts can only start in the previous, the current or the next year, before the current year only while there is no leave data. Takeovers from the previous system are possible only after the leave rules are stored and only for years from the leave account start.",
  "error.TT-3076":
    "An opening balance applies only to the first year of the person's leave account, because it carries over the leave of all earlier years. For an earlier year, cancel the later opening balance with a reversal first.",
  "error.TT-5001":
    "The sign-in service refused or did not answer the request. Please try again later.",
  "error.TT-5002": "The process was interrupted. Please repeat it to complete it.",
  "error.TT-5003": "Registration is not possible at the moment. Please try again later.",
  "error.TT-5004":
    "This installation runs without an operator; the function is not available here.",
  "error.TT-5005": "A subscription cannot be taken out online in this installation.",
  "error.TT-5006": "The payment provider did not answer. Please try again later.",
  "error.TT-5007":
    "The VAT ID could not be checked right now and stays unconfirmed for now. Please try again later.",
  "error.TT-5008": "E-mails cannot be sent because no mail server is set up.",
  "error.TT-5010": "The database does not answer right now. Please try again in about 30 seconds.",
  "field.optional": "(optional)",
  "form.errorSummary.one": "1 problem – please check this entry:",
  "form.errorSummary.other": "{count} problems – please check these entries:",
  "form.problem": "{label}: {message}",
  "form.error.missing": "Please fill in this field.",
  "form.error.missingNumber": "Please enter a number.",
  "form.error.missingDate": "Please enter a date.",
  "form.error.missingMonth": "Please enter a month.",
  "form.error.missingTime": "Please enter a time.",
  "form.error.missingChoice": "Please choose an entry.",
  "form.error.missingCheckbox": "Please tick this box to continue.",
  "form.error.missingFile": "Please choose a file.",
  "form.error.number": "Please enter a valid number.",
  "form.error.incompleteDate": "Please enter a complete date.",
  "form.error.incompleteTime": "Please enter a complete time.",
  "form.error.email": "Please enter a valid e-mail address, for example name@company.com.",
  "form.error.url": "Please enter a complete web address, for example https://company.com.",
  "form.error.pattern": "Please enter the value in the format described at the field.",
  "form.error.digitsOnly": "Please enter digits only.",
  "form.error.digitsExactly.one": "Please enter exactly one digit.",
  "form.error.digitsExactly.other": "Please enter exactly {count} digits.",
  "form.error.digitsBetween": "Please enter {min} to {max} digits.",
  "form.error.lettersAndDigitsExactly.one": "Please enter exactly one letter or digit.",
  "form.error.lettersAndDigitsExactly.other": "Please enter exactly {count} letters or digits.",
  "form.error.lettersAndDigitsBetween":
    "Please enter {min} to {max} letters or digits, without spaces or special characters.",
  "form.error.tooShort.one": "Please enter at least one character.",
  "form.error.tooShort.other": "Please enter at least {count} characters.",
  "form.error.tooLong.one": "Please enter at most one character.",
  "form.error.tooLong.other": "Please enter at most {count} characters.",
  "form.error.min": "Please enter at least {min}.",
  "form.error.max": "Please enter at most {max}.",
  "form.error.earliest": "Please choose {min} or later.",
  "form.error.latest": "Please choose {max} or earlier.",
  "form.error.wholeNumber": "Please enter a whole number.",
  "form.error.decimals.one": "Please enter at most one decimal place.",
  "form.error.decimals.other": "Please enter at most {count} decimal places.",
  "form.error.step": "Please enter a value in steps of {step}.",
  "form.error.invalid": "Please check this entry.",
  "entitlement.Projects": "Projects, teams and cost centres",
  "entitlement.DatevExport": "DATEV payroll export",
  "entitlement.PublicApi": "Public API and webhooks",
  "entitlement.MultiEstablishment": "Several establishments",
  "entitlement.Sso": "Single sign-on and SCIM",
  "entitlement.Silo": "Own database and own key",
  "entitlement.NfcAndLocation": "NFC and location",
  "federalState.BW": "Baden-Württemberg",
  "federalState.BY": "Bavaria",
  "federalState.BE": "Berlin",
  "federalState.BB": "Brandenburg",
  "federalState.HB": "Bremen",
  "federalState.HH": "Hamburg",
  "federalState.HE": "Hesse",
  "federalState.MV": "Mecklenburg-Western Pomerania",
  "federalState.NI": "Lower Saxony",
  "federalState.NW": "North Rhine-Westphalia",
  "federalState.RP": "Rhineland-Palatinate",
  "federalState.SL": "Saarland",
  "federalState.SN": "Saxony",
  "federalState.ST": "Saxony-Anhalt",
  "federalState.SH": "Schleswig-Holstein",
  "federalState.TH": "Thuringia",
  // UX WP-3: employee screens (ux-review.md).
  "times.previous.day": "Previous day",
  "times.previous.week": "Previous week",
  "times.previous.month": "Previous month",
  "times.next.day": "Next day",
  "times.next.week": "Next week",
  "times.next.month": "Next month",
  "times.emptyClockIn": "Clock in now",
  "times.emptyCorrection": "Add missing times",
  "times.emptyRunningShift":
    "You are clocked in. Your running shift began on {date} and belongs to that working day.",
  "times.toRunningShift": "Show running shift",
  "corrections.noteHint": "Required for a rejection; the requesting person sees the note.",
  "corrections.errorSummary": "The request cannot be submitted yet:",
  "corrections.noneAction": "Submit a new request",
  "corrections.approved": "Request approved.",
  "corrections.rejected": "Request rejected.",
  "absences.item.one": "{kind}: {from} – {to} ({count} calendar day)",
  "absences.item.other": "{kind}: {from} – {to} ({count} calendar days)",
  "absences.itemHalfDay": "{kind}: {from} (half day)",
  "absences.cancelConsequence":
    "{kind} from {from} to {to} will be cancelled. This cannot be undone.",
  "absences.cancelConfirm": "Cancel absence",
  "absences.keep": "Keep",
  "absences.cancelled": "Absence cancelled.",
  "absences.approved": "Absence approved.",
  "absences.rejected": "Absence rejected.",
  "absences.overview.open": "Open absence requests",
  "absences.overview.upcoming": "Absent in the next four weeks",
  "absences.overview.noOpen": "No open requests.",
  "absences.overview.noUpcoming": "Nobody is absent in the next four weeks.",
  "absences.overview.hiddenKind": "Absence",
  "absences.overview.decidedByHr": "Only HR sees the reason; HR decides on the request.",
  "absences.overview.notYours": "Another authorised person decides on this request.",
  "absences.overview.unknownPerson": "Unknown person",
  "absences.noneAction": "Request an absence",
  "planning.calendar": "Team calendar",
  "planning.calendar.hint":
    "Requested and approved absences of the month. Some absences appear only as “absent”, without their kind.",
  "planning.calendar.previous": "Previous month",
  "planning.calendar.next": "Next month",
  "planning.calendar.team": "Team",
  "planning.calendar.allTeams": "All employees",
  "planning.calendar.person": "Person",
  "planning.calendar.empty": "Nobody to show in this month.",
  "planning.calendar.absent": "Absent",
  "planning.calendar.code.hidden": "A",
  "planning.calendar.shutdown": "Company shutdown",
  "planning.calendar.legend":
    "Legend: code of the kind (A = absent, kind not shown), dashed = requested, filled = approved, marked days = leave blackout.",
  "planning.calendar.blackouts": "Leave blackouts in the month",
  "planning.calendar.blackoutDay": "Leave blackout",
  "planning.blackout.item": "{from} – {to}: {reason} ({mode})",
  "planning.blackout.mode.Block": "blocks requests",
  "planning.blackout.mode.Warn": "notice only",
  "planning.blackout.warning":
    "The period lies in a leave blackout: {list}. You can send the request; the deciding person sees the notice.",
  "planning.blackout.blocked":
    "The period lies in a leave blackout that blocks requests: {list}. Please choose other days or contact HR.",
  "planning.substitute": "Substitute",
  "planning.substitute.none": "No substitute",
  "planning.substitute.hint": "Who stands in for you during the absence.",
  "planning.substituteOf": "Substitute: {name}",
  "planning.substituteNamed": "Substitute named",
  "planning.rejectionReason": "Reason of the rejection: {reason}",
  "planning.cancellationReason": "Reason of the cancellation: {reason}",
  "planning.cancel.reason": "Reason of the cancellation",
  "planning.cancel.reasonHint": "Required; the person sees the reason and is notified.",
  "planning.cancel.consequence":
    "{kind} from {from} to {to} is cancelled; the person receives your reason.",
  "planning.shutdownLabel": "Company shutdown",
  "planning.cancellationRequested": "Cancellation requested",
  "planning.requestCancellation": "Request cancellation",
  "planning.requestCancellation.consequence":
    "{kind} from {from} to {to} stays approved until the cancellation is decided.",
  "planning.requestCancellation.confirm": "Request cancellation",
  "planning.cancellationRequestedAnnounce": "Cancellation requested.",
  "planning.decide.cancellation": "Cancellation requested: approving cancels the absence.",
  "planning.decide.cancellationAfterStart":
    "Cancellation requested, but the absence has already begun: it can only be rejected. HR records an illness during leave.",
  "planning.reject.reason": "Reason of the rejection",
  "planning.reject.reasonHint": "Required; the person sees the reason.",
  "planning.reject.consequence": "The person receives the rejection with your reason.",
  "planning.reject.confirm": "Reject",
  "planning.reject.missing": "Please give a reason.",
  "planning.hint.blackout.Block": "Lies in a leave blackout that blocks requests.",
  "planning.hint.blackout.Warn": "Lies in a leave blackout (notice).",
  "planning.hint.overlap.one": "{count} other member of the team is absent in the period.",
  "planning.hint.overlap.other": "{count} other members of the team are absent in the period.",
  "planning.hint.shortfall": "On {date} the team would fall below its minimum staffing.",
  "planning.hint.noBlock":
    "No continuous vacation of twelve working days (Werktage) is approved in the year yet (§ 7 (2) BUrlG).",
  "error.TT-1110": "Vacation of a company shutdown is changed by HR only.",
  "error.TT-1111":
    "Leave that has begun can no longer be cancelled by a request. If you fell ill during your leave, please contact HR with the medical certificate.",
  "error.TT-3132":
    "A company shutdown begins today at the earliest; past days are not booked afterwards.",
  "error.TT-3150":
    "Vacation can only be requested from today on. Vacation for past days is recorded by HR.",
  "error.TT-3130":
    "The vacation lies in a leave blackout that blocks requests. Please choose other days or contact HR.",
  "error.TT-3131":
    "Only members of your team, colleagues with the same manager or your manager can be your substitute.",
  "admin.leavePlanning": "Leave planning",
  "admin.leavePlanning.open": "Leave planning: blackouts, company shutdowns, minimum staffing",
  "admin.leavePlanning.back": "Back to the absence requests",
  "admin.leavePlanning.codetermination":
    "Leave principles, leave blackouts and company shutdowns are subject to co-determination by the works council (§ 87 (1) no. 5 BetrVG). Set them only as a works agreement provides, or where there is no works council. The configuration annex of the works agreement lists them.",
  "admin.leavePlanning.blackouts": "Leave blackouts",
  "admin.leavePlanning.blackouts.none": "No leave blackouts.",
  "admin.leavePlanning.blackout.new": "Add a leave blackout",
  "admin.leavePlanning.scope": "Applies to",
  "admin.leavePlanning.scope.Tenant": "All employees",
  "admin.leavePlanning.scope.Establishment": "Establishment",
  "admin.leavePlanning.scope.Team": "Team",
  "admin.leavePlanning.scopeTarget": "Establishment or team",
  "admin.leavePlanning.reason": "Reason",
  "admin.leavePlanning.reasonHint": "Shown to the employees concerned.",
  "admin.leavePlanning.mode": "Effect",
  "admin.leavePlanning.mode.Warn": "Notice (requests possible)",
  "admin.leavePlanning.mode.Block": "Block (own requests refused)",
  "admin.leavePlanning.save": "Save leave blackout",
  "admin.leavePlanning.remove": "Remove",
  "admin.leavePlanning.remove.consequence": "The leave blackout from {from} to {to} is removed.",
  "admin.leavePlanning.removed": "Leave blackout removed.",
  "admin.leavePlanning.saved": "Saved.",
  "admin.leavePlanning.required": "Required: please fill in.",
  "admin.leavePlanning.shutdowns": "Company shutdowns",
  "admin.leavePlanning.shutdowns.none": "No company shutdowns booked.",
  "admin.leavePlanning.shutdown.new": "Book a company shutdown",
  "admin.leavePlanning.shutdown.hint":
    "Books approved vacation from today for everybody concerned on the free days of the period and notifies them. Days with other absences and parental leave stay unchanged. Persons whose leave account does not cover the days or who are under special protection in the period are not booked and listed below; you decide on them one by one.",
  "admin.leavePlanning.shutdown.book": "Book company shutdown",
  "admin.leavePlanning.shutdown.booked": "Booked: {absences} absences for {people} persons.",
  "admin.leavePlanning.shutdown.notBooked.NotCovered":
    "Not booked because the leave account does not cover the days – please record them as paid release or decide one by one, otherwise negative hours arise (§ 615 BGB): {names}.",
  "admin.leavePlanning.shutdown.notBooked.CoverageUnknown":
    "Not booked because the leave account could not be checked: {names}.",
  "admin.leavePlanning.shutdown.notBooked.ProtectedPeriod":
    "Not booked because of maternity protection in the period – please decide one by one: {names}.",
  "admin.leavePlanning.shutdown.notBooked.IndividualDecision":
    "Not booked – please decide one by one: {names}.",
  "admin.leavePlanning.shutdown.item": "{from} – {to}, {scope}: {reason}",
  "admin.leavePlanning.shutdown.counts": "{people} persons, {absences} absences",
  "admin.leavePlanning.shutdown.withdrawn": "Withdrawn: {reason}",
  "admin.leavePlanning.shutdown.withdraw": "Withdraw",
  "admin.leavePlanning.shutdown.withdraw.consequence":
    "Vacation of this company shutdown that has not begun is cancelled and the persons are notified. Vacation that has begun stays and is listed for you.",
  "admin.leavePlanning.shutdown.withdrawResult.one": "{count} absence cancelled.",
  "admin.leavePlanning.shutdown.withdrawResult.other": "{count} absences cancelled.",
  "admin.leavePlanning.shutdown.kept":
    "Already begun, stays – please settle it with the persons: {list}.",
  "admin.leavePlanning.shutdown.keptItem": "{name} ({from} – {to})",
  "admin.leavePlanning.shutdown.keptCounts":
    "Kept because already begun: {people} persons, {absences} absences – please settle them with the persons.",
  "admin.leavePlanning.shutdown.withdraw.reason": "Reason of the withdrawal",
  "admin.leavePlanning.shutdown.withdrawnAnnounce": "Company shutdown withdrawn.",
  "admin.leavePlanning.staffing": "Minimum staffing per team",
  "admin.leavePlanning.staffing.hint":
    "How many members should be present at least. Deciding on vacation shows a warning when the staffing would fall below it; nothing is refused. Leave empty for no minimum.",
  "admin.leavePlanning.staffing.none": "No teams yet.",
  "admin.leavePlanning.staffing.minimum": "Present at least: {team}",
  "admin.leavePlanning.staffing.save": "Save minimum staffing",
  "admin.leavePlanning.staffing.invalid":
    "Please enter a whole number from 1 to 1000 or leave the field empty.",
  "absences.code.Vacation": "V",
  "absences.code.Sick": "S",
  "absences.code.Holiday": "PH",
  "absences.code.Special": "SL",
  "absences.code.Training": "T",
  "absences.code.CompensatoryRest": "CR",
  "absences.calendarEntry": "{kind} ({status})",
  "absences.chargedDays.one": "{count} leave day charged",
  "absences.chargedDays.other": "{count} leave days charged",
  "absences.holidayEntry": "Public holiday: {name}",
  "absences.legend": "Legend",
  "absences.legend.requested": "Requested (dashed border)",
  "absences.legend.approved": "Approved",
  "absences.legend.weekend": "Weekend (shaded grey)",
  "absences.holidays": "Public holidays in the month",
  "absences.holidayItem": "{date}: {name}",
  // UX WP-4: manager and HR screens (ux-review.md).
  "team.approvalsUnavailable": "Open correction requests: not available",
  "violations.day": "Day: {date}",
  "violations.filterSummary": "Filter: {from} – {to} · status: {status} · person: {employee}",
  "violations.filterSummary.establishment":
    "Filter: {from} – {to} · status: {status} · person: {employee} · establishment: {establishment}",
  "violations.employee.unknown": "unknown person",
  "violations.commentHint": "For example what you discussed or arranged.",
  "violations.acknowledgeSave": "Save acknowledgement",
  "violations.acknowledgedNow": "Acknowledgement saved.",
  "violations.noneHint": "If needed, change the period, status or person in the filter.",
  "violations.acknowledgeFurther": "Add acknowledgement",
  "violations.acknowledgedByYou": "You have acknowledged this.",
  "violations.group.count.one": "{count} finding",
  "violations.group.count.other": "{count} findings",
  "violations.group.state.Open.one": "{count} open",
  "violations.group.state.Open.other": "{count} open",
  "violations.group.state.Acknowledged.one": "{count} acknowledged",
  "violations.group.state.Acknowledged.other": "{count} acknowledged",
  "violations.group.state.Resolved.one": "{count} resolved",
  "violations.group.state.Resolved.other": "{count} resolved",
  "violations.group.acknowledge.one": "Acknowledge finding",
  "violations.group.acknowledge.other": "Acknowledge all {count} findings",
  "violations.group.acknowledgeFirst": "Acknowledge the first {count} of {total} findings",
  "violations.group.batchHint":
    "At most {count} findings are acknowledged at once; afterwards the button offers the next ones.",
  "violations.group.acknowledgedNow.one": "Acknowledgement saved for {count} finding.",
  "violations.group.acknowledgedNow.other": "Acknowledgement saved for {count} findings.",
  "violations.group.details.one": "Show the finding",
  "violations.group.details.other": "Show single findings ({count})",
  "projects.savedNamed": "Project {code} saved.",
  "projects.archiveConsequence":
    "{code} will be archived: it can no longer be chosen, all previous attributions are kept. You can reactivate it later.",
  "projects.archiveConfirm": "Archive project",
  "projects.archivedNamed": "Project {code} archived.",
  "projects.reactivatedNamed": "Project {code} reactivated.",
  "projects.noneHint": "Create the first project with “New project”.",
  "projects.policies.deadline.one": "Deadline {count} working day",
  "projects.policies.deadline.other": "Deadline {count} working days",
  "projects.policies.removeNamed": "Remove policy {scope}",
  "projects.policies.removeConsequence":
    "The policy for {scope} will be removed. The policy of the next more general level applies there afterwards, or no requirement.",
  "projects.policies.removeConfirm": "Remove policy",
  "projects.policies.removed": "Policy removed.",
  "projects.policies.saved": "Policy saved.",
  "projects.policies.scopeMissing": "Choose the scope ({scope}) first.",
  "admin.externalWork.declaration.item.one": "{from} – {to}: {work} h on {count} day",
  "admin.externalWork.declaration.item.other": "{from} – {to}: {work} h on {count} days",
  "admin.recordingObligation.deadline.one": "Deadline: {count} calendar day after the day of work",
  "admin.recordingObligation.deadline.other":
    "Deadline: {count} calendar days after the day of work",
  "setup.invitation.failedReason": "The reason is not known.",
  "setup.invitation.failedNextStep":
    "Check the e-mail address in the master data and send the invitation again with “Send again”. If the error persists, give the error code to the administration.",
  "setup.errorUnknown": "This value is invalid.",
  "admin.documentTitle": "{area} – {page} – {app}",
  "admin.savedNamed": "{name} saved.",
  "admin.editNamed": "Edit {name}",
  "admin.employee.none":
    "No employees yet. Create the first person with “Create” or import several under “Setup”.",
  "admin.employee.noMatch": "No matches for “{term}”.",
  "admin.establishment.none":
    "No establishments yet. Create the first one with “Create”; it sets the time zone and the public holidays.",
  "admin.establishment.item": "{name} · {state} · {timeZone}",
  "admin.establishment.timeZoneHint": "IANA name, for example Europe/Berlin.",
  "admin.model.none":
    "No work time models yet. Create one with “Create” or take templates under “Setup”.",
  "admin.team.none": "No teams yet. Create the first one with “New team”.",
  "admin.employment.endConsequence":
    "The employment ends at the end of {date}. It no longer applies to later days.",
  "admin.employment.endConfirm": "End employment",
  "admin.employment.ended": "Employment ended.",
  "admin.consent.revokeNamed": "Record revocation: {document}",
  "admin.consent.revokeConsequence":
    "The revocation of the consent of {granted} ({document}) is recorded with today's date; from today the statutory limits apply again. This cannot be undone.",
  "admin.consent.revokeConfirm": "Record revocation for good",
  "admin.consent.revokedNow": "Revocation recorded.",
  "admin.consent.added": "Consent recorded.",
  "admin.protection.added": "Maternity protection period recorded.",
  "admin.externalWork.period.endConsequence":
    "The period of multiple employment ends at the end of {date}.",
  "admin.externalWork.period.endConfirm": "End period for good",
  "admin.externalWork.period.ended": "Period ended.",
  "admin.externalWork.period.added": "Period recorded.",
  "admin.externalWork.declaration.withdrawConsequence":
    "The declaration {from} – {to} is withdrawn and no longer taken into account. This cannot be undone; record a new declaration if needed.",
  "admin.externalWork.declaration.withdrawConfirm": "Withdraw declaration for good",
  "admin.externalWork.declaration.withdrawnNow": "Declaration withdrawn.",
  "admin.externalWork.declaration.added": "Declaration recorded.",
  "admin.preparation.withdrawConsequence":
    "The entry of {date} is withdrawn and no longer taken into account. This cannot be undone; record a new entry if needed.",
  "admin.preparation.withdrawConfirm": "Withdraw entry for good",
  "admin.preparation.withdrawnNow": "Entry withdrawn.",
  "admin.preparation.added": "Time categorized.",
  "admin.employee.sections": "Areas of {name}",
  "admin.employee.section.master": "Master data",
  "admin.employee.section.employment": "Employment",
  "admin.employee.section.protection": "Protection",
  "admin.employee.section.multiple": "Multiple employment",
  "admin.employee.section.onCall": "On-call",
  "admin.employee.section.absences": "Absences",
  "admin.employee.section.preparation": "Preparation and follow-up",
  "admin.employee.section.leave": "Leave",
  "admin.employee.section.timeAccount": "Time account",
  "admin.leave.entitlements": "Annual entitlement",
  "admin.leave.entitlements.hint":
    "The contractual leave entitlement with validity and source. It must not lie below the statutory minimum leave in any period (§ 3 BUrlG, for minors § 19 JArbSchG); with a different number of working days per week it is converted.",
  "admin.leave.accountStart": "Leave account from {year}.",
  "admin.leave.accountStart.none":
    "No leave account yet: it starts once the leave rules of the tenant are stored and the person has an employment.",
  "admin.leave.entitlements.none":
    "No own entitlement recorded. The tenant default applies: {days} days for {week} working days per week.",
  "admin.leave.default.belowMinimum":
    "From {date}, no own entitlement applies to this person and the tenant default lies below the statutory minimum ({rule}: at least {days} days for {week} working days per week). Record an own entitlement.",
  "admin.leave.entitlement.item": "{days} days for {week} working days per week, {from} – {to}",
  "admin.leave.entitlement.source.Contract": "Employment contract",
  "admin.leave.entitlement.source.CollectiveAgreement": "Collective agreement",
  "admin.leave.entitlement.source.WorksAgreement": "Works agreement",
  "admin.leave.entitlement.reference": "Reference or reason",
  "admin.leave.entitlement.referenceHint":
    "For example “Employment contract of 1 January 2026, section 7”.",
  "admin.leave.entitlement.withdrawn": "Withdrawn on {date}: {reason}",
  "admin.leave.minimum.caption": "Statutory minimum per period",
  "admin.leave.minimum.period": "Period",
  "admin.leave.minimum.week": "Working days per week",
  "admin.leave.minimum.noFixedDays":
    "{days} (reference week of the entitlement, the work time model has no fixed working days)",
  "admin.leave.minimum.rule": "Rule",
  "admin.leave.minimum.minimum": "Minimum (days)",
  "admin.leave.minimum.entitled": "Entitlement (days)",
  "admin.leave.minimum.met": "met",
  "admin.leave.minimum.notMet": "not met",
  "admin.leave.entitlement.new": "New entitlement",
  "admin.leave.entitlement.newHint":
    "A new entitlement ends the previous open-ended entitlement on the day before.",
  "admin.leave.entitlement.validTo": "Valid to",
  "admin.leave.annualDays": "Days per calendar year",
  "admin.leave.annualDaysHint": "In whole or half days, for example 30 or 27.5.",
  "admin.leave.referenceWeek": "Based on working days per week",
  "admin.leave.source": "Source",
  "admin.leave.save": "Record entitlement",
  "admin.leave.saved": "The entitlement is recorded.",
  "admin.leave.withdraw": "Withdraw entitlement",
  "admin.leave.withdrawConfirm": "Withdraw entitlement for good",
  "admin.leave.withdrawConsequence":
    "The entitlement no longer applies but stays in the history with your reason. If it ended an earlier entitlement, that one applies again.",
  "admin.leave.withdrawn": "The entitlement is withdrawn.",
  "admin.leave.reason": "Reason",
  "admin.leave.daysInvalid": "Please enter a number of whole or half days.",
  "admin.leave.bookings": "Takeover from the previous system and corrections",
  "admin.leave.bookings.hint":
    "The opening balance is the leave carried over from earlier years as on 1 January; “Taken in the previous system” are the days of the leave year taken before the go-live. Bookings are never changed or deleted; cancel a wrong booking with a reversal.",
  "admin.leave.bookings.year": "Leave year",
  "admin.leave.bookings.yearInvalid": "Please enter a year from {min} to {max}.",
  "admin.leave.bookings.none": "No bookings in the leave year {year}.",
  "admin.leave.booking.kind.OpeningBalance": "Opening balance",
  "admin.leave.booking.kind.Correction": "Correction",
  "admin.leave.booking.kind.PriorEmployerCredit": "Credit of the previous employer",
  "admin.leave.booking.kind.ParentalLeaveReduction": "Reduction for parental leave",
  "admin.leave.booking.kind.SickDuringLeaveRestore": "Credit for sickness during leave",
  "admin.leave.booking.kind.Forfeiture": "Forfeiture",
  "admin.leave.booking.kind.Payout": "Payment in lieu",
  "admin.leave.booking.kind.TakenInPreviousSystem": "Taken in the previous system",
  "admin.leave.part.Statutory": "statutory leave",
  "admin.leave.part.Extra": "additional leave",
  "admin.leave.booking.item": "{kind}: {days} days, {part} – {reason}",
  "admin.leave.booking.reversed": "Cancelled by a reversal.",
  "admin.leave.booking.reversal": "Reversal",
  "admin.leave.booking.reverse": "Record reversal",
  "admin.leave.booking.reverseConfirm": "Cancel booking",
  "admin.leave.booking.reverseConsequence":
    "A booking of {days} days is recorded; the original booking stays visible.",
  "admin.leave.booking.reversedDone": "The reversal is recorded.",
  "admin.leave.booking.new": "New booking",
  "admin.leave.booking.relationship": "Employment relationship",
  "admin.leave.booking.relationshipHint":
    "In a year with a re-hire: the employment relationship the booking belongs to. Without a choice the booking date decides.",
  "admin.leave.booking.relationship.byDate": "By booking date",
  "admin.leave.booking.kind": "Kind",
  "admin.leave.booking.days": "Days",
  "admin.leave.booking.daysHint": "Positive for a credit, negative for a deduction.",
  "admin.leave.booking.daysHintTaken":
    "The days taken as a positive number; they are deducted from the leave account.",
  "admin.leave.booking.part": "Part",
  "admin.leave.booking.save": "Record booking",
  "admin.leave.booking.saved": "The booking is recorded.",
  "admin.leave.booking.defaultReason": "Remaining leave from the previous system",
  "admin.leave.booking.defaultReasonTaken": "Leave taken in the previous system",
  "admin.leave.exit.title": "Entry and exit",
  "admin.leave.exit.hint":
    "Per employment relationship: leave a previous employer granted or paid in lieu for the year of entry is credited as far as both together would exceed a full year's leave (§ 6 Abs. 1 BUrlG). At the exit, Zeitavo computes partial leave (§ 5 BUrlG) and the payment in lieu (§ 7 Abs. 4 BUrlG) and creates the leave certificate (§ 6 Abs. 2 BUrlG).",
  "admin.leave.exit.none": "No employment is recorded for this person.",
  "admin.leave.exit.since": "Employment relationship since {from}",
  "admin.leave.exit.period": "Employment relationship from {from} to {to}",
  "admin.leave.exit.openEntitlement":
    "A leave entitlement from before this re-hire still applies. End it under “Annual entitlement” if it no longer fits the new contract.",
  "admin.leave.exit.noEntryAccount":
    "There is no leave account for the year of entry {year}; leave of a previous employer cannot be credited.",
  "admin.leave.exit.credit": "Credit leave of the previous employer for {year}",
  "admin.leave.exit.credited.total":
    "Booked according to certificates: {days} days, of which credited: {effective} days.",
  "admin.leave.exit.credited.rule":
    "Leave is credited only as far as the leave of the previous employer and the entitlement here ({entitled} days) together exceed a full year's leave of {fullYear} days (§ 6 Abs. 1 BUrlG).",
  "admin.leave.exit.creditDays": "Days granted or paid in lieu according to the certificate",
  "admin.leave.exit.creditDaysHint":
    "Only leave for the year of entry {year}, without leave carried over from earlier years; at most a full year's leave here, even if the certificate names more. In half days, for example 8 or 7.5.",
  "admin.leave.exit.creditTooMany":
    "Together with the days already booked this exceeds a full year's leave here of {fullYear} days. Book at most that many days; more does not reduce the entitlement further.",
  "admin.leave.exit.creditSource": "Source (certificate, employer, date)",
  "admin.leave.exit.creditSave": "Credit",
  "admin.leave.exit.credited": "The leave of the previous employer is credited.",
  "admin.leave.exit.exit": "Exit on {date}",
  "admin.leave.exit.caption": "Leave in the year of exit {year}",
  "admin.leave.exit.year": "Leave year",
  "admin.leave.exit.waitingPeriod": "Waiting period (§ 4 BUrlG)",
  "admin.leave.exit.waitingPeriod.before": "fulfilled before the leave year",
  "admin.leave.exit.waitingPeriod.fulfilled": "fulfilled on {date}",
  "admin.leave.exit.waitingPeriod.notFulfilled": "not fulfilled (only on {date})",
  "admin.leave.exit.entitled": "Entitlement",
  "admin.leave.exit.partial": "{days} days (partial leave {months}/12, § 5 BUrlG)",
  "admin.leave.exit.carriedOver": "Carried over from earlier years",
  "admin.leave.exit.netEntitled": "Entitlement after credits, as certified",
  "admin.leave.exit.notDeducted": "Excess of an earlier employment relationship, not deducted",
  "admin.leave.exit.withCarryOver": "{days} (of which {carried} from earlier years)",
  "admin.leave.exit.booked": "Bookings (corrections, credits)",
  "admin.leave.exit.granted":
    "Granted (taken and planned until the exit, without certified sickness during leave)",
  "admin.leave.exit.forfeited": "Forfeited",
  "admin.leave.exit.paidOut": "Paid in lieu",
  "admin.leave.exit.openPayout": "Still to be paid in lieu",
  "admin.leave.exit.openRequests":
    "Open leave requests of {days} days: decide them before you book the payment in lieu.",
  "admin.leave.exit.excess":
    "{days} days were granted beyond the entitlement. They are not reclaimed; they reduce later leave in the same year only as far as both together would exceed a full year's leave (§ 5 Abs. 3, § 6 Abs. 1 BUrlG).",
  "admin.leave.exit.overpaid":
    "{days} days more were paid in lieu than are due, for example because the last day was moved forward. Cancel the payment in lieu with a reversal under “Takeover from the previous system and corrections” and book it again.",
  "admin.leave.exit.noExitAccount":
    "Payment in lieu and leave certificate for the year of exit {year} are not possible here. {reason}",
  "admin.leave.exit.payOut": "Book payment in lieu of {days} days",
  "admin.leave.exit.payOutConsequence":
    "{days} days are booked as payment in lieu as of {date} and passed to the DATEV payroll export of that month. Cancel a wrong booking with a reversal.",
  "admin.leave.exit.payOutConfirm": "Book payment in lieu",
  "admin.leave.exit.paidOutDone": "The payment in lieu is booked.",
  "admin.leave.exit.certificate": "Leave certificate (PDF)",
  "tenant.leave.title": "Leave rules",
  "tenant.leave.hint":
    "Apply to all employees without an own entitlement. Changes apply from the chosen day; leave principles are subject to co-determination (§ 87 Abs. 1 Nr. 5 BetrVG).",
  "tenant.leave.default": "No own rules saved yet; the defaults apply.",
  "tenant.leave.since": "The current rules apply since {date}.",
  "tenant.leave.statutory":
    "Statutory minimum: {days} working days for {week} working days per week ({rule}), that is {fiveDays} days for five working days.",
  "tenant.leave.validFrom": "Valid from",
  "tenant.leave.validFromHint":
    "1 January of the current year at the earliest; a worsening from today at the earliest.",
  "tenant.leave.defaultDays": "Default entitlement (days per calendar year)",
  "tenant.leave.referenceWeek": "Based on working days per week",
  "tenant.leave.accountStart": "Leave accounts from (leave year)",
  "tenant.leave.accountStartHint":
    "From this year Zeitavo keeps leave accounts, for later entries from the year of entry; earlier years only with a takeover from the previous system. With the first leave rules the previous, the current and the next year are possible. A change is possible only within these years and, once leave data exists, not past years with leave data and not before the current year.",
  "tenant.leave.accountStartInvalid": "Please enter a year from 2000 to 2100.",
  "tenant.leave.carryOver": "Carried-over leave must be taken by (DD.MM. of the following year)",
  "tenant.leave.carryOverHint": "By law until 31.03.; a later day is possible.",
  "tenant.leave.carryOverInvalid": "Please enter a day as DD.MM., for example 31.03.",
  "tenant.leave.halfDays": "Allow half days of leave",
  "tenant.leave.extraDays": "Treat additional leave like statutory leave",
  "tenant.leave.extraDaysReference": "Basis of the deviation",
  "tenant.leave.extraDaysReferenceHint":
    "Employment contract, collective agreement or works agreement with the provision under which additional leave is treated differently from statutory leave.",
  "tenant.leave.extraDaysReferenceMissing": "Please name the basis of the deviation.",
  "tenant.leave.aboveBalance": "Requests above the leave account",
  "tenant.leave.aboveBalance.Block": "refuse",
  "tenant.leave.aboveBalance.HrApproval": "only with approval of HR",
  "tenant.leave.daysOff": "Company days off",
  "tenant.leave.daysOffHint":
    "Days without work for everyone that are not public holidays, for example 24 and 31 December as half days. They do not count as leave days, half days off not even when half days of leave are not allowed.",
  "tenant.leave.daysOff.none": "No company days off.",
  "tenant.leave.dayOff.item": "{date}, {length}",
  "tenant.leave.dayOff.half": "half day",
  "tenant.leave.dayOff.full": "full day",
  "tenant.leave.dayOff.everyYear": "Every year",
  "tenant.leave.dayOff.halfDay": "Half day",
  "tenant.leave.dayOff.date": "Day off",
  "tenant.leave.dayOff.add": "Add day",
  "tenant.leave.dayOff.remove": "Remove {date}",
  "tenant.leave.dayOff.duplicate": "This day is already listed.",
  "tenant.leave.save": "Save leave rules",
  "tenant.leave.saved": "The leave rules are saved.",
  "setup.leave.title": "Take over leave accounts from the previous system",
  "setup.leave.hint":
    "One line per person and leave year with personnel number and leave year as well as remaining leave (leave carried over from earlier years as on 1 January) and/or taken (days of the leave year taken in the previous system before the go-live), optionally part (statutory or additional) and reason. Store the leave rules first: only years from the leave account start, from the previous to the next year, are taken over. Remaining leave exists per person only for the first year of the leave account, taken days also for later years. Record the annual entitlement not here but as an entitlement or default. Check the file first; only a file without errors is imported, then all lines.",
  "setup.leave.imported": "Lines taken over: {count}.",
  "setup.leave.checkOk": "No errors. Lines that can be taken over: {count}.",
  "admin.employee.section.absencesOpen": "Absences ({count} open)",
  "admin.employee.minorUntilShort": "Minor until",
  "admin.employee.notMinor": "not a minor",
  "admin.employee.noEmployment":
    "Record an employment under “Employment” first; consents and maternity protection belong to it.",
  // UX WP-5: tenant administration (ux-review.md).
  "tenant.employerTitle": "Employer and data protection",
  "tenant.sections": "Areas of tenant administration",
  "tenant.section.general": "General",
  "tenant.section.regimes": "Special regimes",
  "tenant.section.rules": "Deviations",
  "tenant.section.access": "Roles and access",
  "tenant.section.support": "Support access",
  "tenant.section.contract": "Contract and export",
  "tenant.section.open.one": "{count} open",
  "tenant.section.open.other": "{count} open",
  "contacts.saved": "Contacts saved.",
  "legal.short.Terms": "Terms",
  "legal.short.Dpa": "DPA",
  "legal.short.Sla": "SLA",
  "legal.short.PrivacyNotice": "Privacy notice",
  "legal.promptLine.one": "New contract version to review: {documents}.",
  "legal.promptLine.other": "New contract versions to review: {documents}.",
  "legal.review": "Review now",
  "legal.acceptShort": "Accept",
  "legal.declineShort": "Decline",
  "legal.acceptConsequence":
    "Once accepted, version {version} of “{document}” is binding for your company from {day}.",
  "legal.acceptConfirm": "Accept as binding",
  "legal.declineConsequence":
    "You decline version {version}. Version {inForce} continues to apply until the end of your contract.",
  "legal.declineConfirm": "Decline version",
  "legal.acceptedAnnounce": "{document}: version {version} accepted.",
  "legal.declinedAnnounce": "{document}: version {version} declined.",
  "emergency.approveConsequence.one":
    "{name} may then read the data of your company for {count} hour; every access is recorded in the audit log.",
  "emergency.approveConsequence.other":
    "{name} may then read the data of your company for {count} hours; every access is recorded in the audit log.",
  "emergency.approveConfirm": "Grant access",
  "emergency.rejectConsequence":
    "{name} gets no access. If support needs access later, it has to ask again.",
  "emergency.rejectConfirm": "Reject request",
  "emergency.revokeConsequence":
    "The access of {name} ends at once. If support needs access again later, it has to ask again.",
  "emergency.revokeConfirm": "End access now",
  "emergency.approved": "Access granted.",
  "emergency.rejected": "Request rejected.",
  "emergency.revoked": "Access ended.",
  "scim.revokeConsequence":
    "The identity system using the access “{name}” can then no longer create, change or lock employees. The revocation cannot be undone.",
  "scim.revokeConfirm": "Revoke for good",
  "scim.revoked": "Access {name} revoked.",
  "publicApi.revokeConsequence":
    "{name} can then no longer read data. The revocation cannot be undone.",
  "publicApi.revokeConfirm": "Revoke for good",
  "publicApi.revoked": "API access {name} revoked.",
  "publicApi.disableConsequence":
    "The receiver then gets no more notifications. A disabled webhook cannot be enabled again; create a new one if needed.",
  "publicApi.disableConfirm": "Disable for good",
  "publicApi.disabled": "Webhook {url} disabled.",
  "settings.logoRemoveConsequence":
    "The logo disappears from the header for all employees. You can upload a new one at any time.",
  "settings.logoSaved": "Logo saved.",
  "settings.logoRemoved": "Logo removed.",
  "overrides.invalidParameters": "Please check the parameters of override {number}.",
  "legalDeviations.invalidParameters": "Please check the parameters.",
  "legalDeviations.maxDuration.one": "At most {count} day per entry.",
  "legalDeviations.maxDuration.other": "At most {count} days per entry.",
  "legalDeviations.justificationHint.one":
    "At least 20 characters, no health data. An emergency can be recorded at most {count} day after its start.",
  "legalDeviations.justificationHint.other":
    "At least 20 characters, no health data. An emergency can be recorded at most {count} days after its start.",
  "legalDeviations.reasonsHint.one":
    "At least 20 characters, no health data and nothing about individual persons. Record the reasons before the rest days they concern, at most {count} day after the start.",
  "legalDeviations.reasonsHint.other":
    "At least 20 characters, no health data and nothing about individual persons. Record the reasons before the rest days they concern, at most {count} days after the start.",
  "ruleParameters.legend": "Parameters",
  "ruleParameters.intro": "Empty fields keep the value of the rule profile.",
  "ruleParameters.none": "There are no changeable parameters for this choice.",
  "ruleParameters.json": "Parameters (JSON)",
  "ruleParameters.expert": "Edit as JSON (expert view)",
  "ruleParameters.unset": "Value of the rule profile",
  "ruleParameters.yes": "Yes",
  "ruleParameters.no": "No",
  "ruleParameters.hours": "{duration} h",
  "ruleParameters.window": "{start}–{end}",
  "ruleParameters.durationHint": "Hours:minutes, for example 9:30.",
  "ruleParameters.percentHint": "Whole number in percent, for example 50.",
  "ruleParameters.percent": "{value} %",
  "ruleParameters.range": "Allowed: {min} to {max}.",
  "ruleParameters.windowHint": "Start between {earliest} and {latest}, length {duration}.",
  "ruleParameters.jsonHint": "Value in JSON notation ({name}).",
  "ruleParameters.problem.required": "Please enter a value.",
  "ruleParameters.problem.duration": "Please enter hours:minutes, for example 9:30.",
  "ruleParameters.problem.percent": "Please enter a whole number, for example 50.",
  "ruleParameters.problem.time": "Please enter a time such as 22:00.",
  "ruleParameters.problem.json": "This is no valid JSON value.",
  "ruleParameters.problem.choice": "Please choose one of the offered values.",
  "ruleParameters.problem.window": "The start must lie between {earliest} and {latest}.",
  "ruleParameters.problem.object":
    'The parameters must be a JSON object, for example {"minRestSeconds": 39600}.',
  "ruleParameter.dailyRegularMaxSeconds": "Regular maximum working time per day",
  "ruleParameter.dailyAbsoluteMaxSeconds": "Maximum working time per day",
  "ruleParameter.weeklyMaxSeconds": "Maximum working time per week",
  "ruleParameter.averagingPeriod": "Averaging period",
  "ruleParameter.averageCountsAbsenceDays": "Count absence days in the average",
  "ruleParameter.minBreakSegmentSeconds": "Minimum length of a break segment",
  "ruleParameter.maxContinuousWorkSeconds": "Maximum work without a break",
  "ruleParameter.minRestSeconds": "Minimum rest period",
  "ruleParameter.reducedMinRestSeconds": "Reduced minimum rest period",
  "ruleParameter.restCompensationMinSeconds": "Rest period compensating a reduction",
  "ruleParameter.restCompensationPeriod": "Compensation period for reduced rest",
  "ruleParameter.nightWindow": "Night period",
  "ruleParameter.nightWorkerAveragingPeriod": "Averaging period for night work",
  "ruleParameter.weeklyAveragingPeriod": "Averaging period of weekly working time",
  "ruleParameter.adjoiningRestSeconds": "Daily rest together with a rest day",
  "ruleParameter.onCallMaxDeploymentPercent": "Maximum share of deployments in the rest period",
  "ruleParameter.onCallCompensationPeriod": "Compensation period for on-call deployments",
  "ruleParameter.deviationAverageWeeklyMaxSeconds":
    "Average maximum working time per week under deviations",
  "ruleParameter.deviationAveragingPeriod": "Averaging period under deviations",
  "ruleParameter.deviationPermitAveragingPeriod":
    "Averaging period under a permit of the authority",
  "averagingPeriod.FourWeeks": "4 weeks",
  "averagingPeriod.OneCalendarMonth": "1 calendar month",
  "averagingPeriod.SixteenWeeks": "16 weeks",
  "averagingPeriod.FourCalendarMonths": "4 calendar months",
  "averagingPeriod.TwentyFourWeeks": "24 weeks",
  "averagingPeriod.SixCalendarMonths": "6 calendar months",
  "averagingPeriod.TwelveCalendarMonths": "12 calendar months",
  // UX WP-7: screens outside the shell and signup (ux-review.md).
  "auth.failedTitle": "Sign-in failed",
  "auth.failedHint":
    "The sign-in could not be completed, for example because the connection was interrupted or the sign-in took too long. Please sign in again.",
  "signup.company": "Company",
  "signup.contact": "Contact person",
  "signup.legalFormHint": "For example GmbH, UG or e. K.",
  "signup.vatIdHint": "For example DE123456789",
  "signup.contactEmailHint": "We send the confirmation link to this address.",
  "signup.federalStateChoose": "Please choose",
  "signup.readTerms": "Read the terms and conditions (opens in a new window)",
  "signup.readDpa": "Read the data processing agreement (opens in a new window)",
  "signup.errorSummary": "The registration cannot be sent yet:",
  "signup.problem.companyName": "Please enter the name of your company.",
  "signup.problem.legalForm": "Please enter the legal form, for example GmbH.",
  "signup.problem.street": "Please enter the street and number.",
  "signup.problem.postalCode": "Please enter the postal code.",
  "signup.problem.postalCodeFormat": "The postal code has five digits, for example 80331.",
  "signup.problem.city": "Please enter the city.",
  "signup.problem.federalState": "Please choose the federal state of the first establishment.",
  "signup.problem.vatId":
    "The VAT ID starts with the country code, for example DE123456789. Leave the field empty if you have none.",
  "signup.problem.contactFirstName": "Please enter the first name of the contact person.",
  "signup.problem.contactLastName": "Please enter the last name of the contact person.",
  "signup.problem.contactEmail": "Please enter the e-mail address of the contact person.",
  "signup.problem.contactEmailFormat":
    "Please enter a complete e-mail address, for example name@company.com.",
  "signup.problem.businessUse":
    "Please confirm that you register the company as a business. The offer is for businesses only.",
  "signup.problem.acceptTerms": "Please accept the terms and conditions.",
  "signup.problem.acceptDpa": "Please conclude the data processing agreement.",
  "signup.linkExpiredHint":
    "Confirmation links are valid for a limited time only. A new registration sends you a new link.",
  "signup.registerAgain": "Register again",
  "signup.toSignIn": "Go to sign-in",
  // UX WP-8: billing (ux-review.md).
  "plan.trial": "Trial",
  "plan.basic": "Basic",
  "plan.business": "Business",
  "plan.enterprise": "Enterprise",
  "subscription.pastDueForBilling":
    "A payment failed. Please check the payment method under “Subscription and payment”; after the dunning period the administration becomes read-only.",
  "subscription.pastDueForAdministration":
    "A payment failed. People with the role “Billing” change the payment method; after the dunning period the administration becomes read-only.",
  "subscription.suspendedForBilling":
    "The administration is read-only because a payment is outstanding. Please check the payment method under “Subscription and payment”; after the payment everything is available again. Clocking, own times and exports keep working.",
  "subscription.suspendedForAdministration":
    "The administration is read-only because a payment is outstanding. Clocking, own times and exports keep working. Once people with the role “Billing” have arranged the payment, everything is available again.",
  "subscription.total": "Total",
  "subscription.entitlementsTitle": "Additional functions",
  "subscription.noEntitlementsText": "The plan includes no additional functions.",
  "serviceStatus.pastDueForBilling":
    "A payment for the subscription failed. Please check the payment method; without payment the administration becomes read-only after the dunning period.",
  "serviceStatus.pastDueForAdministration":
    "A payment for the subscription failed. People with the role “Billing” change the payment method; without payment the administration becomes read-only after the dunning period.",
  "serviceStatus.suspendedForBilling":
    "The administration is read-only because a payment is outstanding. Please check the payment method; after the payment everything is available again.",
  "serviceStatus.suspendedForAdministration":
    "The administration is read-only because a payment is outstanding. Clocking, own times and exports keep working. Once people with the role “Billing” have arranged the payment, everything is available again.",
  "billing.managedByBillingRole":
    "People with the role “Billing” manage payment, invoices and billing details.",
  "billing.country": "Country",
  "billing.countryHint": "You can choose the member states of the European Union.",
  "billing.saved": "Billing details saved.",
  "invoices.list": "Invoices and credit notes",
  "invoices.pdf": "PDF",
  // UX WP-6: app shell, routes and navigation (ux-review.md).
  "notFound.title": "Page not found",
  "notFound.hint": "Check the address or open the start page.",
  "screenError.title": "Something went wrong",
  "screenError.text": "This area could not be shown. Reload the page or open the start page.",
  "screenError.offline.title": "This area is not available offline yet",
  "screenError.offline.text":
    "It has not been stored on this device yet. Clocking works offline as well. Try again once you are back online.",
  "screenError.unavailable.title": "This area could not be loaded",
  "screenError.unavailable.text":
    "Zeitavo may just have been updated. “Try again” reloads the page.",
  "screenError.stillOffline": "You are still offline. Try again once the connection is back.",
  "access.denied.title": "No permission",
  "access.denied.text":
    "This area is not available for your role. If you need it for your work, contact the HR department or the administrators of your employer.",
  "start.overview.title": "Overview",
  "start.overview.functions": "Your functions in the app",
  "start.overview.privacy": "Privacy information and your rights",
  "start.overview.notInApp": "Not in the app",
  "start.overview.records":
    "Authority exports and the records of individual persons are not part of the app. Please contact the company's HR department for them.",
  "start.overview.reports":
    "Reports under the works agreement are not part of the app. Please contact the company's HR department for them.",
  "start.overview.more":
    "Settings and sign-out are in the account menu at your name in the top right.",
  "privacy.toc": "On this page",
  "privacy.infoHint": "Open a topic to read the details.",
  "privacy.notificationsHint":
    "Whether this device receives reminders as notifications is set under “Settings” in the account menu at your name.",
  "privacy.notificationsLink": "Notification settings",
  "setup.step.LeavePolicy": "Leave rules set",
  "leave.account.title": "Leave account",
  "leave.account.year": "Leave year",
  "leave.account.notSetUp": "Leave account not set up yet",
  "leave.account.notSetUp.text":
    "Your employer has not stored the leave rules in Zeitavo yet. You can still request leave; your leave account appears here once it is set up.",
  "leave.account.notSetUp.hr":
    "Set the leave rules so that Zeitavo keeps the leave accounts of the employees.",
  "leave.account.notSetUp.link": "Set leave rules",
  "leave.account.beforeStart":
    "The leave account starts in the leave year {year}. Zeitavo keeps no leave account for earlier years.",
  "leave.account.offline": "Offline: last known state of {time}.",
  "leave.account.entitled": "Entitlement",
  "leave.account.carriedOver": "Carried over from earlier years",
  "leave.account.booked": "Bookings by HR",
  "leave.account.taken": "Taken",
  "leave.account.approved": "Planned (approved)",
  "leave.account.requested": "Requested",
  "leave.account.forfeited": "Forfeited",
  "leave.account.paidOut": "Paid in lieu",
  "leave.account.pendingPayout": "To be paid in lieu from an ended employment relationship",
  "leave.account.notDeducted": "Excess of an earlier employment relationship, not deducted",
  "leave.account.available": "Available",
  "leave.account.availableHint":
    "Available are the days that can still be requested: entitlement and carry-over minus taken, planned and requested days.",
  "leave.days.one": "{count} day",
  "leave.days.other": "{count} days",
  "leave.carryOver.expires":
    "Of these, {days} from {years} forfeit on {date} unless taken by then.",
  "leave.carryOver.keeps":
    "Of these, {days} from {years} do not forfeit because no timely notice of the forfeiture was sent; they are carried over again.",
  "leave.carryOver.negative": "Taken in advance ({years}): {days}",
  "leave.items": "Show items",
  "leave.items.hint": "Every item names the rule it is computed by.",
  "leave.item.Entitlement": "Entitlement",
  "leave.item.WaitingPeriod": "Waiting period",
  "leave.item.PartialLeave": "Partial leave",
  "leave.item.Rounding": "Rounding",
  "leave.item.SameYearCap": "Limit for several employment relationships in the year",
  "leave.item.CarryOver": "Carry-over",
  "leave.item.CarryOverLimit": "Advance that is not carried over",
  "leave.item.Booking": "Booking",
  "leave.item.Taken": "Taken",
  "leave.item.Approved": "Planned",
  "leave.item.Requested": "Requested",
  "leave.item.Forfeiture": "Forfeiture",
  "leave.item.Payout": "Payment in lieu",
  "leave.item.WithoutEffect": "Without effect",
  "leave.item.withoutEffect.vacation":
    "Leave before the start of the leave account ({date}), without effect: counts in no leave account ({days})",
  "leave.item.withoutEffect.booking":
    "Booking for a year before the start of the leave account, without effect: counts in no leave account ({days})",
  "leave.item.origin": "from {year}",
  "leave.item.rule": "Rule {rule}",
  "leave.request.counted": "Counted leave days: {days}",
  "leave.request.countedHint":
    "Your working days are counted without public holidays; half a company day off counts half.",
  "leave.request.noWorkingDay":
    "The chosen period contains no working day; no leave day is charged. Please check the dates.",
  "leave.request.rest": "Remaining afterwards in the leave year {year}: {days}",
  "leave.request.forfeits": "Of these, {days} forfeit by the end of the year unless taken before.",
  "leave.request.covered": "The leave account covers this request.",
  "leave.request.blocked":
    "The leave account does not cover this request. Requests above the leave account are not possible; contact HR if needed.",
  "leave.request.toHr":
    "The leave account does not cover this request. You can still submit it; HR decides on it.",
  "leave.request.beforeStart":
    "The request starts before the start of your leave account ({year}). Leave before it counts in no leave account and is decided as before.",
  "leave.request.calculating": "Calculating the days of leave …",
  "leave.request.tooFarAhead": "Leave can be requested up to {date} at the most.",
  "leave.request.previewUnavailable":
    "The days of leave cannot be calculated right now. The request is checked against the leave account when it is sent.",
  "leave.request.laterYear":
    "In the leave year {year} the leave would then no longer cover the leave already planned or requested there.",
  "leave.request.noHalfDays": "Your employer does not provide for half days of leave.",
  "leave.request.submittedToHr":
    "The request was submitted. HR decides on it because the leave account does not cover it.",
  "leave.request.hr.notCovered":
    "The leave account does not cover this leave. You can record it only with a reason.",
  "leave.request.hr.beforeStart":
    "The leave starts before the start of the leave account. Days before it count in no leave account.",
  "leave.reason": "Reason",
  "leave.reason.hint": "The reason is stored and visible to the employee.",
  "leave.reason.missing": "Please enter a reason.",
  "leave.coverage.Covered": "Account covers the request",
  "leave.coverage.NotCovered": "Account does not cover the request",
  "leave.coverage.BeforeAccountStart": "Before the start of the leave account",
  "leave.coverage.hrDecides": "HR decides on it, with a reason.",
  "leave.coverage.notCoveredHrDecides":
    "The leave account does not cover the request; HR decides on it, with a reason.",
  "leave.coverage.unknownHrDecides":
    "The request could not be checked against the leave account; HR decides on it.",
  "leave.approveWithReason": "Approve with reason",
  "leave.approveWithReason.consequence":
    "The leave account does not cover this request, or it could not be checked against it. Approve it only with a reason.",
  "leave.approveWithReason.confirm": "Approve",
  "leave.approvalReason": "Reason of the approval: {reason}",
  "error.TT-3090":
    "The leave account does not cover this request, and requests above the leave account are not possible. Contact HR if needed.",
  "error.TT-3091":
    "The leave account does not cover this request, or it could not be checked against it. HR decides on it.",
  "error.TT-3092":
    "Leave above the leave account or with an unknown coverage can be approved or recorded only with a reason.",
  "error.TT-3094": "Leave before the year 2000 cannot be checked against the leave account.",
  "error.TT-3095":
    "Leave can be requested or recorded up to the end of the year after next at the most.",
  "error.TT-3093":
    "Your employer does not provide for half days of leave. Please request the whole day.",
  "absences.kind.Rehabilitation": "Rehabilitation",
  "absences.code.Rehabilitation": "RH",
  "absences.rehabilitationHint":
    "Medical prevention or rehabilitation does not count as leave (§ 10 BUrlG). Only HR records it; managers see only “absent”.",
  "admin.leave.booking.daysHintSick":
    "Only with a medical certificate (§ 9 BUrlG): the leave days of the incapacity as a positive number; they return to the leave account. Do not enter a diagnosis in the reason.",
  "admin.leave.withdrawnItem": "Withdrawn: {reason}",
  "admin.leave.disability.title": "Severe disability (additional leave under § 208 SGB IX)",
  "admin.leave.disability.hint":
    "Health data: only HR sees and maintains it, and it is stored encrypted. Five working days of additional leave at five working days per week, one twelfth per full month in the year it begins or ends. The employee sees the additional leave in the own leave account, managers never.",
  "admin.leave.disability.none": "No severe disability recorded.",
  "admin.leave.disability.open": "from {from}",
  "admin.leave.disability.closed": "{from} to {to}",
  "admin.leave.disability.item": "{period} – {reference}",
  "admin.leave.disability.from": "Valid from",
  "admin.leave.disability.to": "Valid until",
  "admin.leave.disability.toHint": "Leave empty while the severe disability has no end.",
  "admin.leave.disability.reference": "Proof",
  "admin.leave.disability.referenceHint":
    "For example “notice of the pension office of …”. Do not enter a diagnosis or a degree of disability.",
  "admin.leave.disability.save": "Record severe disability",
  "admin.leave.disability.saved": "The severe disability is recorded.",
  "admin.leave.disability.endDate": "Last day",
  "admin.leave.disability.end": "Record end",
  "admin.leave.disability.ended": "The end is recorded.",
  "admin.leave.protection.withdraw": "Withdraw",
  "admin.leave.protection.withdrawConfirm": "Withdraw the entry for good",
  "admin.leave.protection.withdrawConsequence":
    "The entry no longer counts for the leave account but stays in the history with your reason.",
  "admin.leave.protection.withdrawn": "The entry is withdrawn.",
  "admin.leave.parental.title": "Parental leave (§ 17 BEEG)",
  "admin.leave.parental.hint":
    "Leave is reduced only if the employer declared the reduction: one twelfth per full calendar month of parental leave, not for part-time work for us during the parental leave and not for months of maternity protection. Record the declaration only once the employee has received it. Remaining leave from before the parental leave forfeits at the end of the leave year after the parental leave at the earliest (§ 17 Abs. 2 BEEG).",
  "admin.leave.parental.none": "No parental leave recorded.",
  "admin.leave.parental.item": "{from} to {to}",
  "admin.leave.parental.partTimeItem":
    "Part-time work for us during the parental leave, no reduction",
  "admin.leave.parental.declared": "Reduction declared, recorded on {date}: {reference}",
  "admin.leave.parental.notDeclared": "No reduction declared; the leave is not reduced.",
  "admin.leave.parental.from": "First day",
  "admin.leave.parental.to": "Last day",
  "admin.leave.parental.partTime": "Part-time work for us during the parental leave",
  "admin.leave.parental.save": "Record parental leave",
  "admin.leave.parental.saved": "The parental leave is recorded.",
  "admin.leave.parental.reference": "Declaration of the employer",
  "admin.leave.parental.referenceHint":
    "For example “letter of …”. The reduction can be recorded only with this reference and only while the employment lasts.",
  "admin.leave.parental.declare": "Record reduction",
  "admin.leave.parental.declareConfirm": "Record the reduction for good",
  "admin.leave.parental.declareConsequence":
    "The leave account is reduced by one twelfth of the annual leave for every full calendar month of this parental leave. The declaration stays in the history with its instant.",
  "admin.leave.parental.declaredDone": "The declaration is recorded.",
  "privacy.categories.severeDisability": "Severe disability",
  "privacy.categories.severeDisability.basis":
    "Duties under § 208 SGB IX (additional leave) for health data (Art. 9(2)(b) GDPR). Only HR sees the period; you see the additional leave in your leave account, managers never.",
  "privacy.categories.severeDisability.retention":
    "Deleted {years} years after the end of the calendar year in which the period ended, like absences.",
  "privacy.categories.severeDisability.portability":
    "The processing is based on Art. 9(2)(b) GDPR, not on consent or contract.",
  "privacy.categories.parentalLeave": "Parental leave",
  "privacy.categories.parentalLeave.basis":
    "Employment contract and legal obligation (Art. 6(1)(b) and (c) GDPR; § 17 BEEG): period, part-time work and the declaration of a reduction of the leave.",
  "privacy.categories.parentalLeave.retention":
    "Deleted {years} years after the end of the calendar year in which the parental leave ended, like absences.",
  "privacy.categories.parentalLeave.portability":
    "You requested the period of your parental leave yourself; to that extent Art. 20 GDPR applies. The declaration of a reduction comes from your employer and is not covered.",
  "error.TT-1090":
    "Leave cannot be reduced for parental leave with part-time work (§ 17(1) sentence 2 BEEG).",
  "error.TT-3110":
    "The period does not touch any employment of the person. Record the employment first.",
  "admin.leave.protection.reasonHint":
    "Required, without a diagnosis or degree of disability; only then can the entry be withdrawn.",
  "admin.leave.protection.error.required": "Required: please fill in.",
  "admin.leave.protection.error.beforeStart":
    "The last day lies before the first day. Please choose a later day.",
  "error.TT-3111":
    "The reduction for parental leave can only be declared while the employment relationship of the parental leave lasts.",
  "leave.item.SevereDisability": "Additional leave (severe disability)",
  "leave.item.ParentalLeaveReduction": "Reduction for parental leave",
  "leave.item.ExcessNotDeducted": "Excess of an earlier employment relationship (not deducted)",
  "leave.item.PendingPayout": "To be paid in lieu (ended employment relationship)",
  "leave.item.withoutMaternityMonths": "without months of maternity protection",
  "leave.notices.title": "Notices on remaining leave and forfeiture",
  "leave.notices.new":
    "Your employer sent you a notice on your remaining leave. Please read it and confirm that you have read it.",
  "leave.notices.heading": "Notice of {date} on the leave year {year}",
  "leave.notices.emailSent": "Delivered by e-mail on {time}.",
  "leave.notices.confirm": "Read and acknowledged",
  "leave.notices.confirmed": "Read on {time}.",
  "leave.notices.confirmedNow": "The notice is marked as read.",
  "leave.notices.read": "Read the notice",
  "admin.leave.notices.title": "Notices on remaining leave and forfeiture",
  "admin.leave.notices.hint":
    "Carried-over leave forfeits only after a notice delivered in time: accepted by the mail server, confirmed in the app or handed over as a letter. The text stays stored as it was sent.",
  "admin.leave.notices.none": "No notice sent yet.",
  "admin.leave.notices.sendNow": "Send a notice now",
  "admin.leave.notices.sent": "The notice has been sent.",
  "admin.leave.notices.item": "{kind} of {date} on the leave year {year}",
  "admin.leave.notices.kind.Annual": "Annual notice",
  "admin.leave.notices.kind.Reminder": "Reminder",
  "admin.leave.notices.kind.CarryOver": "Notice on carried-over leave",
  "admin.leave.notices.figures":
    "Named: {days} of remaining leave, forfeiting at the end of {deadline}.",
  "admin.leave.notices.delivered.Email": "By e-mail, counts as delivered on {date}.",
  "admin.leave.notices.delivered.InApp": "Confirmed in the app, counts as delivered on {date}.",
  "admin.leave.notices.delivered.Letter": "Handed over as a letter, counts as delivered on {date}.",
  "admin.leave.notices.notDelivered": "Not delivered yet, does not count yet.",
  "admin.leave.notices.email.Pending": "The e-mail is waiting to be sent.",
  "admin.leave.notices.email.Sent": "The e-mail has been delivered.",
  "admin.leave.notices.email.Failed": "The e-mail failed.",
  "admin.leave.notices.email.NoAddress": "No e-mail address known.",
  "admin.leave.notices.email.NotConfigured": "No e-mail delivery set up.",
  "admin.leave.notices.bySystem": "Created automatically on {time}.",
  "admin.leave.notices.byHr": "Created by HR on {time}.",
  "admin.leave.notices.proof": "Template {version}, checksum {hash}",
  "admin.leave.notices.text": "Show the text",
  "admin.leave.notices.resend": "Send the e-mail again",
  "admin.leave.notices.resent": "The e-mail is being sent again.",
  "admin.leave.notices.letterOn": "Handed over as a letter on",
  "admin.leave.notices.letter": "Record the letter",
  "admin.leave.notices.lettered": "The letter is recorded.",
  "admin.leave.notices.overview.title": "Notices on remaining leave {year}",
  "admin.leave.notices.overview.hint":
    "Without a delivered notice no leave forfeits and the limitation period does not start. These employees still need something; the figures are in the person's leave account.",
  "admin.leave.notices.overview.delivered.one": "{count} person has a delivered notice.",
  "admin.leave.notices.overview.delivered.other": "{count} people have a delivered notice.",
  "admin.leave.notices.overview.allDone":
    "Every employee with a leave account has a delivered notice.",
  "admin.leave.notices.figuresWithoutDay":
    "Named: {days} of remaining leave; no day of forfeiture, because a statutory protection keeps the leave at present.",
  "admin.leave.notices.proofKind.EmailOnly":
    "By e-mail only (accepted by the mail server, counts from the next working day), not confirmed.",
  "admin.leave.notices.proofKind.Confirmed": "Confirmed by the person in the app.",
  "admin.leave.notices.proofKind.Letter": "Handed over as a letter.",
  "admin.leave.notices.lateWarning":
    "The last day for timely notices was {date}. A notice delivered now informs, but lets no leave forfeit.",
  "admin.leave.notices.state.DeliveredLate": "Notice delivered too late (no forfeiture)",
  "error.TT-1083":
    "A letter can be recorded at most 14 days after it was handed over. Send a new notice instead.",
  "error.TT-1084":
    "A planned notice lies less than a week before the last day for timely notices and may then arrive too late. Choose an earlier day.",
  "admin.leave.notices.state.Missing": "Notice missing",
  "admin.leave.notices.state.Pending": "Delivery in progress",
  "admin.leave.notices.state.NotDelivered": "Notice not delivered",
  "admin.leave.notices.state.Delivered": "Notice delivered",
  "admin.leave.notices.carriedWithoutNotice":
    "Carried over from {years} without a timely notice: does not forfeit.",
  "admin.leave.notices.forfeitureDeferred":
    "The forfeiture is booked only once an open vacation request has been decided.",
  "admin.leave.notices.overview.lateSchedule":
    "The leave rules plan a notice on {days}. It may then arrive too late and let no leave forfeit. Move it in the leave rules to at least a week before the last day, {date}.",
  "team.leaveHints.title": "Talk about leave planning",
  "team.leaveHints.hint":
    "These team members still have much leave that is neither taken nor planned. Only the person and HR see the figures.",
  "error.TT-1080":
    "A forfeiture follows from the leave account and cannot be reversed. Book a correction with a reason instead.",
  "error.TT-1081": "The day of the letter must lie between the day of the notice and today.",
  "error.TT-3100":
    "No notice sent: the person has no leave account this year or no employment today.",
  "error.TT-3101": "A notice was already sent today. Send its e-mail again or record a letter.",
  "error.TT-3102": "The e-mail of this notice is waiting to be sent or has been delivered.",
  "leave.account.closed":
    "Leave year closed on {time}: the leave account is frozen. HR books later corrections in the current year.",
  "leave.account.deleted":
    "This leave year is closed; its data were deleted after the retention period.",
  "admin.leave.closing.title": "Year-end closing of leave",
  "admin.leave.closing.hint":
    "After 31 December the leave accounts of a year are frozen: the carry-over into the next year is then fixed, and HR books later corrections in the current year. Accounts with undecided leave requests or leave still to be paid in lieu stay open until both are settled.",
  "admin.leave.closing.year": "Leave year",
  "admin.leave.closing.notYet":
    "The leave year {year} can be closed only after it has ended and with stored leave rules.",
  "admin.leave.closing.open": "The leave year {year} is not closed yet.",
  "admin.leave.closing.closedAutomatically":
    "The leave year {year} was closed automatically on {time}.",
  "admin.leave.closing.closedByHr": "The leave year {year} was closed by HR on {time}.",
  "admin.leave.closing.automatic": "Zeitavo closes the year by itself on {date}.",
  "admin.leave.closing.counts":
    "Frozen: {frozen} · ready to close: {ready} · still open: {pending}",
  "admin.leave.closing.pending": "Not frozen yet",
  "admin.leave.closing.blocker.OpenRequests": "A leave request is not decided yet.",
  "admin.leave.closing.blocker.OpenPayout": "Leave is still to be paid in lieu.",
  "admin.leave.closing.blocker.NotCalculable": "The leave account cannot be computed.",
  "admin.leave.closing.close": "Close leave year {year}",
  "admin.leave.closing.closeRemaining": "Close the remaining accounts of {year}",
  "admin.leave.closing.consequence":
    "The leave accounts of {year} are frozen. Afterwards no leave of that year can be requested, approved or cancelled and nothing can be booked; HR books corrections in the current year. Record all exits of the year first: an exit recorded later is paid in lieu in the first open year.",
  "admin.leave.closing.confirm": "Close for good",
  "admin.leave.closing.done": "Leave year {year} closed.",
  "admin.leave.report.title": "Report “Leave per person” {year}",
  "admin.leave.report.hint":
    "Entitlement, carry-over, bookings, days taken, planned, forfeited and paid in lieu of all employees from the leave account. Without the right to read health data, items resting on it count as bookings.",
  "admin.leave.report.csv": "Download as CSV",
  "admin.leave.report.pdf": "Download as PDF",
  "datev.category.LeaveTakenDays":
    "Leave days as the leave account counts them (instead of “Vacation days”)",
  "error.TT-1120": "This leave year is closed; its leave account is frozen.",
  "error.TT-1121": "A leave year can be closed only after 31 December.",
  "error.TT-3140": "The leave year is closed. Book the correction in the current year.",
  "error.TT-3141":
    "The leave lies in a closed leave year and can no longer be changed. HR books a correction in the current year.",
  "error.TT-3142": "This leave year has no leave accounts to close.",
  "privacy.categories.leaveAccount": "Leave account",
  "privacy.categories.leaveAccount.basis":
    "Employment contract and legal obligation (Art. 6(1)(b) and (c) GDPR; BUrlG): leave entitlements, bookings by HR, the leave accounts frozen at the year-end closing and the notices on remaining leave with their delivery.",
  "privacy.categories.leaveAccount.retention":
    "Deleted {years} years after the end of the leave year once it is closed; notices on remaining leave at the earliest {years} years after the end of the year in which they were delivered (proof for forfeiture and limitation).",
  "privacy.categories.leaveAccount.portability":
    "The data come from your employer or are computed from your absences; Art. 20 GDPR does not apply. You receive them with the access under Art. 15 GDPR.",
  "admin.leave.closing.review": "Please check",
  "admin.leave.closing.review.ExitAfterClosing":
    "Exit recorded after the closing: the payment in lieu is still open and is booked in the first open leave year.",
  "admin.leave.closing.review.Deviation":
    "The account differs; please check it and correct it in the open year.",
  "admin.leave.closing.review.NotVerifiable": "The leave year cannot be computed again.",
  "admin.leave.closing.verify": "Check the frozen accounts",
  "admin.leave.closing.verifyHint":
    "Computes the frozen accounts of the year again from today’s data and names the persons whose account differs, without figures and without a reason.",
  "admin.leave.closing.verifiedNone": "No frozen account differs from the account computed again.",
  "admin.leave.exit.closedYear":
    "The leave year {year} was already closed when the last day was recorded: the payment in lieu is booked in the leave year {payoutYear}, at least with the frozen rest. In the DATEV export it belongs to the month of the last day; export that month again.",
  "admin.leave.closedYearReached":
    "The entry reaches into leave years already closed for this person (up to {year}). Closed years do not change: under “Absence requests → Year-end closing of leave”, use “Check the frozen accounts” to see whether the frozen account differs, and book any correction in the first open leave year.",
  "admin.employment.endedInClosedYear":
    "The leave year {year} is already closed for this person. Book the payment in lieu of the remaining leave under “Leave → Entry and exit”; it is booked in the first open leave year.",
  "error.TT-1122":
    "The leave year cannot be computed again: the frozen account of the year before was deleted after the retention period.",
  "error.TT-3143": "The start of the leave accounts can no longer change: leave years are closed.",
  "error.TT-3160":
    "The clock event belongs to a shift that began in a month that is already closed. Please request a correction with this time, together with the start of the shift if it was refused as well; your manager decides on it. Until then, the shift counts as not ended.",
  "admin.monthClosing": "Month closing",
  "admin.monthClosing.open": "Open month closing and approval",
  "closing.title": "Month closing",
  "closing.hint":
    "Close a month when all times are recorded and checked. Afterwards clock times of the month change only through approved correction requests, absences and employments only after a reopening. When an approved correction changes the month, you get a notice here to create the payroll export of the month again.",
  "closing.month": "Month",
  "closing.scope": "Scope",
  "closing.scope.tenant": "All establishments",
  "closing.state.open": "{month} is open and can be closed.",
  "closing.state.closed": "{month} is closed (on {time}).",
  "closing.state.closedForTenant": "{month} is closed for all establishments.",
  "closing.state.notOver": "{month} is not over yet. It can be closed from the following month on.",
  "closing.state.blocked": "{month} cannot be closed yet.",
  "closing.counts":
    "{persons} employees, {confirmed} confirmed and {approved} approved. Confirmation and approval are not required for the closing.",
  "closing.violations":
    "{count} findings of the month are not acknowledged yet. They do not prevent the closing.",
  "closing.recentlyEnded":
    "{month} ended only a few days ago. Clock events of the last days that a device still holds offline are refused after the closing and need a correction request.",
  "closing.blocked": "Still open",
  "closing.blocker.OpenShift": "A shift of the month has not ended.",
  "closing.blocker.PendingCorrection": "A correction request waits for a decision.",
  "closing.blocker.PendingAbsence": "An absence request waits for a decision.",
  "closing.close": "Close month",
  "closing.closeConsequence":
    "Afterwards clock times of the month change only through approved correction requests, absences and employments only after a reopening. A reopening supersedes the confirmations and approvals of the month.",
  "closing.closed": "{month} is closed.",
  "closing.reopen": "Reopen month",
  "closing.reopenReason": "Reason",
  "closing.reopenHint": "The reason is shown in the history and logged.",
  "closing.reasonRequired": "Please give a reason.",
  "closing.reopenConsequence":
    "Clock times, absences and employments of the month can then be changed directly again. Confirmations and approvals of the month are superseded and must be given again. The reason is logged.",
  "closing.reopened": "{month} is open again.",
  "closing.history": "History",
  "closing.history.closed": "Closed on {time}",
  "closing.history.reopened": "Closed on {closed}, reopened on {reopened}: {reason}",
  "closing.notices": "Changed after the closing",
  "closing.noticesHint":
    "Approved corrections changed the closed month. Export the payroll data of the month again.",
  "closing.notice": "{person}, {month}: correction approved on {time}",
  "closing.noticeAcknowledge": "Take note",
  "closing.noticeAcknowledgeFor": "Take note: {person}, {month}",
  "closing.noticeAcknowledged": "Noted on {time}",
  "closing.approvals": "Month approval",
  "closing.approvals.hint":
    "With the approval you confirm the recorded times and the balance of the month. If you doubt single times or overtime, approve with a reservation and give the reason.",
  "closing.approvals.empty": "No persons in this month.",
  "closing.approvals.notYet": "The month can be approved after it has ended.",
  "closing.approvals.azvWithheld":
    "HR approves months with days under the AZV (§ 7 (8) AZV); not listed: {count}",
  "closing.column.person": "Person",
  "closing.column.state": "State",
  "closing.column.comment": "Comment and reservation",
  "closing.note.comment": "Comment: {text}",
  "closing.note.reservation": "Reservation: {text}",
  "closing.column.action": "Action",
  "closing.person.Approved": "Approved",
  "closing.person.ApprovedWithReservation": "Approved with reservation",
  "closing.person.Confirmed": "Confirmed",
  "closing.person.Withdrawn": "Confirmation withdrawn",
  "closing.person.Open": "Open",
  "closing.approve": "Check and approve",
  "closing.approveTitle": "Approve the month of {person}",
  "closing.approvalText":
    "I have checked the working times of {person} for {month} and approve them with the balance shown. The approval can make a credit shown undisputed; if in doubt, approve with a reservation.",
  "closing.reservation": "Reservation with reason",
  "closing.approveConfirm": "Approve month",
  "closing.approveCancel": "Cancel",
  "closing.approved": "The month of {person} is approved.",
  "closing.balance.target": "Target",
  "closing.balance.actual": "Actual",
  "closing.balance.absence": "Absences",
  "closing.balance.month": "Balance of the month",
  "closing.balance.account": "Time account at the end of the month",
  "closing.balance.caption": "Balance {month}",
  "closing.balance.captionPerson": "Balance of {person} for {month}",
  "closing.locked": "Closed",
  "closing.own.title": "Confirm month",
  "closing.own.hint":
    "With the confirmation you state that you took note of the recorded times of the month. You waive nothing and can withdraw the confirmation until the approval. Report errors with a correction request.",
  "closing.own.disabled": "Your employer does not ask for a confirmation of the month.",
  "closing.own.notYet": "The month can be confirmed after it has ended.",
  "closing.own.comment": "Comment for the approval",
  "closing.own.confirm": "Confirm month",
  "closing.own.withdraw": "Withdraw confirmation",
  "closing.own.confirmed": "Confirmed on {time}.",
  "closing.own.commentShown": "Your comment: {comment}",
  "closing.own.withdrawn": "Confirmation withdrawn on {time}.",
  "closing.own.approved": "Approved on {time}.",
  "closing.own.approvedWithReservation": "Approved on {time} with reservation: {reservation}",
  "closing.own.locked":
    "The month is closed. Changes are possible only through a correction request.",
  "closing.own.announceConfirmed": "The month is confirmed.",
  "closing.own.announceWithdrawn": "The confirmation is withdrawn.",
  "corrections.lockedMonth": "Affects a closed month",
  "corrections.lockedMonthHint":
    "The correction changes a closed month. After the approval HR gets a notice, because the payroll data of the month change.",
  "corrections.lockedMonthPreview":
    "The request is not approved automatically: your manager decides, and HR gets a notice after the approval.",
  "settings.monthConfirmation": "Month closing",
  "settings.monthConfirmation.hint":
    "Employees confirm their month before a manager or HR approves it. The confirmation states knowledge without a waiver, can be withdrawn until the approval and is not required for the month closing.",
  "settings.monthConfirmation.label": "Confirmation of the month by the employees",
  "error.TT-1170": "The month is approved already; the confirmation can no longer change.",
  "error.TT-1171": "The month is confirmed already.",
  "error.TT-1172": "The month is not confirmed; there is nothing to withdraw.",
  "error.TT-1173": "The month is not closed for this scope.",
  "error.TT-1180": "Choose at least one establishment (at most 200).",
  "error.TT-1181":
    "Only the HR role without tenant administration and audit can be limited to establishments.",
  "error.TT-3240":
    "This function applies to the whole tenant. Your HR rights are limited to some establishments.",
  "error.TT-3241": "This establishment is not one of your establishments.",
  "error.TT-1174": "The month of this person is approved already.",
  "error.TT-3220":
    "The month cannot be closed: a shift of the month has not ended. The persons are listed under “Still open”.",
  "error.TT-3221":
    "The month cannot be closed: correction requests of the month wait for a decision.",
  "error.TT-3222": "The month cannot be closed: absence requests of the month wait for a decision.",
  "error.TT-3223": "The month is closed already for this scope or for all establishments.",
  "error.TT-3224":
    "The month is not over yet: it can be closed, confirmed and approved only after it has ended, also in the time zones of all establishments.",
  "error.TT-3225": "Your employer does not ask for a confirmation of the month.",
  "error.TT-3226":
    "The absence has a day in a closed month. HR must reopen the month with a reason first.",
  "error.TT-3227": "The month is not closed for this scope.",
  "error.TT-3228":
    "The month cannot be approved yet: a shift of the month has not ended, or a correction or absence request of the month waits for a decision. The balance shown would still change.",
  "error.TT-3229":
    "The change of the employment affects a closed month. Reopen the month with a reason under “Month closing” first.",
  "error.TT-3520":
    "The month was just changed elsewhere, for example confirmed, approved or opened again by a correction. The current state is loaded again; check it and then decide again.",
  "absences.kind.EducationLeave": "Education leave",
  "absences.kind.Other": "Other absence",
  "absences.code.EducationLeave": "EL",
  "absences.code.Other": "A",
  "absences.educationLeaveHint":
    "Education leave under the law of your federal state: paid release with its own quota, not taken from the leave account. Clarify the recognition of the event, the notice period and the proofs with HR; request further training beyond it as training.",
  "absences.error.pastEducationLeave":
    "Education leave can be requested from today ({date}). Past days are recorded by HR.",
  "educationLeave.title": "Education leave",
  "educationLeave.remaining": "{days} left (quota {quota}, {from} to {to})",
  "educationLeave.approved": "Approved: {days}",
  "educationLeave.requested": "Requested: {days}",
  "educationLeave.carriedOver": "Carried over from the previous year: {days}",
  "educationLeave.advance": "Taken in advance from the next year: {days}",
  "educationLeave.hrDecides":
    "Outside the statutory claim (waiting period or quota); HR decides with a reason.",
  "educationLeave.approveWithReason.consequence":
    "The education leave lies outside the statutory claim (waiting period or quota). Approve it only with a reason, for example a transfer after a refusal or an agreement.",
  "educationLeave.statute": "Legal basis ({state}): {reference}.",
  "educationLeave.unverified":
    "The values of this state are not yet checked against the official text; the state law prevails.",
  "educationLeave.waiting": "Education leave can be taken from {date} (waiting period).",
  "educationLeave.noStatute": "{state} has no statutory education leave.",
  "educationLeave.noEmployment":
    "Without employment on this day there is no claim to education leave.",
  "educationLeave.hint":
    "Education leave is not taken from the leave account. The quota is the statutory upper limit for own requests; HR checks whether an event is recognised.",
  "settings.educationLeave": "Education leave under state law",
  "settings.educationLeave.label": "Offer education leave as an absence kind of its own",
  "settings.educationLeave.hint":
    "Off by default. When switched on, employees can request education leave; its quota follows the federal state of the establishment on the first day and is not taken from the leave account. Own requests above the quota or before the end of the waiting period are refused; only HR records or approves more, with a reason (for example a transfer after a refusal or an agreement). Record voluntary release as training or special leave.",
  "settings.educationLeave.warning":
    "Have it checked legally before switching it on: the values per federal state come from profile {version} and are checked against the official text only in part (column “Checked”). The deciding person checks the recognition of the event, notice periods, the size of the business, grounds for refusal and leave granted by a previous employer.",
  "settings.educationLeave.rules": "Values per federal state",
  "settings.educationLeave.column.state": "Federal state",
  "settings.educationLeave.column.quota": "Quota",
  "settings.educationLeave.column.waiting": "Waiting period",
  "settings.educationLeave.column.reference": "Legal basis",
  "settings.educationLeave.column.verified": "Checked",
  "settings.educationLeave.none": "no statute",
  "settings.educationLeave.perYear": "{days} working days per calendar year",
  "settings.educationLeave.perYearCarried":
    "{days} working days per calendar year, previous year transferable",
  "settings.educationLeave.perYearAdvance":
    "{days} working days per calendar year, next year can be taken in advance",
  "settings.educationLeave.perTwoYears": "{days} working days in two calendar years",
  "settings.educationLeave.perTwoYearsOdd":
    "{days} working days in two calendar years from an odd year",
  "settings.educationLeave.perTwoYearsFirstUse":
    "{days} working days in two calendar years from the first use",
  "settings.educationLeave.stateFrom": "{state} (from {date})",
  "settings.educationLeave.stateUntil": "{state} (until {date})",
  "settings.educationLeave.months": "{months} months",
  "settings.educationLeave.verifiedOn": "on {date}",
  "settings.educationLeave.notVerified": "no",
  "error.TT-1150":
    "The federal state of your establishment has no statutory education leave on this day. Request release beyond it as training or special leave.",
  "error.TT-1151":
    "The claim to education leave arises only after the waiting period. Please choose a later first day; before it only HR records education leave, with a reason.",
  "error.TT-1152":
    "The education leave exceeds the statutory quota of the period. Beyond it only HR records education leave, with a reason (for example a transfer after a refusal or an agreement); otherwise as training or special leave.",
  "error.TT-1153": "There is no employment on this day and therefore no claim to education leave.",
  "error.TT-1154": "There are no education leave rules for this day.",
  "error.TT-3190":
    "Education leave is not switched on for your employer. Only the tenant administration switches it on after a legal review.",
  "error.TT-3191":
    "Education leave can only be requested from today. Past days are recorded by HR.",
  "error.TT-3192": "Your employer does not allow half days. Please request the whole day.",
  "error.TT-3193":
    "Education leave can be requested or recorded up to the end of the year after next at the latest.",
  "error.TT-3194":
    "Education leave outside the statutory claim (waiting period or quota) can be recorded or approved only with a reason.",
  "error.TT-3195":
    "This education leave lies outside the statutory claim. Only HR can approve it, with a reason.",
  "terminal.title": "Time clock terminal",
  "terminal.loading": "Loading the terminal …",
  "terminal.privacy":
    "This terminal shows no names. It keeps only encrypted verification data of the employees of this establishment.",
  "terminal.pair.title": "Pair the terminal",
  "terminal.pair.intro":
    "HR creates the terminal under “Administration” › “Establishments” and receives a pairing code. Open the link of the code on this device or paste the code here.",
  "terminal.pair.code": "Pairing code",
  "terminal.pair.submit": "Pair",
  "terminal.pair.pairing": "Pairing …",
  "terminal.pair.invalid":
    "The code is invalid, expired or used already. Please ask HR to create a new terminal.",
  "terminal.pair.offline":
    "No connection. The terminal needs a connection to the server once to be paired.",
  "terminal.pair.error": "The terminal could not be paired. Please try again.",
  "terminal.revoked": "HR has removed this terminal.",
  "terminal.revoked.deleted": "All data were deleted from the device.",
  "terminal.revoked.uploading.one":
    "{count} entry is still sent as soon as there is a connection; then the terminal deletes all data.",
  "terminal.revoked.uploading.other":
    "{count} entries are still sent as soon as there is a connection; then the terminal deletes all data.",
  "terminal.revoked.lost.one":
    "{count} entry could not be sent. All data were deleted from the device; please inform HR.",
  "terminal.revoked.lost.other":
    "{count} entries could not be sent. All data were deleted from the device; please inform HR.",
  "terminal.pair.pending.one":
    "{count} entry of the previous pairing has not been sent from this device yet. Connect the device and try again – pairing would lose it.",
  "terminal.pair.pending.other":
    "{count} entries of the previous pairing have not been sent from this device yet. Connect the device and try again – pairing would lose them.",
  "terminal.pair.discard.one": "Pair anyway and discard {count} entry",
  "terminal.pair.discard.other": "Pair anyway and discard {count} entries",
  "terminal.pair.personSignedIn":
    "A person is signed in to the app on this device. A terminal is a shared device: please sign the person out first, then open the pairing link again.",
  "terminal.pair.signOutPerson": "Sign the person out",
  "terminal.header": "{name} · {establishment}",
  "terminal.offline": "Offline",
  "terminal.pending.one": "{count} entry will be sent as soon as there is a connection.",
  "terminal.pending.other": "{count} entries will be sent as soon as there is a connection.",
  "terminal.signIn.title": "Sign in to clock",
  "terminal.signIn.byNumber": "Personnel number and PIN",
  "terminal.signIn.byBadge": "QR badge",
  "terminal.signIn.method": "Way of signing in",
  "terminal.signIn.number": "Personnel number",
  "terminal.signIn.pin": "PIN",
  "terminal.signIn.pinHint": "6 digits",
  "terminal.signIn.badge": "QR badge",
  "terminal.signIn.badgeHint": "Hold the badge to the scanner or read it with the camera.",
  "terminal.signIn.badgeRead": "Badge read.",
  "terminal.signIn.camera": "Read the badge with the camera",
  "terminal.signIn.cameraStop": "Close the camera",
  "terminal.signIn.cameraVideo": "Camera image to read the badge",
  "terminal.signIn.cameraFailed":
    "The camera is not available. Please use the scanner or the personnel number and PIN.",
  "terminal.signIn.submit": "Sign in",
  "terminal.signIn.checking": "Checking …",
  "terminal.signIn.idle": "Without input the sign-in is discarded after 10 seconds.",
  "terminal.signIn.failed": "The sign-in data are not correct.",
  "terminal.signIn.locked": "Too many failed attempts. The sign-in is locked for a few minutes.",
  "terminal.signIn.incomplete":
    "Please enter your personnel number and 6-digit PIN or read your badge (with the PIN where required).",
  "terminal.signIn.unavailable":
    "No connection and no valid offline data. Please clock later or in the app.",
  "terminal.signIn.error": "The sign-in failed. Please try again.",
  "terminal.person.title": "Personnel number {number}",
  "terminal.person.state.Off": "Not clocked in",
  "terminal.person.state.Working": "Working",
  "terminal.person.state.OnBreak": "Break",
  "terminal.person.since": "since {time}",
  "terminal.person.offline":
    "Checked offline. The entry will be sent as soon as there is a connection.",
  "terminal.person.rejected":
    "“{action}” of {time} was not accepted (error code {code}). Please request a correction in the app or contact HR.",
  "terminal.person.actions": "Clock",
  "terminal.person.idle": "Without input the terminal signs out after 10 seconds.",
  "terminal.action.ClockIn": "Clock in",
  "terminal.action.BreakStart": "Break",
  "terminal.action.BreakEnd": "Resume",
  "terminal.action.ClockOut": "Clock out",
  "terminal.signOut": "Sign out",
  "terminal.recorded": "“{action}” recorded at {time}.",
  "terminal.recordFailed": "The entry could not be stored. Please try again.",
  "admin.terminals.title": "Terminals",
  "admin.terminals.intro":
    "A terminal is a shared device (tablet) at the establishment where employees without a work phone clock with their personnel number and PIN or a QR badge. It shows no names; issue the PIN and badge with the person under “Master data”.",
  "admin.terminals.establishment": "Establishment",
  "admin.terminals.name": "Name of the terminal",
  "admin.terminals.nameHint": "For example “Warehouse entrance”",
  "admin.terminals.badgeRequiresPin":
    "QR badge only together with the PIN (recommended, a QR code can be copied)",
  "admin.terminals.create": "Create terminal",
  "admin.terminals.none": "No terminal yet.",
  "admin.terminals.list": "Terminals",
  "admin.terminals.column.name": "Terminal",
  "admin.terminals.column.establishment": "Establishment",
  "admin.terminals.column.status": "Status",
  "admin.terminals.column.lastSeen": "Last active",
  "admin.terminals.column.actions": "Actions",
  "admin.terminals.status.Pending": "Waiting for pairing",
  "admin.terminals.status.Expired": "Code expired",
  "admin.terminals.status.Paired": "Paired",
  "admin.terminals.status.Revoked": "Revoked",
  "admin.terminals.revoke": "Revoke",
  "admin.terminals.revokeNamed": "Revoke terminal “{name}”",
  "admin.terminals.revokeConfirm":
    "The terminal is refused and deletes its data. Entries not sent yet are lost; check “Last active”.",
  "admin.terminals.revoked": "Terminal “{name}” revoked.",
  "admin.terminals.created": "Terminal “{name}” created.",
  "admin.terminals.code.title": "Pairing code for “{name}”",
  "admin.terminals.code.intro":
    "Valid until {time}, usable once and visible only now. Scan the QR code with the tablet, open the link there or paste the code into the terminal.",
  "admin.terminals.code.link": "Link for the tablet",
  "admin.terminals.code.code": "Pairing code",
  "admin.terminals.code.qr": "QR code with the link for the tablet",
  "admin.terminals.code.done": "Done",
  "admin.terminalAccess.title": "Terminal sign-in",
  "admin.terminalAccess.intro":
    "PIN and QR badge for clocking at a terminal. They are shown only when issued; only a hash is stored.",
  "admin.terminalAccess.pin": "PIN",
  "admin.terminalAccess.badge": "QR badge",
  "admin.terminalAccess.issuedAt": "issued on {date}",
  "admin.terminalAccess.none": "none",
  "admin.terminalAccess.lockedUntil":
    "Locked after failed attempts until {time}; a new PIN lifts the lock.",
  "admin.terminalAccess.issuePin": "Create new PIN",
  "admin.terminalAccess.issueBadge": "Create new QR badge",
  "admin.terminalAccess.remove": "Remove terminal sign-in",
  "admin.terminalAccess.removeConfirm": "PIN and QR badge stop working at once.",
  "admin.terminalAccess.removed": "Terminal sign-in removed.",
  "admin.terminalAccess.newPin":
    "New PIN: {pin}. Hand it to the person in person or sealed; it is not shown again.",
  "admin.terminalAccess.newBadge": "New QR badge. Print it now; it is not shown again.",
  "admin.terminalAccess.badgeQr": "QR badge of personnel number {number}",
  "admin.terminalAccess.print": "Print badge",
  "admin.terminalAccess.hide": "Hide",
  "error.TT-1200": "The name of the terminal is missing or longer than 100 characters.",
  "error.TT-1201": "The pairing code is invalid, expired or used already.",
  "error.TT-1202": "The sign-in needs the personnel number and a 6-digit PIN, or the badge.",
  "error.TT-1203": "The terminal is revoked already.",
  "error.TT-3260": "This function is available only to a paired terminal.",
  "error.TT-3261": "The terminal was revoked.",
  "error.TT-3262": "The sign-in data are not correct.",
  "error.TT-3263": "Too many failed attempts. The sign-in is locked for a few minutes.",
  "error.TT-3264": "The person is not employed at the terminal's establishment on this day.",
  "error.TT-3265": "The source “Terminal” is reserved for paired terminals.",
  "error.TT-3266":
    "The entry does not belong to a sign-in at this terminal (person, terminal or time do not match).",
  "error.TT-3267": "The entry recorded offline cannot be attributed to a sign-in at this terminal.",
  "location.basis.LegitimateInterest": "Legitimate interest (Art. 6 (1) (f) GDPR)",
  "location.basis.WorksAgreement": "Works agreement ({reference})",
  "location.notice.title": "Location when clocking",
  "location.notice.purpose":
    "Your employer captures your approximate location once when you clock. Purpose: {purpose}",
  "location.notice.basis": "Legal basis: {basis}.",
  "location.notice.how":
    "It is captured only at the moment of clocking, coarsely (it may be off by a few kilometres) and rounded to about 100 m, never in the background and without routes. The location is stored apart from the clock entry and deleted after {days} days; the clock entry stays. HR and your responsible manager see the coordinates and the distance to the establishment in kilometres, no map.",
  "location.notice.objection":
    "You can object to the capture at any time on grounds relating to your particular situation (Art. 21 (1) GDPR), for example to HR or the data protection officer.",
  "location.notice.privacyLink": "More under Privacy",
  "location.notice.withoutLocation": "Clock without location",
  "location.locating": "Determining the location … The clock entry is already saved.",
  "location.notice.choice":
    "Your device asks for permission. If you refuse or no location is available, you are clocked anyway. You can change the permission in the settings of your device at any time.",
  "location.notice.confirm": "Understood, clock",
  "location.clockHint":
    "Your approximate location is captured once when you clock (purpose: {purpose}). Without permission you are clocked without a location.",
  "location.settings.title": "Location when clocking",
  "location.settings.hint":
    "Off by default. The app asks for the approximate location only at the moment a person clocks (rounded to about 100 m), never in the background, and clocks without permission anyway. The capture is subject to co-determination under § 87 (1) no. 6 BetrVG and needs a data protection impact assessment; purpose and legal basis appear in the information to the employees and in the configuration export.",
  "location.settings.purpose": "Purpose",
  "location.settings.purposeHint":
    "What the location is needed for, for example “proof of field work” (10 to 500 characters).",
  "location.settings.legalBasis": "Legal basis",
  "location.settings.worksAgreement": "Works agreement",
  "location.settings.reference": "Works agreement (title and date)",
  "location.settings.referenceHint": "Appears in the information to the employees.",
  "location.settings.retentionDays": "Delete after days",
  "location.settings.retentionHint":
    "At most {days} days after clocking; you can only shorten the period. The clock entry itself stays.",
  "location.settings.assessment":
    "The data protection impact assessment is done and the works council, if there is one, is involved.",
  "location.settings.save": "Save location setting",
  "location.settings.saved": "Location setting saved.",
  "location.settings.notEntitled":
    "The location when clocking belongs to the function “NFC and location” of the Business and Enterprise plans.",
  "location.settings.inactive":
    "Switched on but not active: the plan no longer contains the function, or purpose and legal basis are missing. The app asks for no location.",
  "location.establishments.title": "Coordinates of the establishments",
  "location.establishments.hint":
    "For the distance of a clocking location to the establishment: latitude and longitude in degrees (for example 48.137 and 11.575), rounded to three decimal places. Empty fields remove the coordinates.",
  "location.establishments.latitude": "Latitude",
  "location.establishments.longitude": "Longitude",
  "location.establishments.save": "Save coordinates",
  "location.establishments.saved": "Coordinates of {name} saved.",
  "location.establishments.invalid":
    "Please enter a number in degrees (for example 48.137) or leave the field empty.",
  "location.day.title": "Location when clocking",
  "location.day.hint":
    "Approximate coordinates and distance to the establishment in whole kilometres per clock entry of the chosen day; the device location is coarse. Every look is logged; no map, no routes, no evaluation.",
  "location.day.person": "Person",
  "location.day.show": "Show locations",
  "location.day.none": "No location was captured on this day.",
  "location.day.distanceBelowOne": "less than 1 km from the establishment",
  "location.day.distanceKilometers": "about {kilometers} km from the establishment",
  "location.day.accuracy.Android":
    "Approximate location of the Android app: may be off by up to about 3 km.",
  "location.day.accuracy.Ios":
    "Approximate location of the iOS app: may be off by several kilometres.",
  "location.day.accuracy.Web":
    "Location of the browser (mostly from Wi-Fi or the mobile network): may be off by several kilometres.",
  "location.day.noEstablishment": "No employment on this day, no distance.",
  "location.day.noEstablishmentCoordinates": "The establishment has no coordinates, no distance.",
  "privacy.location.title": "Location when clocking",
  "privacy.location.off": "Your employer captures no location when you clock.",
  "privacy.location.on":
    "Your employer captures your approximate location once when you clock (coarse, rounded to about 100 m), never in the background. Purpose: {purpose}. Legal basis: {basis}. Deleted after {days} days, apart from the clock entry. Without the permission of your device you are clocked without a location. With legitimate interest you can object to the capture (Art. 21 (1) GDPR).",
  "error.TT-1190": "Please state the purpose of the location capture (10 to 500 characters).",
  "error.TT-1191": "Locations are kept at most 60 days; please choose 1 to 60 days.",
  "error.TT-1192": "Please name the works agreement (title and date, at most 200 characters).",
  "error.TT-3250":
    "Please confirm the data protection impact assessment and the involvement of the works council before switching the location on.",
  "geofence.title": "Reminder at the site",
  "geofence.titleShiftStart": "Reminder at the start of a shift",
  "geofence.consent.regions":
    "If you switch the reminder on, your device recognises when you enter the area of your establishment and reminds you to clock in – only on days you work according to your work time model or roster, and only if you have not clocked in yet. Your location is evaluated on this device only; it is neither sent to Zeitavo nor to your employer, and it is not stored. Nothing is ever clocked automatically.",
  "geofence.consent.background":
    "For this, Zeitavo uses your precise location in the background too, that is while the app is closed or not in use. Android asks for it; choose „Allow all the time“ there. If you leave the site while clocked in, the app asks „Still clocked in?“.",
  "geofence.consent.ios":
    "iOS reminds you when you arrive without starting the app; the permission „While using the app“ is enough. iOS cannot remind you when you leave.",
  "geofence.consent.shiftStart":
    "If you switch the reminder on, the app reminds you to clock in at the start of your published shifts if you have not clocked in yet. Your location is not used for this.",
  "geofence.consent.voluntary":
    "The reminder is voluntary. You can switch it off here at any time without any disadvantage; your employer does not learn whether you use it.",
  "geofence.enable": "Switch reminder on",
  "geofence.disable": "Switch reminder off",
  "geofence.state.on": "The reminder is switched on on this device.",
  "geofence.hint.notifications":
    "Notifications are switched off for Zeitavo; without them no reminder appears.",
  "geofence.hint.location":
    "Location is not allowed for Zeitavo; without it your device does not remind you at the site.",
  "geofence.hint.background":
    "Location in the background is not allowed. Until you choose „Allow all the time“, the app reminds you only at the start of your published shifts.",
  "geofence.hint.precise":
    "With the approximate location iOS may not remind you. Switch on „Precise Location“ in the settings if you want reliable reminders.",
  "geofence.hint.noRegion":
    "No area is set for your establishment yet; your HR department can enter it.",
  "geofence.hint.noShift": "No shift of yours is published at the moment.",
  "geofence.openSettings": "Open settings",
  "geofence.tenant.title": "Reminder at the site",
  "geofence.tenant.hint":
    "Off by default. Employees can switch on in the apps that their device reminds them to clock in when they enter their establishment (only on working days and before shifts). The location is evaluated on the device only; neither Zeitavo nor you learn it or who uses the reminder, and nothing is ever clocked automatically. The function is subject to co-determination under § 87 (1) no. 6 BetrVG. You set the area per establishment as a radius with the coordinates.",
  "geofence.tenant.enabled": "Offer the reminder at the site",
  "geofence.tenant.agreement":
    "The reminder is agreed with the works council, if there is one, and employees are told that it is voluntary and can be switched off at any time without any disadvantage.",
  "geofence.tenant.save": "Save setting",
  "geofence.tenant.saved": "Setting of the reminder at the site saved.",
  "geofence.tenant.notEntitled":
    "The reminder at the site belongs to the function „NFC and location“ of the Business and Enterprise plans.",
  "geofence.tenant.inactive":
    "Switched on but not active: the plan no longer contains the function. The apps offer no reminder.",
  "geofence.radius.label": "Radius of the reminder for {name} (metres)",
  "geofence.radius.hint":
    "100 to 1000 m around the coordinates. Empty: no reminder at this establishment.",
  "geofence.radius.save": "Save radius",
  "geofence.radius.saved": "Radius of {name} saved.",
  "geofence.radius.invalid":
    "Please enter a whole number from 100 to 1000 or leave the field empty.",
  "error.TT-1193": "The radius of the reminder is 100 to 1000 metres.",
  "error.TT-3251":
    "Please confirm the agreement with the works council and that employees are informed before switching the reminder at the site on.",
  "error.TT-3252": "A radius needs the coordinates of the establishment first.",
  "nfc.clock.title": "Clock at an NFC tag",
  "nfc.clock.hint":
    "Choose the step and hold your phone to the NFC tag at your workplace. Only online, without a location.",
  "nfc.clock.action": "{action} at the NFC tag",
  "nfc.clock.disabled": "NFC is switched off. Please switch NFC on in the settings of the phone.",
  "nfc.clock.pending": "Saved clock entries are sent first; then you can clock at the NFC tag.",
  "nfc.clock.prompt": "{action}: hold your iPhone to the NFC tag at your workplace.",
  "nfc.clock.reading": "Hold your phone to the NFC tag now …",
  "nfc.clock.notRead": "No NFC tag was read. Please try again and hold the phone still at the tag.",
  "nfc.clock.failed": "Clocking at the NFC tag failed. Please try again or clock without the tag.",
  "nfc.clock.recorded": "{action} at {time} saved at the NFC tag “{tag}”.",
  "admin.nfc.title": "NFC tags and chips",
  "admin.nfc.intro":
    "The server checks workplace tags (NTAG 424 DNA with SUN message) at every step; personal chips (MIFARE DESFire EV3) replace the QR badge at an Android terminal, and the PIN stays required as the terminal is set. UID and key are never shown.",
  "admin.nfc.create": "Register tag",
  "admin.nfc.notEntitled":
    "New tags and chips can be registered only with the feature “NFC and location”. Existing tags can still be blocked.",
  "admin.nfc.establishment": "Establishment",
  "admin.nfc.kind": "Kind",
  "admin.nfc.kind.Workplace": "Workplace tag",
  "admin.nfc.kind.PersonalBadge": "Personal chip",
  "admin.nfc.name": "Name",
  "admin.nfc.nameHint": "for example “Warehouse entrance” or “Chip 12” (at most 100 characters)",
  "admin.nfc.uid": "UID",
  "admin.nfc.uidHint":
    "4, 7 or 10 bytes in hexadecimal notation, for example 04:DE:5F:1E:AC:C0:40; only a hash is stored.",
  "admin.nfc.sunKey": "SUN key (SDM file read key)",
  "admin.nfc.sunKeyHint": "32 hexadecimal digits; stored encrypted and never shown again.",
  "admin.nfc.employee": "Person",
  "admin.nfc.created": "Tag “{name}” registered.",
  "admin.nfc.blocked": "Tag “{name}” blocked.",
  "admin.nfc.none": "No NFC tags yet.",
  "admin.nfc.list": "NFC tags",
  "admin.nfc.column.name": "Name",
  "admin.nfc.column.kind": "Kind",
  "admin.nfc.column.establishment": "Establishment",
  "admin.nfc.column.state": "State",
  "admin.nfc.column.lastUsed": "Last clocked",
  "admin.nfc.column.actions": "Actions",
  "admin.nfc.state.active": "Active",
  "admin.nfc.state.blocked": "Blocked",
  "admin.nfc.block": "Block",
  "admin.nfc.blockNamed": "Block tag “{name}”",
  "admin.nfc.blockConfirm":
    "A blocked tag never clocks again. It cannot be unblocked, only registered anew.",
  "terminal.signIn.byChip": "NFC chip",
  "terminal.signIn.chipHint": "Hold your chip to the device.",
  "terminal.signIn.chipRead": "Chip read.",
  "terminal.signIn.chipFailed": "The chip is unknown or blocked, or the PIN is wrong.",
  "terminal.signIn.chipOffline":
    "Signing in with a chip needs a connection. Please sign in with personnel number and PIN.",
  "error.TT-1220": "The UID of a tag has 4, 7 or 10 bytes in hexadecimal notation.",
  "error.TT-1221": "The SUN key has 32 hexadecimal digits (AES-128).",
  "error.TT-1222": "The name of the tag is missing or longer than 100 characters.",
  "error.TT-1223": "An active tag with this UID is registered already. Block it first.",
  "error.TT-1224": "The tag is blocked already.",
  "error.TT-1225": "The NFC tag carries no valid SUN message. Nothing was recorded.",
  "error.TT-1226":
    "A workplace tag needs a SUN key and no person, a personal chip a person and no key.",
  "error.TT-3280":
    "This NFC tag could not be verified as a tag of your employer. Nothing was recorded.",
  "error.TT-3281": "This NFC tag is blocked. Nothing was recorded.",
  "error.TT-3283":
    "This message of the NFC tag was used already. Please hold the phone to the tag again.",
  "error.TT-3284": "Only the server sets the source “NFC tag” after checking a tag.",
  "error.TT-3285":
    "The tag belongs to an establishment you are not employed at today. Please clock without the tag.",
  // Rostering (T-176, ADR-0146).
  "nav.roster": "Rostering",
  "roster.inactive": "Rostering is not switched on for your company.",
  "roster.notPlanner": "You do not create rosters. HR grants the right „create rosters“.",
  "roster.intro":
    "Shift templates and staffing requirements are the basis of rosters. A roster is a plan: only what is clocked is recorded.",
  "roster.establishment": "Establishment",
  "roster.allEstablishments": "All establishments",
  "roster.team": "Team",
  "roster.wholeEstablishment": "Whole establishment",
  "roster.start": "Start",
  "roster.end": "End",
  "roster.endHint": "An end before the start lies on the next day.",
  "roster.validFrom": "Valid from",
  "roster.validTo": "Valid until",
  "roster.validity": "Validity",
  "roster.validSince": "from {from}",
  "roster.validBetween": "{from} to {to}",
  "roster.time": "Time",
  "roster.timeSameDay": "{start}–{end}",
  "roster.timeNextDay": "{start}–{end} (next day)",
  "roster.minutes": "{minutes} min",
  "roster.actions": "Actions",
  "roster.edit": "Edit",
  "roster.delete": "Delete",
  "roster.settings.title": "Rostering",
  "roster.settings.hint":
    "Off by default. Rosters and shift plans are subject to co-determination (§ 87 (1) no. 2 and 3 BetrVG). The planning principles are part of the configuration export for the works agreement.",
  "roster.settings.notEntitled": "Rostering is not part of your plan.",
  "roster.settings.enabled": "Switch rostering on",
  "roster.settings.leadTime": "Lead time in calendar days",
  "roster.settings.leadTimeHint":
    "Publishing or changing a plan at shorter notice shows a notice, never a lock ({min} to {max} days; usual without a collective or works agreement: 4).",
  "roster.settings.colleagueNames":
    "Employees see the names of their colleagues in the published plan",
  "roster.settings.swapAutoApproval":
    "Take over shift swaps without plan notice without the planners' approval",
  "roster.settings.planActualPerPerson":
    "Plan/actual comparison with figures per person and per day (only with a works agreement; otherwise sums of the period only, never rankings)",
  "roster.settings.planActualMinimumGroup":
    "Minimum group for sums in the plan/actual comparison (persons)",
  "roster.settings.planActualMinimumGroupHint":
    "Without figures per person the comparison shows sums only from this many different persons on (2 to 50, usually 5), so no sum points to one person.",
  "roster.settings.codetermination":
    "I am aware that rosters are subject to the co-determination of the works council, and the works council is involved if there is one.",
  "roster.settings.save": "Save rostering",
  "roster.settings.saved": "Rostering saved.",
  "roster.compare.title": "Plan/actual comparison",
  "roster.compare.hint":
    "Published shifts against the recorded working time of an establishment or team. Deviations of begin and end count only beyond the tolerance; shifts that have not ended are not compared. No rankings; figures per person only if the tenant switched them on by works agreement.",
  "roster.compare.team": "Team",
  "roster.compare.allRosters": "All rosters of the establishment you plan",
  "roster.compare.from": "From",
  "roster.compare.to": "To",
  "roster.compare.tolerance": "Tolerance in minutes",
  "roster.compare.toleranceHint":
    "Deviations up to this duration do not count (0 to {max} minutes).",
  "roster.compare.show": "Compare",
  "roster.compare.suppressed.one":
    "{count} person has a compared shift in the period (ended, without absence), fewer than the minimum group of {minimum}. Without figures per person no sums are shown, so they do not point to single persons.",
  "roster.compare.suppressed.other":
    "{count} persons have a compared shift in the period (ended, without absence), fewer than the minimum group of {minimum}. Without figures per person no sums are shown, so they do not point to single persons.",
  "roster.compare.shiftCount.one": "{count} shift",
  "roster.compare.shiftCount.other": "{count} shifts",
  "roster.compare.personCount.one": "{count} person",
  "roster.compare.personCount.other": "{count} persons",
  "roster.compare.total.shifts": "Compared shifts",
  "roster.compare.total.shiftsValue": "{shifts} of {persons}; {pending} not ended yet",
  "roster.compare.total.time": "Working time",
  "roster.compare.total.timeValue":
    "planned {planned} h, recorded {recorded} h, recorded without a shift {unplanned} h",
  "roster.compare.total.deviations": "Deviations beyond {minutes} minutes",
  "roster.compare.total.deviationsValue":
    "{missed} without recording; begin {late}× later, {early}× earlier; end {earlyEnd}× earlier, {lateEnd}× later",
  "roster.compare.total.notCompared": "Not compared",
  "roster.compare.total.notComparedValue":
    "{absent} on days of an absence, {unevaluable} whose recording cannot be evaluated",
  "roster.compare.days": "Days",
  "roster.compare.daysCaption": "Plan/actual comparison per day",
  "roster.compare.day": "Day",
  "roster.compare.persons": "Persons",
  "roster.compare.personsCaption": "Plan/actual comparison per person",
  "roster.compare.person": "Person",
  "roster.compare.personsHint": "Ordered by name; the table cannot be sorted by figures.",
  "roster.compare.perPersonOff":
    "Figures per day and per person are not switched on in this tenant; only the sums of the period are shown.",
  "roster.compare.noShifts": "There are no published shifts in this period.",
  "roster.compare.shifts": "Shifts",
  "roster.compare.planned": "Planned (h)",
  "roster.compare.recorded": "Recorded (h)",
  "roster.compare.unplanned": "Without a shift (h)",
  "roster.compare.missed": "Not recorded",
  "roster.compare.begin": "Begin deviations",
  "roster.compare.end": "End deviations",
  "roster.compare.notCompared": "Not compared",
  "roster.templates.title": "Shift templates",
  "roster.templates.hint":
    "Templates apply to all establishments or one establishment. A template is never deleted; it ends with its last day. Planned shifts keep their times.",
  "roster.templates.empty": "No shift templates yet.",
  "roster.templates.new": "New shift template",
  "roster.templates.edit": "Edit shift template",
  "roster.templates.editNamed": "Edit shift template {name}",
  "roster.templates.name": "Name",
  "roster.templates.break": "Break in minutes",
  "roster.templates.breakInvalid": "Please enter the break in whole minutes (0 to 1440).",
  "roster.templates.save": "Save shift template",
  "roster.templates.saved": "Shift template saved.",
  "roster.requirements.title": "Staffing requirements",
  "roster.requirements.hint":
    "How many persons are needed at least, per weekday or on a date. Planning shows deviations as a notice.",
  "roster.requirements.empty": "No staffing requirements yet.",
  "roster.requirements.new": "New staffing requirement",
  "roster.requirements.edit": "Edit staffing requirement",
  "roster.requirements.editNamed": "Edit staffing requirement {day}",
  "roster.requirements.deleteNamed": "Delete staffing requirement {day}",
  "roster.requirements.deleteConsequence":
    "The staffing requirement is deleted. Existing plans stay unchanged.",
  "roster.requirements.deleteConfirm": "Delete staffing requirement",
  "roster.requirements.deleted": "Staffing requirement deleted.",
  "roster.requirements.day": "Day",
  "roster.requirements.weekly": "Every week",
  "roster.requirements.once": "On a date",
  "roster.requirements.weekday": "Weekday",
  "roster.requirements.date": "Date",
  "roster.requirements.every": "every {weekday}",
  "roster.requirements.minimum": "Minimum staff",
  "roster.requirements.save": "Save staffing requirement",
  "roster.requirements.saved": "Staffing requirement saved.",
  "roster.planners.title": "Planners",
  "roster.planners.hint":
    "Who creates rosters for an establishment or a team. The right does not pass to deputies. HR plans for its establishments.",
  "roster.planners.empty": "Nobody creates rosters yet.",
  "roster.planners.new": "Grant the right „create rosters“",
  "roster.planners.person": "Person",
  "roster.planners.choosePerson": "Choose a person",
  "roster.planners.otherEstablishment": "Person now employed at another establishment",
  "roster.planners.grant": "Grant right",
  "roster.planners.granted": "Right granted.",
  "roster.planners.revoke": "Revoke",
  "roster.planners.revokeNamed": "Revoke the right of {name}",
  "roster.planners.revokeConsequence":
    "The right ends yesterday; a right that has not begun is deleted.",
  "roster.planners.revokeConfirm": "Revoke right",
  "roster.planners.revoked": "Right revoked.",
  "entitlement.ProjectBilling": "Hourly rates and billing",
  "entitlement.Rostering": "Rostering",
  "error.TT-1210":
    "Start and end give no shift. Please check the times (whole minutes, at most one day).",
  "error.TT-1211": "The break must be shorter than the shift.",
  "error.TT-1212": "Please choose either a weekday or a date.",
  "error.TT-1213": "The minimum staff lies between 1 and 999 persons.",
  "error.TT-1214": "A roster covers a closed period of at most 93 days.",
  "error.TT-1215": "Two shifts of the same person overlap.",
  "error.TT-1216": "The shift lies outside the person's employment at this establishment.",
  "error.TT-1217": "This version of the roster is published. A change starts a new version.",
  "error.TT-1218": "The shift begins outside the period of the roster.",
  "error.TT-1219": "The lead time lies between 1 and 60 calendar days.",
  "error.TT-3270": "Rostering is switched off.",
  "error.TT-3271":
    "Please acknowledge the co-determination of the works council before switching rostering on.",
  "error.TT-3272": "You do not create rosters for this establishment or team.",
  "error.TT-3273":
    "The person already holds the right for this establishment and team in an overlapping period.",
  "error.TT-3275":
    "Another person changed the roster in the meantime. Please reload it and repeat the change.",
  "error.TT-3274": "The team belongs to another establishment.",
  // Planning view and rule check of rosters (T-177, ADR-0148).
  "roster.plan.title": "Roster",
  "roster.plan.hint":
    "The rule check shows notices per person and day together with the working time recorded before – they are not violations.",
  "roster.plan.rosters": "Rosters",
  "roster.plan.noRosters": "This establishment has no roster you plan yet.",
  "roster.plan.period": "{from} to {to}",
  "roster.plan.status.Draft": "Draft",
  "roster.plan.status.Published": "Published",
  "roster.plan.open": "Open",
  "roster.plan.openNamed": "Open roster {period}",
  "roster.plan.new": "New roster",
  "roster.plan.periodStart": "First day",
  "roster.plan.periodEnd": "Last day",
  "roster.plan.periodHint": "At most 93 days.",
  "roster.plan.create": "Create roster",
  "roster.plan.created": "Roster created.",
  "roster.plan.range": "View",
  "roster.plan.week": "Week",
  "roster.plan.month": "Month",
  "roster.plan.previous.week": "Previous week",
  "roster.plan.next.week": "Next week",
  "roster.plan.previous.month": "Previous month",
  "roster.plan.next.month": "Next month",
  "roster.plan.gridLabel": "Roster: persons and days",
  "roster.plan.gridHint": "Use the arrow keys to move between cells; Enter or Space opens a cell.",
  "roster.plan.person": "Person",
  "roster.plan.openShifts": "Open shifts",
  "roster.plan.noticeCount.one": "{count} notice",
  "roster.plan.noticeCount.other": "{count} notices",
  "roster.plan.breachCount.one": "{count} rule breach",
  "roster.plan.breachCount.other": "{count} rule breaches",
  "roster.plan.cellNotices": "In this cell: {summary}.",
  "roster.plan.cellNoNotices": "This cell has no notices.",
  "roster.plan.cellTitle": "{name}, {day}",
  "roster.plan.notices": "Notices of the rule check",
  "roster.plan.noNotices": "No notices.",
  "roster.plan.ruleId": "Rule ID {ruleId}",
  "roster.plan.rule.ROSTER-NOT-PLANNABLE": "Cannot be planned",
  "roster.plan.rule.ROSTER-ABSENT": "Absent",
  "roster.plan.rule.ROSTER-OVERLAP": "Overlaps another shift of the person",
  "roster.plan.rule.ROSTER-STAFFING": "Staffing below the requirement",
  "roster.plan.staffingValues": "planned {planned} · required {minimum}",
  "roster.plan.notPlannableHint": "Only HR knows the reason.",
  "roster.plan.severity.Breach": "Breaks a rule if worked like this",
  "roster.plan.severity.Attention": "Please check",
  "roster.plan.shifts": "Shifts",
  "roster.plan.noShifts": "No shift.",
  "roster.plan.editShift": "Edit shift {times}",
  "roster.plan.deleteShift": "Delete shift {times}",
  "roster.plan.deleteConsequence": "The shift is deleted from the draft.",
  "roster.plan.deleteConfirm": "Delete shift",
  "roster.plan.deleted": "Shift deleted.",
  "roster.plan.newShift": "New shift",
  "roster.plan.changeShift": "Change shift",
  "roster.plan.kind": "Kind of shift",
  "roster.plan.fromTemplate": "From template",
  "roster.plan.free": "Free times",
  "roster.plan.template": "Shift template",
  "roster.plan.noTemplates": "No shift template applies on this day.",
  "roster.plan.openShift": "Open shift (no person)",
  "roster.plan.breakHint": "Empty: the template's break, none for free times.",
  "roster.plan.note": "Note for the person",
  "roster.plan.saveShift": "Save shift",
  "roster.plan.saved": "Shift saved.",
  "roster.plan.copyTitle": "Copy week",
  "roster.plan.copyHint":
    "Copies all shifts of a week with the same times. Shifts of persons not employed on the new day and notes for the person are not copied; shifts already planned in the target week are not added again.",
  "roster.plan.copyFrom": "From the week starting",
  "roster.plan.copyTo": "To the week starting",
  "roster.plan.copy": "Copy week",
  "roster.plan.copied":
    "{copied} shifts copied, {skipped} left out, {alreadyPlanned} were already planned.",
  "roster.plan.publishedHint": "This version is published and cannot be changed here.",
  "roster.plan.close": "Close cell",
  "nav.myRoster": "My roster",
  "myRoster.own": "Your shift",
  "myRoster.open": "Open shift",
  "myRoster.colleague": "Colleague",
  "myRoster.break": "{minutes} min break planned",
  "myRoster.fromDevice": "Offline – published roster as of {time}",
  "myRoster.state": "Published roster as of {time}",
  "myRoster.next": "Your next shift",
  "myRoster.days": "The next four weeks",
  "myRoster.empty": "No shifts are published for the next four weeks.",
  "myRoster.target": "Target from the roster: {hours} h",
  "myRoster.targetTotal":
    "Target from the roster from today: {hours} h (without public holidays and absences; the balance is what counts)",
  "myRoster.hint":
    "The roster is a plan. You keep recording your working time by clocking in and out.",
  "clock.nextShift": "Next shift according to the roster: {date}, {times}.",
  "clock.myRosterLink": "My roster",
  "roster.publish.title": "Publish",
  "roster.publish.draftHint":
    "Only planners see the draft. Employees see their shifts and are notified once it is published.",
  "roster.publish.draftHintRevision":
    "Until it is published, employees keep seeing the last published version.",
  "roster.publish.open": "Publish …",
  "roster.publish.codetermination":
    "Rosters are subject to the co-determination of the works council (section 87 (1) nos. 2 and 3 BetrVG). Only publish rosters drawn up according to the agreed principles.",
  "roster.publish.changes": "New: {added}, changed: {changed}, removed: {removed}.",
  "roster.publish.affected":
    "Only persons concerned with shifts that have not ended are notified: {count} (not you).",
  "roster.publish.changeList": "Changes against the published version",
  "roster.publish.kind.Added": "New",
  "roster.publish.kind.Changed": "Changed",
  "roster.publish.kind.Removed": "Removed",
  "roster.publish.before": "before {date}, {times}",
  "roster.publish.beforePerson": "before {date}, {times} · {name}",
  "roster.publish.moreChanges.one": "And {count} more change.",
  "roster.publish.moreChanges.other": "And {count} more changes.",
  "roster.publish.notices":
    "The check shows {notices}. You can still publish if you give a reason.",
  "roster.publish.noticesWithBreaches":
    "The check shows {notices}, including {breaches}. You can still publish if you give a reason.",
  "roster.publish.shortNotice":
    "Lead time not met: fewer than {days} full calendar days lie between today and the day of the shift (neither counts). This is a notice, not a lock.",
  "roster.publish.shortNoticeItem.one": "{date} (lead time {count} full calendar day): {name}",
  "roster.publish.shortNoticeItem.other": "{date} (lead time {count} full calendar days): {name}",
  "roster.publish.hiddenPerson": "Person outside your view",
  "roster.publish.reason": "Reason",
  "roster.publish.reasonHint":
    "For example the occasion of a short-term change. No health data and no names of others.",
  "roster.publish.reasonHintRequired":
    "Required while the check shows notices; it is stored with the version. No health data and no names of others.",
  "roster.publish.reasonMissing": "Give a reason, the check shows notices.",
  "roster.publish.reasonTooLong": "The reason has at most 500 characters.",
  "roster.publish.confirm": "Publish now",
  "roster.publish.done": "Version {version} published. {count} persons are notified.",
  "roster.publish.publishedHint":
    "This version is published. To change it, start a new version; this version stays in force until the new one is published.",
  "roster.publish.revise": "Edit a new version",
  "roster.publish.revised": "Version {version} is a draft.",
  "roster.publish.versions": "Published versions",
  "roster.publish.version": "Version",
  "roster.publish.publishedAt": "Published at",
  "roster.publish.publishedBy": "By",
  "roster.publish.changeColumn": "Changes",
  "roster.publish.noticeColumn": "Notices",
  "roster.publish.noticeCell": "{notices}, {short} with short notice",
  "roster.publish.notShown": "not shown",
  "roster.publish.reasonGiven": "Reason given",
  "availability.title": "My availability",
  "availability.hint":
    "Tell the planners when you can, cannot or would like to work. Only you and the planners of your team or establishment see your availability, colleagues do not. It is a notice for planning, never a lock.",
  "availability.empty": "You have not entered any availability.",
  "availability.add": "Add availability",
  "availability.new": "New availability",
  "availability.edit": "Change availability",
  "availability.kind": "Kind",
  "availability.kind.Available": "Available",
  "availability.kind.Unavailable": "Unavailable",
  "availability.kind.Preferred": "Preferred",
  "availability.date": "Day",
  "availability.wholeDay": "Whole day",
  "availability.wholeDayShort": "whole day",
  "availability.weekly": "Repeat every week",
  "availability.until": "Repeat until",
  "availability.untilHint": "At most one year after the first day.",
  "availability.note": "Note for the planners",
  "availability.noteHint": "For example a fixed appointment. No health data.",
  "availability.save": "Save availability",
  "availability.saved": "Availability saved.",
  "availability.item": "{date}, {time}",
  "availability.weeklyUntil": "weekly until {date}",
  "availability.editItem": "Change availability {item}",
  "availability.deleteItem": "Delete availability {item}",
  "availability.deleteConsequence": "The planners no longer see this availability.",
  "availability.deleteConfirm": "Delete availability",
  "availability.deleted": "Availability deleted.",
  "availability.cellTitle": "Availability",
  "availability.error.date": "Choose a day.",
  "availability.error.times": "Enter a begin and an end; they must differ.",
  "availability.error.until":
    "The repetition ends on or after the first day and at most one year later.",
  "availability.error.note": "The note has at most 500 characters.",
  "shiftRequest.title": "Swaps and open shifts",
  "shiftRequest.hint":
    "You can hand over your own shifts and apply for open shifts of your team. Every takeover is checked against the working time rules first; planners usually approve it. The roster only changes after the approval.",
  "shiftRequest.offers": "Offers from your team",
  "shiftRequest.noOffers": "Nobody offers a shift at the moment.",
  "shiftRequest.mine": "Your requests",
  "shiftRequest.noRequests": "You have no open or recently decided requests.",
  "shiftRequest.handOver": "Hand over",
  "shiftRequest.handOverItem": "Hand over shift {times}",
  "shiftRequest.apply": "Take over",
  "shiftRequest.applyItem": "Apply for the open shift {times}",
  "shiftRequest.target": "To whom",
  "shiftRequest.targetHint": "An offer to the team can be accepted by anybody of your team.",
  "shiftRequest.toTeam": "To the team",
  "shiftRequest.toTeamOnly":
    "The shift is offered to your team; anybody of your team can accept it.",
  "shiftRequest.sendHandover": "Offer shift",
  "shiftRequest.sendApplication": "Send application",
  "shiftRequest.sentHandover": "The shift is offered.",
  "shiftRequest.sentApplication": "Your application is sent; the planners decide.",
  "shiftRequest.accept": "Accept …",
  "shiftRequest.acceptConfirm": "Accept bindingly",
  "shiftRequest.decline": "Decline",
  "shiftRequest.declined": "Request declined.",
  "shiftRequest.withdraw": "Withdraw",
  "shiftRequest.withdrawn": "Request withdrawn.",
  "shiftRequest.offline":
    "Without a connection you can only view requests and availability, not change them.",
  "shiftRequest.acceptItem": "Accept shift {shift}",
  "shiftRequest.declineItem": "Decline the request about shift {shift}",
  "shiftRequest.withdrawItem": "Withdraw the request about shift {shift}",
  "shiftRequest.approveItem": "Approve the request about shift {shift}",
  "shiftRequest.rejectItem": "Reject the request about shift {shift}",
  "shiftRequest.shiftGone": "Shift no longer in the roster",
  "shiftRequest.kind.Application": "Application for an open shift",
  "shiftRequest.kind.Offer": "Offer to the team",
  "shiftRequest.kind.Handover": "Handover to {name}",
  "shiftRequest.from": "from {name}",
  "shiftRequest.stale":
    "The shift changed since the request; the request can only be withdrawn or rejected.",
  "shiftRequest.reason": "Reason: {reason}",
  "shiftRequest.status.Open": "Waiting for acceptance",
  "shiftRequest.status.Accepted": "Waiting for approval",
  "shiftRequest.status.Approved": "Approved",
  "shiftRequest.status.Rejected": "Rejected",
  "shiftRequest.status.Withdrawn": "Withdrawn",
  "shiftRequest.status.Superseded": "Closed, the shift was given otherwise or changed",
  "shiftRequest.approval.Required": "Planners approve the takeover.",
  "shiftRequest.approval.Automatic":
    "The takeover is approved without planners because the check shows no notice.",
  "shiftRequest.approval.OnAcceptance":
    "After the acceptance, the takeover is approved by planners or automatically, depending on the rules of your employer.",
  "shiftRequest.check.noNotices": "The check shows no notice for you.",
  "shiftRequest.check.notices":
    "The check shows these notices for you. You can still send the request; planners decide then.",
  "shiftRequest.check.shortNotice.one":
    "Short notice: {count} full calendar day lies between today and the shift, fewer than the agreed {days}. This is a notice, never a lock.",
  "shiftRequest.check.shortNotice.other":
    "Short notice: {count} full calendar days lie between today and the shift, fewer than the agreed {days}. This is a notice, never a lock.",
  "shiftRequest.decision.reason": "Reason",
  "shiftRequest.decision.reasonHint":
    "Shown to the persons taking part. No health data and no names of others.",
  "shiftRequest.decision.reasonHintRequired":
    "Required to approve while the check shows notices; shown to the persons taking part. No health data and no names of others.",
  "shiftRequest.decision.reasonMissing": "Give a reason, the check shows notices.",
  "shiftRequest.approve": "Approve",
  "shiftRequest.reject": "Reject",
  "shiftRequest.approved":
    "Approved. A new version of the roster is published; both persons are notified.",
  "shiftRequest.rejected": "Rejected. The persons taking part are notified.",
  "shiftRequest.planner.title": "Requests about shifts",
  "shiftRequest.planner.hint":
    "Handovers and applications of employees. An approval publishes a new version with a change list; requests you take part in are decided by another planner.",
  "shiftRequest.planner.empty": "No pending requests.",
  "shiftRequest.planner.application": "Application by {name}",
  "shiftRequest.planner.handover": "{from} hands over to {to}",
  "shiftRequest.planner.team": "the team (nobody accepted yet)",
  "shiftRequest.planner.shortNotice":
    "Short notice: the reassignment lies within the lead time. This is a notice, never a lock.",
  "shiftRequest.planner.waiting": "Waiting for a colleague to accept.",
  "error.TT-1232":
    "A weekly availability ends on or after its first day and at most one year later.",
  "error.TT-1233": "This request about a shift is not possible like this.",
  "error.TT-1234": "The shift has already begun and can no longer be taken over.",
  "error.TT-1235": "A request you take part in is decided by another planner.",
  "error.TT-3276": "You cannot handle this request.",
  "error.TT-3277": "The roster is being revised. Decide the request after the publication.",
  "error.TT-3278":
    "The shift changed since the request. The request can only be withdrawn or rejected.",
  "error.TT-3279": "There is already a pending request about this shift.",
  "error.TT-3294": "The check shows notices. Give a reason to approve the takeover anyway.",
  "error.TT-3295": "Colleagues' names are not shown in your tenant. Offer the shift to the team.",
  "error.TT-1236": "The target from the roster is available for shift models only.",
  "error.TT-1237":
    "The target from the roster is switched on or off from the first day of a month.",
  "error.TT-1238": "The tolerance lies between 0 and 120 minutes.",
  "error.TT-1239": "The minimum group lies between 2 and 50 persons.",
  "error.TT-3296":
    "The change would reach past days. The target from the roster changes only from a month that has not begun.",
  "error.TT-3293": "The check shows notices. Give a reason to publish the roster anyway.",
  "error.TT-1230": "A week is copied to another week, whole weeks forward or back.",
  "error.TT-1231": "The shift template does not apply on this day.",
  "error.TT-3290": "This establishment and team already have a roster in the period.",
  "error.TT-3291": "The shift template belongs to another establishment.",
  "error.TT-3292":
    "The person does not belong to the team or establishment of the roster on this day.",
  "datev.program.Generic": "Other payroll program (CSV)",
  "datev.genericHint":
    "For other payroll programs (for example Lexware lohn+gehalt or ADDISON) you get a CSV file with a header and the chosen columns. Consultant and client number are not needed.",
  "datev.category.OvertimePayoutHours":
    "Overtime paid out from the overtime account (payout bookings; beside the increase only with switch months)",
  "datev.category.VacationHours": "Vacation hours",
  "datev.category.SickHours": "Sick hours (with rehabilitation)",
  "datev.category.SpecialLeaveHours": "Special leave hours",
  "datev.category.TrainingHours": "Training hours",
  "datev.category.EducationLeaveHours": "Education leave hours",
  "datev.exclusiveHint":
    "Never twice: the increase and the overtime paid out together only with switch months (each month transfers exactly one of them), either the absence hours in total or per kind, an absence kind either in the calendar or as hours or days. Flexitime payouts have their own wage type.",
  "datev.surcharges": "Surcharge hours",
  "datev.surchargesHint":
    "The hours per surcharge kind come from the surcharge profile. Surcharges in the payroll export belong to the DATEV payroll export of the plan.",
  "datev.column.surcharge": "Surcharge kind",
  "datev.transferSurcharge": "Transfer surcharge {code}",
  "datev.surchargeWageTypeFor": "Wage type for surcharge {code}",
  "datev.surchargeKeyFor": "Processing key for surcharge {code}",
  "datev.calendar": "Absences as calendar",
  "datev.calendarHint":
    "Days of these absence kinds are transferred per day with outage key and hours instead of hours or days per month. LODAS and other payroll programs only; the payroll office names the outage key.",
  "datev.column.absenceKind": "Absence kind",
  "datev.column.outageKey": "Outage key",
  "datev.calendarFor": "Transfer {kind} as calendar",
  "datev.outageKeyFor": "Outage key for {kind}",
  "datev.costCenters": "Cost centers per establishment",
  "datev.costCentersHint":
    "Every value of a person carries the cost center of the establishment the person last belongs to in the month. Leave empty for none.",
  "datev.column.establishment": "Establishment",
  "datev.column.costCenter": "Cost center",
  "datev.costCenterFor": "Cost center for {establishment}",
  "datev.columns": "Columns of the CSV file",
  "datev.columnFor": "Write column {column}",
  "datev.columnUp": "Move {column} forward",
  "datev.columnDown": "Move {column} back",
  "datev.columnName.PersonnelNumber": "Personnel number",
  "datev.columnName.Period": "Period",
  "datev.columnName.WageType": "Wage type",
  "datev.columnName.Quantity": "Quantity",
  "datev.columnName.Unit": "Unit",
  "datev.columnName.CostCenter": "Cost center",
  "datev.columnName.Date": "Date",
  "datev.columnName.OutageKey": "Outage key",
  "datev.columnName.Year": "Year",
  "datev.columnName.Month": "Month",
  "datev.downloadGeneric": "Download payroll export (CSV)",
  "datev.check": "Show check log",
  "datev.checkTitle": "Check log {month}",
  "datev.provisional":
    "The month is not closed for every person or surcharges could not be computed: the file is provisional and has “vorlaeufig” in its name.",
  "datev.final":
    "The month is closed: the file is final. The same data and settings give the same file; leave payouts, leave days of the leave account, settings and master data can still change.",
  "datev.checkTotals": "Totals",
  "datev.column.value": "Value",
  "datev.column.quantity": "Quantity",
  "datev.column.records": "Records",
  "datev.column.person": "Person",
  "datev.column.previous": "Last file",
  "datev.column.current": "Now",
  "datev.withoutValues": "Without values in the month: {persons}",
  "datev.surchargeFailures":
    "Surcharges could not be computed, the file has no surcharges for: {persons}",
  "datev.unmappedSurcharges": "Surcharge kinds with hours but without a wage type: {codes}",
  "datev.noPrevious": "No file of this month has been downloaded yet.",
  "datev.previous": "Deviations from the file of {date}",
  "datev.noDeviations": "No deviations from the last downloaded file.",
  "datev.surchargeLabel": "Surcharge {code}",
  "datev.calendarLabel": "Calendar {kind}",
  "datev.unit.hours": "h",
  "datev.unit.days": "days",
  "error.TT-3330":
    "The settings name another payroll program. Download the file of the program set.",
  "error.TT-3331": "A cost center names an establishment that does not exist.",
  "error.TT-3332": "The switch between the increase and the overtime paid out must be confirmed.",
  "error.TT-3333":
    "A switch of the overtime mode starts only in a month that has not begun and was not exported yet; earlier months keep their mode, so both wage types and the earlier switches stay.",
  "datev.category.FlexitimePayoutHours": "Flexitime paid out (payout bookings)",
  "datev.overtimeModeWarning":
    "You change how overtime is transferred. In both directions the switch applies only from a month that has not begun and was not exported yet; earlier months keep their transfer, so both wage types stay required. Hours already transferred as increase must not be paid out again; the check log names payouts that may concern them.",
  "datev.confirmOvertimeModeChange":
    "I confirm the change and check the payouts against the increase already transferred.",
  "datev.calendarHealthHint":
    "Note: the calendar transfers single sick days – health data under Art. 9 GDPR. Have the privacy notice and the data protection officer check it.",
  "datev.calendarRehabilitationHint":
    "Without an own key, rehabilitation follows the outage key of sickness. Single days are health data under Art. 9 GDPR.",
  "datev.column.day": "Day",
  "datev.payout": "{person}: {hours} h {account}",
  "datev.account.Overtime": "overtime account",
  "datev.account.Flexitime": "flexitime account",
  "datev.untransferredPayouts":
    "Payouts booked but not transferred (no wage type in this month): {payouts}",
  "datev.untransferredAbsences":
    "Absences with credit but without wage type, outage key or day category (not transferred): {absences}",
  "datev.untransferredAbsence": "{kind} {hours} h for {persons} persons",
  "datev.establishmentChanges":
    "Change of establishment in the month, all values carry the cost center of the last one: {persons}",
  "datev.previousCompared": "The last file was compared with its predecessor ({count} deviations).",
  "datev.overtimeSwitchFrom.Payouts":
    "Transfer overtime paid out from (month; the increase before it)",
  "datev.overtimeSwitchFrom.Increase":
    "Transfer the increase again from (month; the payouts before it)",
  "datev.overtimeHistory":
    "Switches of the overtime mode (kept while earlier months can still be exported): {switches}",
  "datev.overtimeSwitch.Payouts": "overtime paid out from {month}",
  "datev.overtimeSwitch.Increase": "increase from {month}",
  "datev.possiblyTransferredPayouts":
    "Overtime paid out that may already have been transferred as increase – reconcile with the increase transferred: {payouts}",
  "datev.possiblyTransferredPayout":
    "{payout} ({open} h more transferred as increase than paid out so far)",
  "absences.kind.VocationalTraining": "Vocational school or examination",
  "absences.code.VocationalTraining": "VS",
  "absences.vocational.category": "Kind of release",
  "absences.vocational.category.SchoolDay": "School day (more than five lessons)",
  "absences.vocational.category.BlockWeek": "Block teaching (block week)",
  "absences.vocational.category.Lessons": "Lessons with times",
  "absences.vocational.category.Examination": "Examination or training outside the company",
  "absences.vocational.category.ExamEve": "Working day before the written final examination",
  "absences.vocational.category.Other": "Vocational school or examination",
  "absences.vocational.startsAt": "Start",
  "absences.vocational.endsAt": "End",
  "absences.vocational.travel": "Travel time in minutes",
  "absences.vocational.travelHint":
    "Only the necessary travel between school or examination and the company, if you work there on the same day.",
  "absences.vocational.blockStartsAt": "Start of lessons",
  "absences.vocational.blockStartsAtHint":
    "If the block lessons begin before 9:00, Zeitavo checks that nobody works before them on the school days. Without it this check is not possible.",
  "absences.vocational.hint":
    "Release for vocational school and examinations (§ 15 BBiG, §§ 9 and 10 JArbSchG). You do not clock in for it and no project time is booked: the time counts for target and balance and in the working time checks – a whole day with the average daily working time (weekly target divided by the working days), also on a day off of the work time model, a block week with the average weekly working time, lessons and examinations with their duration including breaks and travel, less own work during that time. A school day with more than five lessons counts as a whole day once a week; enter further school days of that week as lessons with times.",
  "absences.vocational.earlyHint":
    "Lessons before 9:00: no work is allowed before the lessons on this day – in a block week on every school day.",
  "absences.vocational.blockHint":
    "At least five days of every calendar week. Beside it, at most two hours of in-company training events a week are allowed.",
  "absences.vocational.timed": "{category}, {from}–{to}, travel {minutes} min (credited {hours} h)",
  "absences.vocational.block": "{category}, lessons begin at {from}",
  "error.TT-1320": "Please choose the kind of release for vocational school or an examination.",
  "error.TT-1321":
    "Lessons and examinations need a start and a later end on the same day, each in whole minutes; a block week has at most a start of the lessons, whole days have no times.",
  "error.TT-1322":
    "The travel time lies between 0 and 240 minutes and belongs to lessons or an examination only.",
  "error.TT-1323":
    "This kind covers exactly one whole day; a block week covers at least five days of every calendar week. There are no half days.",
  "error.TT-1324":
    "This week already has a school day. Enter the further school day as lessons with times.",
  "error.TT-1325":
    "The release for vocational school and examinations is available only to trainees and to minors who are not yet 18 on the first day.",
  "error.TT-1326":
    "A block week releases its whole calendar weeks: these weeks hold no further release for vocational school or an examination, not even on a day outside the block.",
  "error.TT-1360": "Only the person who filed the correction request can withdraw it.",
  "error.TT-3390":
    "Vocational school and examinations cannot be requested through the public API; the person requests them or HR records them in Zeitavo with their kind and times.",
  "rule.JARBSCHG-SCHOOL": "Vocational school and examinations of minors (§§ 9, 10 JArbSchG)",
  "rule.BBIG-SCHOOL": "Release for vocational school and examinations (§ 15 BBiG)",
  "check.SchoolDayWork": "Work on a school day",
  "check.ExamEveWork": "Work on the day before the written final examination",
  "check.BlockWeekWork": "More than two hours of work in a block week",
  "check.EarlyLessonWork": "Work before lessons that begin before 9:00",
  "check.ReleaseWork": "Work during lessons or an examination",
  "check.TrainingCreditUnknown": "Average working time for the credit is missing",
  // On-call duty and on-site standby (F-106).
  "rule.ARBZG-ONCALL-REST": "Rest period with on-call duty (§ 5(3) ArbZG)",
  "rule.ARBZG-EXTENDED-DAY-REST": "Rest period after more than 12 hours of work (§ 7(9) ArbZG)",
  "rule.ARBZG-DEVIATION-AVERAGE": "Weekly average under deviations (§ 7(8) ArbZG)",
  "check.OnCallRest": "Rest period after on-call deployments too short",
  "check.RestAfterOnCall":
    "Rest period after an on-call deployment too short – only hospitals and care facilities or a collective agreement under § 7(2) no. 1 ArbZG may shorten it, not for minors or during maternity protection",
  "check.OnCallCompensation": "Compensation for on-call deployments",
  "check.ExtendedDayRest": "Rest period after a working day over 12 hours too short",
  "check.DeviationAverage":
    "Average weekly working time under a deviation according to § 7 ArbZG exceeded (§ 7(8) ArbZG)",
  // Working time rules of federal civil servants (F-115).
  "rule.AZV-DAILY-MAX": "Daily working time including breaks (§ 4 AZV)",
  "rule.AZV-AVERAGE": "Weekly working time on a yearly average (§ 87(1) BBG, § 3 AZV)",
  "rule.AZV-BREAK": "Rest breaks (§ 5(1) AZV)",
  "rule.AZV-REST": "Rest period (§ 5(3) AZV)",
  "rule.AZV-WEEKLY-REST": "Weekly rest (§ 5(3) AZV)",
  "rule.AZV-NIGHT": "Night duty (§ 14 AZV)",
  "rule.AZV-FREE-DAY": "Days free of duty (§ 6 AZV)",
  "rule.AZV-MULTI-EMPLOYER": "Work for other employers (federal civil servants)",
  "check.WeeklyRest": "Too few weekly rests of 24 hours in 14 days",
  "check.LateBreak": "Rest break too late – too short before the hour limit was reached",
  "check.ServiceFreeDay": "Duty on a day free of duty in principle – only if ordered or voluntary",
  "error.TT-1450":
    "The rule profile belongs to another working time law than the employment. Without an assignment the profile of the working time law applies.",
  "onCall.title": "On-call duty and on-site standby",
  "onCall.hint":
    "Deployments are not entered separately: a recorded shift that begins during on-call duty is a deployment. On-site standby always counts as working time.",
  "onCall.none": "No periods in the range shown.",
  "onCall.person": "Person",
  "onCall.item": "{kind}: {start} to {end}, {counts}",
  "onCall.item.counts": "counts as working time",
  "onCall.item.deployments": "working time only when deployed",
  "onCall.item.withdrawn": "{item} (withdrawn on {at})",
  "onCall.kind": "Kind",
  "onCall.kind.OnCall": "On-call duty",
  "onCall.kind.OnSiteStandby": "On-site standby",
  "onCall.new": "Add period",
  "onCall.startDate": "Start (date)",
  "onCall.startTime": "Start (time)",
  "onCall.endDate": "End (date)",
  "onCall.endTime": "End (time)",
  "onCall.counts":
    "Count entirely as working time (the restrictions affect free time very significantly)",
  "onCall.standbyCounts": "On-site standby counts as working time.",
  "onCall.invalid": "The end must lie after the start; a period lasts at most 7 days.",
  "onCall.add": "Add",
  "onCall.added": "Period added.",
  "onCall.withdraw": "Withdraw",
  "onCall.withdrawConsequence":
    "The period is withdrawn; shifts within it no longer count as deployments. It stays listed as withdrawn, and the audit log keeps the change.",
  "onCall.withdrawConfirm": "Withdraw period",
  "onCall.withdrawn": "Period withdrawn.",
  "onCall.tooEarly":
    "A period can begin on {date} at the earliest: older days are no longer evaluated again.",
  "roster.onCall.hint":
    "Periods belong to no version of the roster: changes apply at once, also in a published roster, and the rule check takes them into account.",
  "roster.onCall.none": "No on-call duty and no on-site standby on this day.",
  "roster.onCall.notReadable": "You do not see the periods of this person.",
  "roster.onCall.readOnly": "You may not change the periods of this person.",
  "roster.onCall.tooOld": "Lies too far back to change it.",
  "roster.onCall.changeLabel": "Change {item}",
  "roster.onCall.withdrawLabel": "Withdraw {item}",
  "roster.onCall.changeTitle": "Change period",
  "roster.onCall.changeHint":
    "Saving withdraws the former period and records a new version; the former one is kept as evidence.",
  "roster.onCall.save": "Save",
  "roster.onCall.changed": "Period changed.",
  "roster.onCall.legend": "OC: on-call duty, SB: on-site standby.",
  "roster.onCall.mark.OnCall": "OC",
  "roster.onCall.mark.OnSiteStandby": "SB",
  "error.TT-3600":
    "Periods that lie more than three months back can no longer be recorded, changed or withdrawn.",
  "error.TT-3661":
    "On days under the AZV superiors see no bookings, times or findings of the person (§ 7 (8) AZV); HR is responsible.",
  "error.TT-1420": "A period ends after its start and lasts at most 7 days.",
  "error.TT-1421": "On-site standby always counts as working time.",
  // Operator area (F-69).
  ...platformMessagesEn,
};
